import type { HubLang } from './i18n'
import type { WelcomeCard, WelcomeCardKey } from './notices'
import type { AskIntent, AskIntentKey, OneReason } from './wiki'
import type { SupportLane } from './support'
import type { ReactionKind } from './feed'
import { PROVISIONAL_CAMPAIGN_COPY } from './copy'
import type { TourStepKey } from './tour'
import type { CropShapeKey } from './photoCrop'

/**
 * The hub's words, in English and Spanish, in ONE FILE (SHL-25, the UI half).
 *
 * ── WHY ONE FILE AND NOT A STRING BESIDE EACH COMPONENT ─────────────────────
 *
 * The Spanish here was written by an assistant on 24 Sep and HAS NOT BEEN READ
 * BY A SPANISH SPEAKER. The person who reads it — somebody in the Lima or
 * Santiago office, or Comms — should be able to open one file, read two
 * columns side by side and correct them, without learning React. The same
 * reason the sign-in screen's copy is lib/signInCopy.ts. Terms follow that
 * file so a reader meets one vocabulary: tú, "portal" for the hub,
 * "ingresar" for sign in, "celular". Its open questions are on SHL-25.
 *
 * ── WHAT IS NOT TRANSLATED, DELIBERATELY ────────────────────────────────────
 *
 * The brand names — OneConetec, Wiki One, Star One, Ask One, One. "One" names
 * the COMPANY (CLAUDE.md, the teal ruling), so it does not become "Uno".
 *
 * ── HOW TO ADD A STRING ─────────────────────────────────────────────────────
 *
 * Add it to the section's type, then to BOTH columns. The type makes a missing
 * Spanish string a compile error, and the suite fails on a Spanish value that
 * is identical to its English one (outside the brand names above) — which is
 * what an untranslated copy-paste looks like.
 *
 * The section's keys in the ENGLISH column are the English the screen showed
 * before this file existed, so every existing assertion still reads the same
 * words.
 */

/** A language's name, IN THE READER'S LANGUAGE — for "Shown in …" flags. */
const LANGUAGE_NAMES: Record<HubLang, Record<string, string>> = {
  en: { 'en-CA': 'English', 'es-CL': 'Spanish', 'fr-FR': 'French' },
  es: { 'en-CA': 'inglés', 'es-CL': 'español', 'fr-FR': 'francés' },
}

/**
 * The flag on a post, notice or answer shown in a language other than the
 * reader's, because no translation exists (resolve_variant's fallback).
 *
 * English keeps its old wording, raw tag and all, because that is what the
 * suite and the moderators already read. Spanish names the language, because
 * "Mostrado en en-CA" is a code, not a sentence.
 */
export function shownIn(lang: HubLang, variantLocale: string): string {
  if (lang === 'en') return `Shown in ${variantLocale}`
  return `Solo en ${LANGUAGE_NAMES.es[variantLocale] ?? variantLocale}`
}

/* ── THE SHELL: rail, topbar, bell, tab bar, identity menu ─────────────────── */

/**
 * NAV KEYS ARE ENGLISH AND STAY ENGLISH. `current` is compared against the
 * key to mark the active item and tab — there is no second source for "where
 * am I" — so only the DISPLAY goes through this map, never the comparison.
 */
export type NavKey =
  | 'Engagement Hub'
  | 'Wiki One'
  | 'Star One'
  | 'Photos'
  | 'Photo contests'
  | 'Gallery'
  | 'Admin'
  | 'Admin home'
  | 'People and roles'
  | 'Notices'
  | 'Shop and stock'
  | 'Star One admin'
  | 'Moderation'
  /** 196. */
  | 'Audit log'
  /** 301. */
  | 'Usage'
  | 'Support inbox'
  | 'Your questions'
  | 'Your profile'
  /** 337 (HLP-43). #/help — tickets, feedback, guides. */
  | 'Help'
  /** 310. */
  | 'Surveys'
  /** 165. #/terms — not in NAV; reached from the identity menu. */
  | 'Terms'

/** 'Review' went on 4 Oct with its group (Moderation is under Administration). */
export type NavHeadingKey = 'Engagement' | 'Administration' | 'Support' | 'You'

export type ShellCopy = {
  nav: Record<NavKey, string>
  headings: Record<NavHeadingKey, string>
  /** The tab bar's short label for the hub; the other two are brand names. */
  hubShort: string
  skip: string
  sectionsLabel: string
  /** 4 Oct. The rail toggle's text — its accessible name at both widths. */
  collapseRail: string
  expandRail: string
  tabbarLabel: string
  installLabel: string
  installTitle: string
  notifications: string
  notificationsUnread: (n: number) => string
  nothingYet: string
  /** 125 (HLP-23). Clears every unread row in one press. */
  markAllRead: string
  /** 332. The bell's two views, and the per-row read toggle. */
  bellViewLabel: string
  bellNew: string
  bellAll: string
  markRead: string
  markUnread: string
  nothingNew: string
  showAll: string
  bellShowing: (shown: number, total: number) => string
  waiting: (n: number) => string
  // Identity menu
  yourAccount: string
  accountLabel: (name: string) => string
  armedLabel: (labels: string) => string
  showWelcomeAgain: string
  /** SHL-34. Starts the hub tour again, from any page. */
  takeTour: string
  /** 165. Opens #/terms — the terms of use and the Star One terms. */
  terms: string
  /** 4 Oct. Opens the digital business card over whatever screen you are on. */
  yourCard: string
  otherRoles: string
  otherRolesNote: string
  signOut: string
}

export const SHELL: Record<HubLang, ShellCopy> = {
  en: {
    nav: {
      'Engagement Hub': 'Engagement Hub',
      'Wiki One': 'Wiki One',
      'Star One': 'Star One',
      Photos: 'Photos',
      'Photo contests': 'Photo contests',
      Gallery: 'Gallery',
      Admin: 'Admin',
      'Admin home': 'Admin home',
      'People and roles': 'People and roles',
      Notices: 'Notices',
      'Shop and stock': 'Shop and stock',
      'Star One admin': 'Star One admin',
      Moderation: 'Moderation',
      'Audit log': 'Audit log',
      Usage: 'Usage',
      'Support inbox': 'Support inbox',
      'Your questions': 'Your questions',
      'Your profile': 'Your profile',
      Help: 'Help',
      Surveys: 'Surveys',
      Terms: 'Terms',
    },
    headings: {
      Engagement: 'Engagement',
      Administration: 'Administration',
      Support: 'Support',
      You: 'You',
    },
    hubShort: 'Hub',
    skip: 'Skip to content',
    sectionsLabel: 'Sections',
    collapseRail: 'Collapse menu',
    expandRail: 'Expand menu',
    tabbarLabel: 'Main',
    installLabel: 'Install OneConetec on this device',
    installTitle: 'Install on this device',
    notifications: 'Notifications',
    notificationsUnread: (n) => `Notifications, ${n} unread`,
    nothingYet: 'Nothing yet.',
    markAllRead: 'Mark all as read',
    bellViewLabel: 'Show',
    bellNew: 'New',
    bellAll: 'All',
    markRead: 'Mark as read',
    markUnread: 'Mark as unread',
    nothingNew: 'Nothing new.',
    showAll: 'Show all',
    bellShowing: (shown, total) => `Showing the newest ${shown} of ${total} unread.`,
    waiting: (n) => `${n} waiting`,
    yourAccount: 'Your account',
    accountLabel: (name) => `${name}. Your account`,
    armedLabel: (labels) => `On: ${labels}`,
    showWelcomeAgain: 'Show the welcome again',
    takeTour: 'Take the tour',
    terms: 'Terms',
    yourCard: 'Your digital card',
    otherRoles: 'Your other roles',
    otherRolesNote:
      'Each of these changes what this page shows you. None of them changes what you are allowed to do.',
    signOut: 'Sign out',
  },
  es: {
    nav: {
      'Engagement Hub': 'Portal',
      'Wiki One': 'Wiki One',
      'Star One': 'Star One',
      Photos: 'Fotos',
      'Photo contests': 'Concursos de fotos',
      Gallery: 'Galería',
      Admin: 'Administración',
      'Admin home': 'Inicio de administración',
      'People and roles': 'Personas y roles',
      Notices: 'Avisos',
      'Shop and stock': 'Tienda e inventario',
      'Star One admin': 'Administrar Star One',
      Moderation: 'Moderación',
      'Audit log': 'Registro de auditoría',
      Usage: 'Uso',
      'Support inbox': 'Bandeja de soporte',
      'Your questions': 'Tus preguntas',
      'Your profile': 'Tu perfil',
      Help: 'Ayuda',
      Surveys: 'Encuestas',
      Terms: 'Términos',
    },
    headings: {
      Engagement: 'Comunidad',
      Administration: 'Administración',
      Support: 'Soporte',
      You: 'Tú',
    },
    hubShort: 'Portal',
    skip: 'Ir al contenido',
    sectionsLabel: 'Secciones',
    collapseRail: 'Contraer menú',
    expandRail: 'Expandir menú',
    tabbarLabel: 'Principal',
    installLabel: 'Instalar OneConetec en este dispositivo',
    installTitle: 'Instalar en este dispositivo',
    notifications: 'Notificaciones',
    notificationsUnread: (n) => `Notificaciones, ${n} sin leer`,
    nothingYet: 'Nada por ahora.',
    markAllRead: 'Marcar todo como leído',
    bellViewLabel: 'Mostrar',
    bellNew: 'Nuevas',
    bellAll: 'Todas',
    markRead: 'Marcar como leída',
    markUnread: 'Marcar como no leída',
    nothingNew: 'Nada nuevo.',
    showAll: 'Ver todas',
    bellShowing: (shown, total) => `Se muestran las ${shown} más recientes de ${total} sin leer.`,
    waiting: (n) => `${n} pendientes`,
    yourAccount: 'Tu cuenta',
    accountLabel: (name) => `${name}. Tu cuenta`,
    armedLabel: (labels) => `Activo: ${labels}`,
    showWelcomeAgain: 'Ver la bienvenida otra vez',
    takeTour: 'Hacer el recorrido',
    terms: 'Términos',
    yourCard: 'Tu tarjeta digital',
    otherRoles: 'Tus otros roles',
    otherRolesNote:
      'Cada uno cambia lo que esta página te muestra. Ninguno cambia lo que tienes permitido hacer.',
    signOut: 'Cerrar sesión',
  },
}

/** The display label for a nav key, or the key itself for anything unmapped. */
export function navLabel(lang: HubLang, key: string): string {
  return (SHELL[lang].nav as Record<string, string>)[key] ?? key
}

/* ── SHL-33: the admin home (#/admin) and Notices (#/admin/notices) ─────────
   One line per destination, saying what the page is FOR — the card's label is
   the nav label, so these never restate it. Keyed by route, because the
   routes are the destinations' identity (lib/adminHome.ts). A route with no
   line shows its label alone rather than nothing. */
export type AdminHomeCopy = {
  lede: string
  nothing: string
  waiting: (n: number) => string
  about: Record<string, string>
}

export const ADMIN_HOME: Record<HubLang, AdminHomeCopy> = {
  en: {
    lede: 'The pages your roles open. Everybody else sees the hub without these.',
    nothing: 'None of your roles opens an admin page.',
    waiting: (n) => `${n} waiting`,
    about: {
      'admin/people': 'Invite people, keep their details and work email, suspend or restore access, and grant roles.',
      'admin/notices': 'Write a notice for the top of the hub, or the welcome, and take one down.',
      'admin/shop': 'The store: items, sizes, stock, pre-order windows and collection points.',
      'admin/stars': 'What each activity earns in stars, the monthly limits, and the "What for" words.',
      moderation: 'Posts and replies the content check held back or flagged.',
      'admin/audit': 'Who did what, to whom and when - every act the platform records, by person, area or action.',
      'admin/usage': 'How the hub is used: people active, sign-ins, devices, countries, features and hours - counts only.',
      support: 'Private questions sent to the lanes you cover.',
      'support/ask-one': 'What Ask One is asked, and how it does.',
      'support/one-question': 'This week\'s One Question answers, for Communications.',
    },
  },
  es: {
    lede: 'Las páginas que abren tus roles. Los demás ven el portal sin ellas.',
    nothing: 'Ninguno de tus roles abre una página de administración.',
    waiting: (n) => `${n} en espera`,
    about: {
      'admin/people': 'Invitar personas, mantener sus datos y correo de trabajo, suspender o restablecer el acceso y otorgar roles.',
      'admin/notices': 'Escribir un aviso para la parte superior del portal o la bienvenida, y retirarlo.',
      'admin/shop': 'La tienda: artículos, tallas, stock, plazos de preventa y puntos de retiro.',
      'admin/stars': 'Cuántas estrellas da cada actividad, los límites mensuales y las palabras del "Por qué".',
      moderation: 'Publicaciones y respuestas que la revisión de contenido retuvo o marcó.',
      'admin/audit': 'Quién hizo qué, a quién y cuándo: cada acción que registra la plataforma, por persona, área o acción.',
      'admin/usage': 'Cómo se usa el portal: personas activas, ingresos, dispositivos, países, funciones y horas, solo como conteos.',
      support: 'Preguntas privadas enviadas a las áreas que atiendes.',
      'support/ask-one': 'Qué se le pregunta a Ask One y cómo responde.',
      'support/one-question': 'Las respuestas de One Question de la semana, para Comunicaciones.',
    },
  },
}

export type NoticesPageCopy = {
  lede: string
  previewHeading: string
  previewNote: string
  previewEmpty: string
  manageHeading: string
  noAccess: string
}

export const NOTICES_PAGE: Record<HubLang, NoticesPageCopy> = {
  en: {
    lede: 'Notices sit at the top of the hub for the people they are aimed at. A welcome notice opens as a window instead.',
    previewHeading: 'On the hub now',
    previewNote: 'The strip as it shows at the top of the hub, in your language. Aimed notices show only to their audience.',
    previewEmpty: 'No notice is showing on the hub.',
    manageHeading: 'All notices',
    noAccess: 'Notices need the Communications admin role.',
  },
  es: {
    lede: 'Los avisos aparecen en la parte superior del portal para las personas a quienes van dirigidos. Un aviso de bienvenida se abre como ventana.',
    previewHeading: 'En el portal ahora',
    previewNote: 'La franja tal como se ve arriba en el portal, en tu idioma. Los avisos dirigidos solo los ve su público.',
    previewEmpty: 'No hay avisos en el portal.',
    manageHeading: 'Todos los avisos',
    noAccess: 'Los avisos requieren el rol de administración de Comunicaciones.',
  },
}

export function headingLabel(lang: HubLang, key: string): string {
  return (SHELL[lang].headings as Record<string, string>)[key] ?? key
}

/* ── THE BELL'S SENTENCES (lib/moderation describeNotification) ────────────── */

export type NotificationCopy = {
  failedOpen: string
  blocked: string
  flagged: string
  mentioned: string
  replyToYou: string
  /** 143 (FED-49) */
  photoTagged: string
  /** 163. A colleague suggested somebody in your photo; you decide. */
  photoTagSuggested: string
  /** 146. A judge chose your photo. */
  contestWon: (place: number, title: string, stars: number) => string
  /** 147. The judges' award, outside the vote. */
  contestJudgesAward: (title: string, stars: number) => string
  /** 147. A judge took your photo out of a contest, and why. */
  contestDisqualified: (title: string, reason: string) => string
  /** 152. The person who asked hears the outcome of a photo review. */
  photoReviewDecided: (outcome: string, identityCorrected: boolean) => string
  /** 152. The poster hears only when a review changed their photo — never who asked. */
  photoReviewChanged: (outcome: string) => string
  /** 152. A photo review was handed to you (HLP-31's shape). */
  photoReviewHanded: string
  onYourPost: string
  replied: string
  supportAsked: string
  supportAnswered: string
  supportAskerReplied: string
  /** 119 */
  supportHandedToYou: string
  supportHandedOn: (to: string) => string
  /** 121 — `to` is laneWords().to, the lane in the reader's language */
  supportMovedToYourLane: string
  supportLaneChanged: (to: string) => string
  /** 120 */
  supportPublishedAsAnswer: string
  /** 172. Platform support awarded (or raised) stars on your ticket. `level` is SUPPORT_STAR_LEVELS' words. */
  supportStarsAwarded: (level: string, stars: number, raised: boolean) => string
  /** 174. The lane closed your private question and asks you to confirm. */
  supportClosed: string
  wikiAnswerDrafted: string
  wikiAsked: string
  wikiAnswered: string
  wikiDeclined: string
  /** 127 (FED-25) */
  replyFlagged: string
  replyBlocked: string
  replyCheckFailed: string
  yourReplyRetracted: string
}

export const NOTIFICATIONS: Record<HubLang, NotificationCopy> = {
  en: {
    failedOpen: 'A post published without being checked.',
    blocked: 'A post was blocked and is waiting for review.',
    flagged: 'A post was flagged for review.',
    mentioned: 'Someone mentioned you.',
    replyToYou: 'Someone replied to you.',
    photoTagged: 'Someone tagged you in a photo.',
    photoTagSuggested: 'A colleague suggested who is in your photo. You decide.',
    contestWon: (place, title, stars) =>
      `Your photo won ${place === 1 ? 'first place' : place === 2 ? 'second place' : place === 3 ? 'third place' : `place ${place}`} in ${title}` +
      (stars > 0 ? ` — ${stars.toLocaleString()} stars.` : '.'),
    contestJudgesAward: (title, stars) =>
      `Your photo won the judges' award in ${title}` + (stars > 0 ? ` — ${stars.toLocaleString()} stars.` : '.'),
    contestDisqualified: (title, reason) => `A judge took your photo out of ${title}: ${reason}.`,
    photoReviewDecided: (outcome, identityCorrected) =>
      identityCorrected
        ? 'The photo you asked about was reviewed: the reviewer found it is not you.'
        : outcome === 'restrict'
          ? 'The photo you asked about was reviewed, and some of its uses were taken away.'
          : outcome === 'remove'
            ? 'The photo you asked about was taken out of the Gallery.'
            : outcome === 'moderation'
              ? 'The photo you asked about was sent to the moderators.'
              : 'The photo you asked about was reviewed and kept.',
    photoReviewChanged: (outcome) =>
      outcome === 'remove'
        ? 'A Gallery review took one of your photos out of the Gallery. It stays on your post.'
        : 'A Gallery review restricted how one of your photos can be used.',
    photoReviewHanded: 'A photo review was handed to you.',
    onYourPost: 'Someone commented on your post.',
    replied: 'There is a new comment on something of yours.',
    supportAsked: 'Somebody asked your support lane a question.',
    supportAnswered: 'Your private question has a reply.',
    supportAskerReplied: 'Somebody came back on a question in your lane.',
    supportHandedToYou: 'A private support question was handed to you.',
    supportHandedOn: (to) => `Your private question was handed to ${to}, who will reply here.`,
    supportMovedToYourLane: 'A private question was moved into your support lane.',
    supportLaneChanged: (to) => `Your private question now goes to ${to}, who will reply here.`,
    supportPublishedAsAnswer: 'A general answer written from your private question is now on Wiki One. Your question stays private.',
    supportStarsAwarded: (level, stars, raised) =>
      (raised ? `Platform support raised your ticket to ${level}` : `Platform support marked your ticket ${level}`) +
      (stars > 0 ? ` — ${stars.toLocaleString()} ${raised ? 'more ' : ''}stars. Thank you.` : '. Thank you.'),
    supportClosed: 'Support closed your private question. Is it solved?',
    wikiAnswerDrafted: 'Support drafted a Wiki One answer from a private thread. It needs a curator to publish it.',
    wikiAsked: 'Somebody asked a new question on Wiki One.',
    wikiAnswered: 'Your question has been answered.',
    wikiDeclined: 'Your question was closed without an answer.',
    replyFlagged: 'The content check flagged a reply for review.',
    replyBlocked: 'The content check took a reply down. It is waiting for review.',
    replyCheckFailed: 'A reply could not be checked after five tries.',
    yourReplyRetracted: 'The content check took one of your replies down. Open it to see why.',
  },
  es: {
    failedOpen: 'Una publicación salió sin ser revisada.',
    blocked: 'Una publicación fue bloqueada y espera revisión.',
    flagged: 'Una publicación fue marcada para revisión.',
    mentioned: 'Alguien te mencionó.',
    replyToYou: 'Alguien te respondió.',
    photoTagged: 'Alguien te etiquetó en una foto.',
    photoTagSuggested: 'Un colega sugirió quién está en tu foto. Tú decides.',
    contestWon: (place, title, stars) =>
      `Tu foto ganó el ${place === 1 ? 'primer lugar' : place === 2 ? 'segundo lugar' : place === 3 ? 'tercer lugar' : `lugar ${place}`} en ${title}` +
      (stars > 0 ? ` — ${stars.toLocaleString()} Stars.` : '.'),
    contestJudgesAward: (title, stars) =>
      `Tu foto ganó el premio del jurado en ${title}` + (stars > 0 ? ` — ${stars.toLocaleString()} Stars.` : '.'),
    contestDisqualified: (title, reason) => `El jurado sacó tu foto de ${title}: ${reason}.`,
    photoReviewDecided: (outcome, identityCorrected) =>
      identityCorrected
        ? 'Se revisó la foto que consultaste: quien revisó constató que no eres tú.'
        : outcome === 'restrict'
          ? 'Se revisó la foto que consultaste y se quitaron algunos de sus usos.'
          : outcome === 'remove'
            ? 'La foto que consultaste salió de la Galería.'
            : outcome === 'moderation'
              ? 'La foto que consultaste se envió a los moderadores.'
              : 'Se revisó la foto que consultaste y se mantuvo.',
    photoReviewChanged: (outcome) =>
      outcome === 'remove'
        ? 'Una revisión de la Galería sacó una de tus fotos de la Galería. Sigue en tu publicación.'
        : 'Una revisión de la Galería restringió cómo se puede usar una de tus fotos.',
    photoReviewHanded: 'Te entregaron una revisión de foto.',
    onYourPost: 'Alguien comentó tu publicación.',
    replied: 'Hay un comentario nuevo en algo tuyo.',
    supportAsked: 'Alguien hizo una pregunta a tu canal de soporte.',
    supportAnswered: 'Tu pregunta privada tiene una respuesta.',
    supportAskerReplied: 'Alguien volvió a escribir en una pregunta de tu canal.',
    supportHandedToYou: 'Te pasaron una pregunta privada de soporte.',
    supportHandedOn: (to) => `Tu pregunta privada pasó a ${to}, que te responderá aquí.`,
    supportMovedToYourLane: 'Una pregunta privada pasó a tu vía de soporte.',
    supportLaneChanged: (to) => `Tu pregunta privada ahora va a ${to}, que te responderá aquí.`,
    supportPublishedAsAnswer: 'Una respuesta general escrita a partir de tu pregunta privada ya está en Wiki One. Tu pregunta sigue siendo privada.',
    supportStarsAwarded: (level, stars, raised) =>
      (raised ? `Soporte de la plataforma subió tu ticket a ${level}` : `Soporte de la plataforma marcó tu ticket como ${level}`) +
      (stars > 0 ? ` — ${stars.toLocaleString()} Stars${raised ? ' más' : ''}. Gracias.` : '. Gracias.'),
    supportClosed: 'Soporte cerró tu pregunta privada. ¿Está resuelta?',
    wikiAnswerDrafted: 'Soporte redactó una respuesta para Wiki One a partir de una conversación privada. Necesita que un curador la publique.',
    wikiAsked: 'Alguien hizo una pregunta nueva en Wiki One.',
    wikiAnswered: 'Tu pregunta fue respondida.',
    wikiDeclined: 'Tu pregunta se cerró sin respuesta.',
    replyFlagged: 'La revisión de contenido marcó una respuesta para revisión.',
    replyBlocked: 'La revisión de contenido retiró una respuesta. Espera revisión.',
    replyCheckFailed: 'Una respuesta no se pudo revisar después de cinco intentos.',
    yourReplyRetracted: 'La revisión de contenido retiró una de tus respuestas. Ábrela para ver por qué.',
  },
}

/* ── THE WELCOME MODAL'S CHROME AND CARDS ──────────────────────────────────── */

/**
 * The modal's BODY is content (091) — Comms writes it and an es-CL variant is
 * a content row, not a string here. What is here is the frame around it: the
 * fallback heading, the list label, the way out, and the four cards.
 *
 * The cards' ENGLISH stays in lib/notices.ts WELCOME_CARDS, because the admin
 * screen's card picker walks that list and it is the one definition of which
 * cards exist (091). Only the Spanish is here, keyed by the same card keys, so
 * a fifth card added there without a Spanish line here is a compile error.
 */
export type WelcomeCopy = {
  fallbackTitle: string
  whereToStart: string
  lookAround: string
}

export const WELCOME: Record<HubLang, WelcomeCopy> = {
  en: {
    fallbackTitle: 'Welcome',
    whereToStart: 'Where to start',
    lookAround: 'Have a look around',
  },
  es: {
    fallbackTitle: 'Te damos la bienvenida',
    whereToStart: 'Por dónde empezar',
    lookAround: 'Echar un vistazo',
  },
}

type CardText = Pick<WelcomeCard, 'title' | 'body' | 'action'>

export const WELCOME_CARDS_ES: Record<WelcomeCardKey, CardText> = {
  video: {
    title: 'Mira el anuncio',
    body: 'Por qué cambia el nombre y qué significa para el trabajo.',
    action: 'Verlo',
  },
  ask_one: {
    title: 'Hazle una pregunta a One',
    body: 'Responde a partir de lo que realmente se ha publicado. Si necesitas a una persona, Ayuda está a un toque.',
    action: 'Preguntar algo',
  },
  recognition: {
    title: 'Mira a quién han reconocido',
    body: 'Tienes 1.000 Stars para regalar cada mes. Se reinician, así que úsalas.',
    action: 'Abrir Star One',
  },
  tell_us: {
    title: 'Cuéntanos algo',
    body: '¿Encontraste un error o se te ocurrió algo que el portal debería hacer? Llega a quienes lo están construyendo.',
    action: 'Contarnos',
  },
  tour: {
    title: 'Hacer el recorrido',
    body: 'Una vuelta corta por el portal, que te muestra cada parte.',
    action: 'Hacer el recorrido',
  },
}

export function welcomeCardText(lang: HubLang, card: WelcomeCard): CardText {
  return lang === 'es' ? WELCOME_CARDS_ES[card.key] : card
}

/* ── ASK ONE: the drawer, the routing step, the thumbs ─────────────────────── */

/**
 * THE RULES THIS COPY CARRIES DO NOT CHANGE WITH THE LANGUAGE, and the suite
 * applies the English guards to the Spanish too. The ones that matter most:
 *
 *  - NOTHING HERE NAMES A DESTINATION THAT DOES NOT SEND. The HR intent has no
 *    door (HLP-20), so its Spanish may not promise one — "enviar a RR. HH." is
 *    exactly the sentence the English guards refuse.
 *  - THE PUBLIC WARNING SAYS "WITH YOUR NAME" at the moment of choosing (034).
 *  - A PRIVATE SEND SAYS "THEY SEE YOUR NAME" — named, not anonymous (16 Sep).
 *  - NO REPLY TIME IS PROMISED, in either language.
 *
 * Sentences with a bold phrase inside them are THREE parts — before, the bold
 * words, after — rather than markup in a string, so the reader of this file
 * sees plain text and the component owns the <strong>.
 */
export type Emph = readonly [string, string, string]

/** How a lane is spoken about. English derives `to` and `queue` from the label
 *  exactly as the drawer always did (`label.toLowerCase()`); Spanish needs an
 *  article the label does not carry ("un experto técnico"). */
export type LaneWords = { label: string; about: string; to: string; queue: string }

export const LANE_WORDS_ES: Record<SupportLane, LaneWords> = {
  platform: {
    label: 'Soporte de la plataforma',
    about: 'El portal en sí: algo que no funciona, confunde o falta, y a donde van las ideas.',
    to: 'soporte de la plataforma',
    queue: 'el soporte de la plataforma',
  },
  hr: {
    label: 'RR. HH.',
    about: 'Sueldo, permisos, contratos y beneficios.',
    to: 'RR. HH.',
    queue: 'RR. HH.',
  },
  technical: {
    label: 'Experto técnico',
    about: 'Datos de terreno, ensayos, equipos, entregables a clientes. El trabajo, no el portal.',
    to: 'un experto técnico',
    queue: 'las consultas técnicas',
  },
}

type IntentText = Pick<AskIntent, 'label' | 'blurb' | 'prompt' | 'still'>

/**
 * The four intents in Spanish. The English, the lane and the ORDER are
 * lib/wiki.ts ASK_INTENTS, which is the one definition of which intents exist
 * and where each routes (`intentRouting`) — only the words are here.
 *
 * `still` completes "¿Todavía quieres …?" and "Viniste a …", so it is an
 * infinitive phrase, as the English completes "Still want to …?".
 */
export const ASK_INTENTS_ES: Record<AskIntentKey, IntentText> = {
  board: {
    label: 'Pregúntale a la comunidad OneConetec',
    blurb: 'Cualquier persona de la empresa puede responder. Es pública, con tu nombre.',
    prompt:
      'Quieres hacerle una pregunta a la comunidad. ¿Cuál es? Primero veré si ya tiene respuesta.',
    still: 'llevarla a la comunidad',
  },
  platform: {
    label: 'Soporte de la plataforma',
    blurb: 'Algo del portal que no funciona, confunde o falta, y a donde van las ideas.',
    prompt: '¿Qué pasó, o qué te gustaría ver?',
    still: 'enviarla a soporte de la plataforma',
  },
  hr: {
    label: 'RR. HH. y empleo',
    blurb:
      'One responde a partir de las preguntas frecuentes publicadas. Todavía no hay un canal privado de RR. HH. en el portal: para algo personal, habla directamente con tu jefatura o con RR. HH.',
    prompt: '¿Cuál es tu pregunta?',
    still: 'ver preguntas de RR. HH. y empleo',
  },
  technical: {
    label: 'Consultar a un experto técnico',
    blurb: 'Datos de terreno, ensayos, equipos, entregables a clientes. El trabajo, no el portal.',
    prompt: '¿Cuál es tu pregunta?',
    still: 'contactar a un experto técnico',
  },
}

export function intentText(lang: HubLang, intent: AskIntent): IntentText {
  return lang === 'es' ? ASK_INTENTS_ES[intent.key] : intent
}

export const ONE_REASONS_ES: Record<OneReason, string> = {
  not_my_question: 'No respondió lo que pregunté',
  wrong: 'Incorrecto o desactualizado',
  vague: 'Demasiado vago para actuar',
  unsourced: 'Quisiera el nombre de una persona en esto',
  other: 'Otra cosa',
}

export type AskCopy = {
  // A message from One
  unavailableLabel: string
  unavailableBody: string
  rateLimitedLabel: string
  inferred: string
  unofficial: string
  inferredNote: string
  // Opening
  welcome: string
  reading: string
  askLabel: string
  placeholder: string
  ask: string
  startOver: string
  /** SUP-2. Buttons for the two action pages (web/src/lib/pages.ts ACTION_LABELS). */
  openTicket: string
  shareFeedback: string
  close: string
  foot: string
  // S2 (HLP-44) - the one door, the page chip, the starters, the launcher
  /** Before the link to #/help. Always on the panel; again after a thumbs-down. */
  helpLead: string
  helpLink: string
  /** One recognised an idea or a fault: a LINK that opens Help's form filled. */
  requestLink: { idea: string; problem: (lane: LaneWords) => string; technical: (lane: LaneWords) => string }
  /** Under that link: nothing goes until the person presses Send. */
  requestLinkNote: string
  /** The chip in the box names the page by its own nav label; this is its ✕. */
  pageChipRemove: string
  /** Over the starter questions on an empty panel. */
  startersHead: string
  // Routing step
  notNow: string
  communityName: string
  communityWho: string
  questionLabel: string
  questionPlaceholder: string
  detailLabel: string
  detailPlaceholder: string
  publicWarn: Emph
  laneWarn: (lane: LaneWords) => Emph
  // 116 — pictures and page details on a private question
  picturesLabel: string
  picturesHint: (max: number) => string
  attachPicture: string
  capturePage: string
  capturing: string
  removePicture: string
  pagePictureTag: string
  pageDetailsNote: (route: string, viewport: string, app: string) => string
  pictureFailed: string
  sentPicturesFailed: (n: number) => string
  sending: string
  sendTo: (lane: LaneWords) => string
  postToCommunity: string
  noHrRoute: Emph
  /** What the routing step seeds the background with. It is SENT — to the
   *  board or the lane — so it is in the asker's language, not the reader's. */
  escalateDetail: (summary: string) => string
  escalateNoAnswer: string
  // After a send
  sentTo: (lane: LaneWords) => string
  sentLaneBody: string
  /** 337. Feedback from Help — RouteSent's fourth outcome. */
  sentFeedbackHead: string
  sentFeedbackBody: string
  liveHead: string
  liveBody: string
  blockedHead: string
  blockedBody: (reason: string) => string
  heldHead: string
  heldBody: string
  askSomethingElse: string
  // Thumbs
  goodAnythingElse: string
  thanksTeam: string
  whatWasWrong: string
  skip: string
  wasUseful: string
  yesUseful: string
  noUseful: string
  // Source line
  fromWiki: (w: number) => string
  fromGuide: (h: number) => string
  fromBoth: (w: number, h: number) => string
  /** v10 (S4). An answer that rests only on the person's own screens. */
  fromScreens: (screens: string) => string
  /** S5. An answer that rests on guide sections the guides tool returned. */
  fromGuides: (sections: string) => string
  /** v10. The lead of the line above an answer naming the screens One read. */
  lookedAt: string
  // v9 — links, clarifying, and the way out when it is not working
  /** The button under a guide-sourced answer. Names the SCREEN, never a person or lane. */
  /** HLP-37. The button under an answer for a page a cited entry names; the
   *  page's name is the nav's own (lib/pages.ts). */
  openPage: (page: string) => string
  /** Accessible name for the list of tap-through sources. */
  sourcesLabel: string
  /** The last chip under a clarifying question: puts the question back in the box. */
  somethingElse: string
  readingAs: (s: string) => string
  notIt: string
  // v9.5 - One recognised an idea or a fault (RequestOffer)
  /** One's reply when it found nothing: what it is, not where it goes. */
  requestBody: Record<OneRequest, string>
  // v9.6 - a question about the work: an expert, the community, or both
}

/** "a, b and c" / "a, b y c". Anything that is not a list reads as empty. */
const andList = (xs: unknown, and: string): string => {
  const a = Array.isArray(xs) ? xs.map(String) : []
  return a.length > 1 ? `${a.slice(0, -1).join(', ')} ${and} ${a[a.length - 1]}` : a[0] ?? ''
}
const enAnswers = (n: number) => `${n} Wiki One ${n === 1 ? 'answer' : 'answers'}`
const enEntries = (n: number) => `${n} guide ${n === 1 ? 'entry' : 'entries'}`
const esAnswers = (n: number) => `${n} ${n === 1 ? 'respuesta' : 'respuestas'} de Wiki One`
const esEntries = (n: number) => `${n} ${n === 1 ? 'entrada' : 'entradas'} de la guía`

/** v9.5. What ask-one says a message IS when it is not a question. Here, not
 *  in wiki.ts, because wiki.ts imports this file. */
export type OneRequest = 'idea' | 'problem' | 'technical'

export const ASK: Record<HubLang, AskCopy> = {
  en: {
    unavailableLabel: 'One is unavailable',
    unavailableBody:
      'Something went wrong reaching One. Try again in a moment, or use Help to reach a person.',
    rateLimitedLabel: 'One needs a moment',
    inferred: 'inferred',
    unofficial: 'uses unofficial answers',
    inferredNote:
      "Wiki One does not say this in so many words — it is One's reading of what is published. If you need it confirmed, ask a person.",
    welcome:
      'Ask anything. I look through the FAQ and the platform guide and answer from them. I don\'t send anything to anybody — if you need a person, Help is at the foot of this panel.',
    reading: 'One is reading…',
    askLabel: 'Ask One a question',
    placeholder: 'Type your question…',
    ask: 'Ask',
    startOver: 'Start over',
    openTicket: 'Open a ticket',
    shareFeedback: 'Share feedback',
    close: 'Close Ask One',
    foot:
      'Written by AI. This conversation is yours — colleagues cannot see it, and One sends nothing to anybody.',
    helpLead: 'Need a person?',
    helpLink: 'Help',
    requestLink: {
      idea: 'Send it as feedback',
      problem: (lane) => `Report it to ${lane.to}`,
      technical: (lane) => `Ask a ${lane.to}`,
    },
    requestLinkNote: 'Opens a form with your words in it. Nothing is sent until you press Send.',
    pageChipRemove: 'Ask without this page',
    startersHead: 'Try asking',
    notNow: 'Not now',
    communityName: 'Ask the OneConetec community',
    communityWho:
      'Published with your name once the content check passes it. Everyone can see it and reply.',
    questionLabel: 'Your question, as it will appear',
    questionPlaceholder: 'Your question, in one line',
    detailLabel: 'Background — what you already tried, in your words',
    detailPlaceholder: 'Any background (optional)',
    publicWarn: [
      'This goes on the Wiki One board ',
      'with your name on it',
      ', where every colleague can read it, upvote it and answer it. It is checked against the content guidelines first — if it breaks one, it will not go up and you will be told why.',
    ],
    laneWarn: (lane) => [
      `Only the people who answer ${lane.queue} see this, and `,
      'they see your name',
      '. It is not published, it does not go on any board, and no other colleague can read it.',
    ],
    picturesLabel: 'Screenshots or photos',
    picturesHint: (max) => `Up to ${max}. They go to the same people as the question, and nowhere else.`,
    attachPicture: 'Attach a screenshot or photo',
    capturePage: 'Add a picture of this page',
    capturing: 'Drawing the page…',
    removePicture: 'Remove',
    pagePictureTag: 'Picture of the page',
    pageDetailsNote: (route, viewport, app) =>
      `The page details go with it, so the team can see where you were: screen ${route}, window ${viewport}, app version ${app}, and your browser and language.`,
    pictureFailed: 'That picture could not be added. Try a JPEG, PNG or WebP.',
    sentPicturesFailed: (n) =>
      `${n === 1 ? 'One picture' : `${n} pictures`} did not arrive. Say so in a reply and the team will ask for ${n === 1 ? 'it' : 'them'}.`,
    sending: 'Sending…',
    sendTo: (lane) => `Send to ${lane.to}`,
    postToCommunity: 'Post to the community',
    noHrRoute: [
      'There is no private route for an employment question in the hub, and ',
      'this must not go on a public board',
      ' — it would carry your name in front of every colleague. Speak to your manager or to HR directly. If it is something you cannot raise through the chain of command, VINCI Integrity is the route for that.',
    ],
    escalateDetail: (summary) =>
      `I asked One first and it said:\n\n"${summary}"\n\nThat did not answer it.`,
    escalateNoAnswer: 'I asked One first and it had no answer for this.',
    sentTo: (lane) => `Sent to ${lane.to}`,
    sentLaneBody:
      'They have it, with your name and what you typed. You will get a notification here when somebody replies — nothing about this is published.',
    sentFeedbackHead: 'Thank you — the platform team has it',
    sentFeedbackBody:
      'It is under Your tickets on the Help page, with your name and what you typed. You will get a notification when somebody replies. Nothing about it is published.',
    liveHead: 'It is on the board',
    liveBody:
      'Your question is on Wiki One now, with your name on it. You will get a notification here when somebody answers.',
    blockedHead: 'It did not go up',
    blockedBody: (reason) =>
      `${reason} Nothing was published. A moderator will look at it too, and can put it up if the check got it wrong.`,
    heldHead: 'Saved — a curator will put it up',
    heldBody:
      'The content check could not be reached just now, so your question is waiting for a curator instead of going straight on the board. You will get a notification when somebody answers.',
    askSomethingElse: 'Ask something else',
    goodAnythingElse: 'Good — anything else on your mind?',
    thanksTeam: 'Thanks — the team will see this.',
    whatWasWrong: 'What was wrong with it?',
    skip: 'Skip',
    wasUseful: 'Was this useful?',
    yesUseful: 'Yes, this was useful',
    noUseful: 'No, this was not useful',
    fromWiki: (w) => `From ${enAnswers(w)}`,
    fromGuide: (h) => `From the platform guide — ${enEntries(h)}`,
    fromBoth: (w, h) => `From ${enAnswers(w)} and ${enEntries(h)}`,
    fromScreens: (screens) => `Read from your own screens: ${screens}`,
    fromGuides: (sections) => `From your guides: ${sections}`,
    lookedAt: 'Looked at',
    openPage: (page) => `Open ${page}`,
    sourcesLabel: 'Where this answer comes from',
    somethingElse: 'Something else',
    readingAs: (s) => `Reading this as: ${s}`,
    notIt: 'Not it?',
    requestBody: {
      idea: 'That sounds like an idea for the hub rather than a question, so there is nothing for me to look up.',
      problem: 'That sounds like something in the hub not working as it should, which is not something I can look up or fix from here.',
      technical:
        "That's a technical question about the work. I don't answer those myself, so nobody acts on a machine's guess about field data or equipment.",
    },
  },
  es: {
    unavailableLabel: 'One no está disponible',
    unavailableBody:
      'Algo falló al contactar a One. Inténtalo de nuevo en un momento, o usa Ayuda para hablar con una persona.',
    rateLimitedLabel: 'One necesita un momento',
    inferred: 'inferido',
    unofficial: 'usa respuestas no oficiales',
    inferredNote:
      'Wiki One no lo dice con estas palabras: es la lectura que One hace de lo publicado. Si necesitas confirmarlo, pregúntale a una persona.',
    welcome:
      'Pregunta lo que quieras. Busco en las preguntas frecuentes y en la guía de la plataforma y respondo con lo que dicen. No envío nada a nadie: si necesitas a una persona, Ayuda está al pie de este panel.',
    reading: 'One está leyendo…',
    askLabel: 'Hazle una pregunta a One',
    placeholder: 'Escribe tu pregunta…',
    ask: 'Preguntar',
    startOver: 'Empezar de nuevo',
    openTicket: 'Abrir una solicitud',
    shareFeedback: 'Enviar comentarios',
    close: 'Cerrar Ask One',
    foot:
      'Escrito por IA. Esta conversación es tuya: tus colegas no pueden verla, y One no envía nada a nadie.',
    helpLead: '¿Necesitas a una persona?',
    helpLink: 'Ayuda',
    requestLink: {
      idea: 'Enviarla como comentario',
      problem: (lane) => `Avisar a ${lane.to}`,
      technical: (lane) => `Preguntar a ${lane.to}`,
    },
    requestLinkNote: 'Abre un formulario con tus palabras. No se envía nada hasta que pulses Enviar.',
    pageChipRemove: 'Preguntar sin esta página',
    startersHead: 'Prueba a preguntar',
    notNow: 'Ahora no',
    communityName: 'Pregúntale a la comunidad OneConetec',
    communityWho:
      'Se publica con tu nombre una vez que pasa la revisión de contenido. Todos pueden verla y responder.',
    questionLabel: 'Tu pregunta, tal como aparecerá',
    questionPlaceholder: 'Tu pregunta, en una línea',
    detailLabel: 'Contexto: lo que ya intentaste, con tus palabras',
    detailPlaceholder: 'Cualquier contexto (opcional)',
    publicWarn: [
      'Esto va al foro de Wiki One ',
      'con tu nombre',
      ', donde cualquier colega puede leerla, votarla y responderla. Primero se revisa según las pautas de contenido: si infringe alguna, no se publica y te diremos por qué.',
    ],
    laneWarn: (lane) => [
      `Solo quienes atienden ${lane.queue} ven esto, y `,
      'ven tu nombre',
      '. No se publica, no va a ningún foro y ningún otro colega puede leerlo.',
    ],
    picturesLabel: 'Capturas de pantalla o fotos',
    picturesHint: (max) => `Hasta ${max}. Llegan a las mismas personas que la pregunta y a nadie más.`,
    attachPicture: 'Adjuntar una captura o una foto',
    capturePage: 'Agregar una imagen de esta página',
    capturing: 'Dibujando la página…',
    removePicture: 'Quitar',
    pagePictureTag: 'Imagen de la página',
    pageDetailsNote: (route, viewport, app) =>
      `Se envían también los datos de la página, para que el equipo vea dónde estabas: pantalla ${route}, ventana ${viewport}, versión ${app}, y tu navegador e idioma.`,
    pictureFailed: 'No se pudo agregar esa imagen. Prueba con JPEG, PNG o WebP.',
    sentPicturesFailed: (n) =>
      `${n === 1 ? 'Una imagen no llegó' : `${n} imágenes no llegaron`}. Dilo en una respuesta y el equipo te la${n === 1 ? '' : 's'} pedirá.`,
    sending: 'Enviando…',
    sendTo: (lane) => `Enviar a ${lane.to}`,
    postToCommunity: 'Publicar en la comunidad',
    noHrRoute: [
      'En el portal no hay un canal privado para preguntas sobre tu empleo, y ',
      'esto no debe ir a un foro público',
      ': llevaría tu nombre frente a todos tus colegas. Habla directamente con tu jefatura o con RR. HH. Si es algo que no puedes plantear por la línea de mando, el canal para eso es VINCI Integrity.',
    ],
    escalateDetail: (summary) =>
      `Primero le pregunté a One y me dijo:\n\n"${summary}"\n\nEso no respondió mi pregunta.`,
    escalateNoAnswer: 'Primero le pregunté a One y no tenía respuesta para esto.',
    sentTo: (lane) => `Se envió a ${lane.to}`,
    sentLaneBody:
      'Ya la tienen, con tu nombre y lo que escribiste. Recibirás una notificación aquí cuando alguien responda; nada de esto se publica.',
    sentFeedbackHead: 'Gracias: el equipo de la plataforma ya lo tiene',
    sentFeedbackBody:
      'Está en Tus solicitudes, en la página de Ayuda, con tu nombre y lo que escribiste. Recibirás una notificación cuando alguien responda. Nada de esto se publica.',
    liveHead: 'Ya está en el foro',
    liveBody:
      'Tu pregunta ya está en Wiki One, con tu nombre. Recibirás una notificación aquí cuando alguien la responda.',
    blockedHead: 'No se publicó',
    blockedBody: (reason) =>
      `${reason} No se publicó nada. Un moderador también la revisará y puede publicarla si la revisión se equivocó.`,
    heldHead: 'Guardada: un curador la publicará',
    heldBody:
      'No pudimos contactar la revisión de contenido, así que tu pregunta espera a un curador en vez de ir directo al foro. Recibirás una notificación cuando alguien la responda.',
    askSomethingElse: 'Preguntar otra cosa',
    goodAnythingElse: 'Bien. ¿Algo más que quieras preguntar?',
    thanksTeam: 'Gracias, el equipo lo verá.',
    whatWasWrong: '¿Qué estuvo mal?',
    skip: 'Omitir',
    wasUseful: '¿Te sirvió?',
    yesUseful: 'Sí, me sirvió',
    noUseful: 'No, no me sirvió',
    fromWiki: (w) => `De ${esAnswers(w)}`,
    fromGuide: (h) => `De la guía de la plataforma: ${esEntries(h)}`,
    fromBoth: (w, h) => `De ${esAnswers(w)} y ${esEntries(h)}`,
    fromScreens: (screens) => `Leído de tus propias pantallas: ${screens}`,
    fromGuides: (sections) => `De tus guías: ${sections}`,
    lookedAt: 'Revisé',
    openPage: (page) => `Abrir ${page}`,
    sourcesLabel: 'De dónde viene esta respuesta',
    somethingElse: 'Otra cosa',
    readingAs: (s) => `Lo entiendo como: ${s}`,
    notIt: '¿No es eso?',
    requestBody: {
      idea: 'Eso suena a una idea para el portal más que a una pregunta, así que no hay nada que yo pueda buscar.',
      problem: 'Eso suena a algo del portal que no funciona como debería, y no es algo que yo pueda buscar ni arreglar desde aquí.',
      technical:
        'Esa es una pregunta técnica sobre el trabajo. No las respondo yo, para que nadie actúe sobre lo que una máquina supone de datos de terreno o equipos.',
    },
  },
}

/* ── THE HUB'S HOME SCREEN: the feed card, a post, the rail cards ──────────── */

/**
 * The FRAME around what people post — never the posts, which are content in
 * whatever language their author wrote (FED-5's variants), and never the
 * pinned announcement's title and body, which are Comms' copy. The countdown's
 * phrase after the number ("until we go public") is Comms' too: 113 gave it a
 * Spanish row (launch_milestone_label), and launch_countdown() returns it in the
 * reader's language while it still translates the live English. The card's
 * frame — its title, "days", and the arrived line — is here.
 */
export type HomeCopy = {
  /** 11 Oct. Names the rail / the phone's swipe row to a screen reader. */
  hooksLabel: string
  loadingFeed: string
  feedError: string
  nothingPublished: string
  /** 125 (HLP-23). The #/item/:id permalink a bell row opens. */
  itemGone: string
  backToHub: string
  itemLoading: string
  postedUnchecked: string
  postSomething: string
  yourDrafts: string
  /** The floating post button's name and its menu's (12 Oct). */
  postFabLabel: string
  editDraft: string
  // 115 — editing your own live post
  editPost: string
  editThisPost: string
  editLoadFailed: string
  // A post
  authorUnavailable: string
  /** 300. The small line under a byline: who actually posted it. */
  postedBy: (name: string) => string
  edited: string
  everyone: string
  noVariant: (locale: string) => string
  uncheckedTitle: string
  publishedUnchecked: string
  // 113 — the launch countdown's frame; the phrase after "days" is Comms' label
  countdownTitle: string
  countdownDays: (label: string) => string
  countdownArrived: string
  // 111 — a machine translation, and the way back to the author's words
  translatedByOne: string
  translatedTitle: string
  showOriginal: string
  showTranslation: string
  originalIn: (locale: string) => string
  originalFailed: string
  addComment: string
  noComments: string
  /** The control under the comment bar (25 Sep): it names the act, in blue. */
  showComments: (n: number) => string
  hideComments: string
  reactions: Record<ReactionKind, string>
  reactionCount: (label: string, n: number) => string
  reactionFailed: string
  totalReactions: (n: number) => string
  // The pinned announcement's frame
  pinned: string
  lockedSub: string
  readAnnouncement: string
  internalOnly: string
  // 156: the video's show-from strip, schedulers only
  videoHiddenUntil: (when: string) => string
  videoShownToAll: string
  videoHideUntil: string
  videoChangeTime: string
  videoShowNow: string
  videoShowFrom: string
  videoSave: string
  videoCancel: string
  videoSaveFailed: string
  // 198: the video's view counts, whole-company analytics.read only
  videoViewsTitle: string
  videoViewsStages: { started: string; sound: string; half: string; finished: string }
  videoViewsToday: (n: string) => string
  videoViewsNote: string
  videoViewsNone: string
  // The rail
  starsYouGave: string
  givenThisMonth: string
  leftToGive: (days: number) => string
  gaveNobody: string
  youRecognized: (name: string) => string
  recognizeColleague: string
  starsYouReceived: string
  store: string
  toSpend: string
  arrivingShortly: string
  receivedNobody: string
  redeem: string
  /** 11 Oct. The rail card's button; `redeem` is its accessible name. */
  redeemShort: string
}

export const HOME: Record<HubLang, HomeCopy> = {
  en: {
    hooksLabel: 'This week on the hub',
    loadingFeed: 'Loading the feed…',
    feedError: 'The feed could not be loaded.',
    nothingPublished: 'Nothing published yet.',
    itemGone: 'This is no longer available. It may have been removed, or it is not shared with you.',
    backToHub: 'Back to the hub',
    itemLoading: 'Opening…',
    postedUnchecked:
      'Posted. Nothing was able to check it against the guidelines just now, so it is marked unscreened and a moderator will see it.',
    postSomething: 'Post something',
    yourDrafts: 'Your drafts',
    postFabLabel: 'Post, recognize or open a draft',
    editDraft: 'Edit your draft',
    editPost: 'Edit your post',
    editThisPost: 'Edit',
    editLoadFailed: 'This post could not be opened for editing. Try again.',
    authorUnavailable: 'Author unavailable',
    postedBy: (name) => `posted by ${name}`,
    edited: 'edited',
    everyone: 'Everyone',
    noVariant: (locale) => `No ${locale} variant exists yet`,
    uncheckedTitle: 'The FED-10 guidelines check could not run',
    publishedUnchecked: 'Published unchecked',
    countdownTitle: 'Launch countdown',
    countdownDays: (label) => `days ${label}`,
    countdownArrived: 'we are public',
    translatedByOne: 'Translated by One',
    translatedTitle: 'A machine translated this from the author’s own language. Nobody has reviewed it.',
    showOriginal: 'Show original',
    showTranslation: 'Show translation',
    originalIn: (locale) => `Original, in ${LANGUAGE_NAMES.en[locale] ?? locale}`,
    originalFailed: 'The original could not be loaded.',
    addComment: 'Add a comment…',
    noComments: 'No comments',
    showComments: (n) => `Show ${n} comment${n === 1 ? '' : 's'}`,
    hideComments: 'Hide comments',
    reactions: { like: 'Noted', celebrate: 'Well done', support: 'With you', learned: 'Learned something' },
    reactionCount: (label, n) => `${label} — ${n} ${n === 1 ? 'person' : 'people'}`,
    reactionFailed: 'That did not save. Try again.',
    totalReactions: (n) => `${n} ${n === 1 ? 'reaction' : 'reactions'}`,
    pinned: 'Pinned',
    lockedSub: 'Plays for signed-in staff only. Not shareable outside the hub until the external launch.',
    readAnnouncement: 'Read the announcement',
    internalOnly: 'Internal only until the external launch in April 2027. Please do not share this outside the hub.',
    videoHiddenUntil: (when) => `Hidden from staff until ${when}. You can see it early because you schedule it.`,
    videoShownToAll: 'Everyone can see this video.',
    videoHideUntil: 'Hide until…',
    videoChangeTime: 'Change time',
    videoShowNow: 'Show it now',
    videoShowFrom: 'Show to staff from',
    videoSave: 'Save',
    videoCancel: 'Cancel',
    videoSaveFailed: 'That did not save. Try again.',
    videoViewsTitle: 'Video views',
    videoViewsStages: { started: 'Started', sound: 'With sound', half: 'Halfway', finished: 'To the end' },
    videoViewsToday: (n) => `today ${n}`,
    videoViewsNote: 'Each device counts once at each stage. Nobody’s name is recorded.',
    videoViewsNone: 'No views counted yet.',
    starsYouGave: 'Stars you gave',
    givenThisMonth: 'given this month',
    leftToGive: (days) => `left to give — resets in ${days} ${days === 1 ? 'day' : 'days'}`,
    gaveNobody: 'You have not recognized anybody yet.',
    youRecognized: (name) => `You recognized ${name}`,
    recognizeColleague: 'Recognize a colleague',
    starsYouReceived: 'Stars you received',
    store: 'Store',
    toSpend: 'to spend',
    arrivingShortly: 'arriving shortly',
    receivedNobody: 'Nobody has recognized you yet — but you can recognize somebody.',
    redeem: 'Redeem stars in the store',
    redeemShort: 'Redeem',
  },
  es: {
    hooksLabel: 'Esta semana en el hub',
    loadingFeed: 'Cargando las novedades…',
    feedError: 'No se pudieron cargar las novedades.',
    nothingPublished: 'Todavía no hay nada publicado.',
    itemGone: 'Esto ya no está disponible. Puede que se haya eliminado o que no esté compartido contigo.',
    backToHub: 'Volver al portal',
    itemLoading: 'Abriendo…',
    postedUnchecked:
      'Publicado. En este momento nada pudo revisarlo según las pautas, así que queda marcado como no revisado y un moderador lo verá.',
    postSomething: 'Publicar algo',
    yourDrafts: 'Tus borradores',
    postFabLabel: 'Publicar, reconocer o abrir un borrador',
    editDraft: 'Editar tu borrador',
    editPost: 'Editar tu publicación',
    editThisPost: 'Editar',
    editLoadFailed: 'No se pudo abrir esta publicación para editarla. Inténtalo de nuevo.',
    authorUnavailable: 'Autor no disponible',
    postedBy: (name) => `publicado por ${name}`,
    edited: 'editado',
    everyone: 'Todos',
    noVariant: (locale) => `Todavía no existe una versión en ${LANGUAGE_NAMES.es[locale] ?? locale}`,
    uncheckedTitle: 'La revisión de pautas (FED-10) no pudo ejecutarse',
    publishedUnchecked: 'Publicado sin revisar',
    countdownTitle: 'Cuenta regresiva',
    countdownDays: (label) => `días ${label}`,
    countdownArrived: 'el lanzamiento público ya llegó',
    translatedByOne: 'Traducido por One',
    translatedTitle: 'Una máquina lo tradujo del idioma del autor. Nadie lo ha revisado.',
    showOriginal: 'Ver original',
    showTranslation: 'Ver traducción',
    originalIn: (locale) => `Original, en ${LANGUAGE_NAMES.es[locale] ?? locale}`,
    originalFailed: 'No se pudo cargar el original.',
    addComment: 'Agregar un comentario…',
    noComments: 'Sin comentarios',
    showComments: (n) => `Ver ${n} ${n === 1 ? 'comentario' : 'comentarios'}`,
    hideComments: 'Ocultar comentarios',
    reactions: { like: 'Anotado', celebrate: 'Bien hecho', support: 'Contigo', learned: 'Aprendí algo' },
    reactionCount: (label, n) => `${label}: ${n} ${n === 1 ? 'persona' : 'personas'}`,
    reactionFailed: 'No se guardó. Inténtalo de nuevo.',
    totalReactions: (n) => `${n} ${n === 1 ? 'reacción' : 'reacciones'}`,
    pinned: 'Fijado',
    lockedSub: 'Solo se reproduce para el personal que ingresó. No se puede compartir fuera del portal hasta el lanzamiento externo.',
    readAnnouncement: 'Leer el anuncio',
    internalOnly: 'Solo uso interno hasta el lanzamiento externo en abril de 2027. Por favor, no lo compartas fuera del portal.',
    videoHiddenUntil: (when) => `Oculto para el personal hasta el ${when}. Lo ves antes porque puedes programarlo.`,
    videoShownToAll: 'Todos pueden ver este video.',
    videoHideUntil: 'Ocultar hasta…',
    videoChangeTime: 'Cambiar hora',
    videoShowNow: 'Mostrar ahora',
    videoShowFrom: 'Mostrar al personal desde',
    videoSave: 'Guardar',
    videoCancel: 'Cancelar',
    videoSaveFailed: 'No se guardó. Inténtalo de nuevo.',
    videoViewsTitle: 'Vistas del video',
    videoViewsStages: { started: 'Iniciado', sound: 'Con sonido', half: 'Hasta la mitad', finished: 'Hasta el final' },
    videoViewsToday: (n) => `hoy ${n}`,
    videoViewsNote: 'Cada dispositivo cuenta una vez en cada etapa. No se registra el nombre de nadie.',
    videoViewsNone: 'Aún no hay vistas contadas.',
    starsYouGave: 'Stars que diste',
    givenThisMonth: 'dadas este mes',
    leftToGive: (days) => `por dar: se reinician en ${days} ${days === 1 ? 'día' : 'días'}`,
    gaveNobody: 'Todavía no has reconocido a nadie.',
    youRecognized: (name) => `Reconociste a ${name}`,
    recognizeColleague: 'Reconocer a un colega',
    starsYouReceived: 'Stars que recibiste',
    store: 'Tienda',
    toSpend: 'para gastar',
    arrivingShortly: 'llegan pronto',
    receivedNobody: 'Todavía nadie te ha reconocido, pero tú puedes reconocer a alguien.',
    redeem: 'Canjear Stars en la tienda',
    redeemShort: 'Canjear',
  },
}

/* ── WRITING: the composer, the draft picker, a photo, the @ menu ──────────── */

/**
 * The composer is the one place most people WRITE, so it is the screen where
 * an English frame around Spanish words is felt most. What is NOT here: the
 * check's own reason for a block (`blocked.reason`) — that is the model's
 * sentence, written in the language of the post — and a server error, which
 * `messageOf` passes through as Postgres raised it. Both stay as they arrive.
 */
export type ComposeCopy = {
  editingDraft: string
  // 115 — a live post
  editingLive: string
  liveNote: string
  liveKeptPhoto: (i: number) => string
  liveRemoveKeptPhoto: (i: number) => string
  editNotSaved: string
  editBlockedAppeal: string
  saveChanges: string
  checkingEdit: string
  postingAs: (name: string) => string
  // 300 — a byline ("Communications"), offered only to people who hold one
  postAsLabel: string
  postAsMe: (name: string) => string
  editingLiveUnder: (byline: string) => string
  editingDraftUnder: (byline: string) => string
  bylineNote: (byline: string, name: string) => string
  takeDown: string
  takeDownConfirm: (byline: string) => string
  takeDownYes: string
  takeDownNo: string
  takingDown: string
  headline: string
  optional: string
  headlinePlaceholder: string
  yourPost: string
  bodyPlaceholder: string
  tags: string
  keptPhoto: (i: number) => string
  removeKeptPhoto: (i: number) => string
  remove: string
  describePhoto: string
  photoDescription: (i: number) => string
  removePhoto: (i: number) => string
  /** FED-66. Cropping a new photo before it is posted. */
  crop: string
  cropFor: (i: number) => string
  cropCopy: Pick<CardCopy, 'cropTitle' | 'cropFrameLabel' | 'cropHelp' | 'zoomLabel' | 'photoSaving' | 'usePhoto' | 'cancel'>
  cropShape: string
  cropShapes: Record<CropShapeKey, string>
  cropTagsNote: string
  cropFailed: string
  addPhotos: string
  photoCount: (n: number, max: number) => string
  photoHint: string
  audience: Emph
  checkedNote: string
  notPublished: string
  guidelines: (list: string) => string
  blockedAppeal: string
  working: string
  post: string
  saveDraft: string
  saveAsDraft: string
  cancel: string
  photoLimit: (max: number) => string
  preparingPhotos: string
  uploadingPhoto: (i: number, n: number) => string
  /** 146. The full-size copy of an entered photo. */
  uploadingOriginal: (i: number, n: number) => string
  saving: string
  checkingPost: string
  checkingPostAndPhotos: string
  // The draft picker
  lookingForDrafts: string
  noDrafts: string
  untitled: string
  stoppedByCheck: string
  draft: string
  open: string
  delete: string
  close: string
  confirmDelete: string
  confirmRemove: string
  // A photo in a post
  photoFailed: string
  // 148 / 150 (FED-53, FED-54) — posting is the opt-in; a photo is opted out
  // of the contest, the use licence (which earns the stars), its camera
  // details and its location, each on its own. photoLine + photoHelp +
  // optOut* + useLabel are what photo_permission.notice_version
  // '2026-10-10-licence-earns-stars' records: change the wording, change the
  // version in set_photo_uses too.
  photoLine: string
  photoHelpTitle: string
  photoHelp: (stars: { n: number; cap: number } | null) => string[]
  optOutLink: string
  optOutFor: (i: number) => string
  optOutChange: string
  outOfContest: string
  outOfLicence: string
  optedOutAll: string
  optOutTitle: string
  optOutIntro: string
  useLabel: Record<'contest' | 'licence', string>
  useHint: Record<'contest', string>
  licenceHint: (stars: number | null) => string
  optOutAll: string
  optOutReason: string
  optOutLicenceStars: (n: number) => string
  optOutDone: string
  detailLabel: Record<'camera' | 'location', string>
  detailHint: Record<'camera' | 'location', string>
  detailsNotHeld: string
  noDetail: Record<'camera' | 'location', string>
  removeDetail: Record<'camera' | 'location', string>
  detailRemoved: Record<'camera' | 'location', string>
  // CMP-7 — themed contests offered as the post goes up
  alsoEnter: string
  alsoEnterHint: string
  enteredIn: (titles: string[]) => string
  notEnteredIn: (title: string, why: string) => string
  postIsUp: string
  // 143 (FED-49) — people in a photo
  tagPeople: string
  tagPeopleFor: (i: number) => string
  tagSearch: string
  tagAdd: (name: string) => string
  tagRemove: (name: string) => string
  tagNobody: string
  tagLimit: string
  tagNote: string
  taggedWith: string
  youAreTagged: string
  removeMyTag: string
  myTagRemoved: string
  /** 170: where the box was — how to ask the company not to use it. */
  myReviewNote: string
  tagFailed: string
  // 161 — a tag sits on the person in the photo
  tagHint: string
  tagPlaceHint: (name: string) => string
  tagWho: string
  tagInPhoto: (n: number) => string
  tagNoneYet: string
  tagNotPlaced: string
  tagPlace: string
  tagMove: string
  tagAddByName: string
  tagPin: (name: string) => string
  tagDone: string
  tagEdit: string
  tagMore: (n: number) => string
  tagPhotoLoading: string
  showTags: (n: number) => string
  hideTags: string
  photoCard: (i: number, n: number) => string
  dropPhotos: string
  dragHint: string
  // 163 — a colleague suggests a tag; the poster or a moderator decides
  suggestTag: string
  suggestTitle: string
  suggestHint: string
  thatsMe: string
  thatsMeHere: string
  suggestByName: string
  suggestResult: (outcome: string, name: string | null) => string
  suggestionsWaiting: (n: number) => string
  suggestedBy: (name: string) => string
  yourSuggestion: (name: string) => string
  acceptSuggestion: (name: string) => string
  declineSuggestion: (name: string) => string
  accept: string
  decline: string
  withdraw: string
  suggestionClosed: string
  suggestedPin: (name: string) => string
  /** 144 */
  needsSomething: string
  // The photo viewer
  photo: string
  photoOf: (i: number, n: number) => string
  openPhoto: (i: number, n: number) => string
  previousPhoto: string
  nextPhoto: string
  // 142 — what a ticked photo earns, from star_earning_rule
  earnStars: (n: number) => string
  contestStars: (n: number, cap: number) => string
  // The @ menu and every people picker's foot
  noOneByThatName: string
  showingMatches: (shown: number, total: number) => string
  showingPeople: (shown: number, total: number) => string
  /** 336: the same foot under a list of words ("Show all" on What for), where "people" and "a name" are wrong. */
  showingWords: (shown: number, total: number) => string
}

export const COMPOSE: Record<HubLang, ComposeCopy> = {
  en: {
    editingDraft: 'Editing your draft',
    editingLive: 'Editing your post. It is already live',
    liveNote:
      'Your changes are checked against the content guidelines before they replace what is there, and the post will say it was edited. You can remove photos, and add up to four new ones.',
    liveKeptPhoto: (i) => `Photo ${i} on this post`,
    liveRemoveKeptPhoto: (i) => `Remove photo ${i} from this post`,
    editNotSaved: 'Your changes were not saved. The post still says what it said before.',
    editBlockedAppeal: 'Your changes are still here. Edit them and try again.',
    saveChanges: 'Save changes',
    checkingEdit: 'Checking your changes…',
    postingAs: (name) => `Posting as ${name}`,
    postAsLabel: 'Post as',
    postAsMe: (name) => `${name} (you)`,
    editingLiveUnder: (byline) => `Editing a ${byline} post. It is already live`,
    editingDraftUnder: (byline) => `Editing a ${byline} draft`,
    bylineNote: (byline, name) =>
      `Everyone sees it came from ${byline}, posted by ${name}. Anyone who can post as ${byline} can edit it or take it down. It earns no stars, and its photos are not entered in contests.`,
    takeDown: 'Take down',
    takeDownConfirm: (byline) =>
      `Take this post down? Anyone who can post as ${byline} can do this. It stays in the record as removed.`,
    takeDownYes: 'Take it down',
    takeDownNo: 'Keep it',
    takingDown: 'Taking it down…',
    headline: 'Headline',
    optional: 'optional',
    headlinePlaceholder: 'One line, if it helps',
    yourPost: 'Your post',
    bodyPlaceholder: 'What happened, where, and who did it. Use #tags to say where or what.',
    tags: 'Tags:',
    keptPhoto: (i) => `Photo ${i} from this draft`,
    removeKeptPhoto: (i) => `Remove photo ${i} from this draft`,
    remove: 'Remove',
    describePhoto: 'Add a caption (optional)',
    photoDescription: (i) => `Caption for photo ${i}`,
    removePhoto: (i) => `Remove photo ${i}`,
    crop: 'Crop',
    cropFor: (i) => `Crop photo ${i}`,
    cropCopy: {
      cropTitle: 'Crop the photo',
      cropFrameLabel: 'The photo. Arrow keys move it, plus and minus zoom.',
      cropHelp: 'Pick a shape, then drag to move it. Pinch, scroll or use the slider to zoom. What is inside the frame is what gets posted.',
      zoomLabel: 'Zoom',
      photoSaving: 'Cropping…',
      usePhoto: 'Use this crop',
      cancel: 'Cancel',
    },
    cropShape: 'Shape',
    cropShapes: { original: 'Original', square: 'Square', portrait: 'Tall 4:5', wide: 'Wide 16:9' },
    cropTagsNote: 'Anyone tagged outside the new frame stays tagged, without a pin.',
    cropFailed: 'That photo could not be cropped. It is unchanged.',
    addPhotos: 'Add photos',
    photoCount: (n, max) => `${n} of ${max}`,
    photoHint: 'Up to nine. Resized on your device.',
    audience: ['Goes to ', 'everyone at Conetec', '. Posts cannot be aimed at one business unit from here.'],
    checkedNote: 'Posts and photos are checked against the content guidelines before they publish.',
    notPublished: 'This was not published.',
    guidelines: (list) => `Guidelines ${list}`,
    blockedAppeal:
      'Your text and photos are still here. Edit and try again, or ask a moderator to look at it.',
    working: 'Working…',
    post: 'Post',
    saveDraft: 'Save draft',
    saveAsDraft: 'Save as draft',
    cancel: 'Cancel',
    photoLimit: (max) => `${max} photos is the limit on one post.`,
    preparingPhotos: 'Preparing photos…',
    uploadingPhoto: (i, n) => `Uploading photo ${i} of ${n}…`,
    uploadingOriginal: (i, n) => `Saving the full-size copy of photo ${i} of ${n} for the photo contest…`,
    saving: 'Saving…',
    checkingPost: 'Checking your post…',
    checkingPostAndPhotos: 'Checking your post and photos…',
    lookingForDrafts: 'Looking for your drafts…',
    noDrafts: 'Nothing saved yet. Anything you write and save instead of posting shows up here.',
    untitled: 'Untitled',
    stoppedByCheck: 'Stopped by the check',
    draft: 'Draft',
    open: 'Open',
    delete: 'Delete',
    close: 'Close',
    confirmDelete: 'Delete this draft? Nobody has seen it, so it will be gone for good.',
    confirmRemove: 'Remove this post? It was published, so the record of it is kept.',
    photoFailed: 'This photo did not load.',
    photoLine: 'Photos you post go in the monthly photo contest and are licensed for company use.',
    photoHelpTitle: 'About your photos',
    photoHelp: (stars) => [
      'Posting a photo enters it in the monthly photo contest and licenses it for company use: marketing, sales, internal training and other uses.' +
        (stars?.n ? ` Each licensed photo earns ${stars.n.toLocaleString('en-CA')} stars when your post goes live, up to ${stars.cap.toLocaleString('en-CA')} a month.` : ''),
      'To keep a photo out of the contest, out of company use, or both, use "Opt out" under it. Opting out of the contest costs nothing; opting out of company use means no stars. You can change it later by editing the post.',
      'The camera and date taken, and where a photo was taken, go to photo room admins only — never to the feed, where every photo is resized and stripped. Each can be kept back on its own in the same "Opt out" menu, and removed later.',
      'People you tag are told once the post is live. They can remove their tag and keep the photo out of company marketing; it stays in photo contests, which are yours to enter.',
      'Only post photos of people who are happy to be in them.',
    ],
    optOutLink: 'Opt out of the contest, company use or photo details',
    optOutFor: (i) => `Opt photo ${i} out of the contest, company use or photo details`,
    optOutChange: 'Change',
    outOfContest: 'Out of the photo contest',
    outOfLicence: 'Not licensed for company use',
    optedOutAll: 'Feed only — out of the contest and company use',
    optOutTitle: 'Keep this photo out of…',
    optOutIntro: 'Posting a photo is how it comes in. Tick what this one should stay out of.',
    useLabel: { contest: 'Photo contest', licence: 'Marketing, sales, internal training and other uses' },
    useHint: { contest: 'The monthly contest and any themed contest. Opting out costs nothing.' },
    licenceHint: (stars) =>
      'Free use by the company.' + (stars ? ` This is what earns the ${stars.toLocaleString('en-CA')} stars.` : ''),
    optOutAll: 'All of these',
    optOutReason: 'Why? (optional — photo room admins see this)',
    optOutLicenceStars: (n) => `Out of company use, this photo does not earn the ${n.toLocaleString('en-CA')} stars.`,
    optOutDone: 'Done',
    detailLabel: { camera: 'Camera details', location: 'Location (GPS)' },
    detailHint: {
      camera: 'The camera and when the photo was taken. Only photo room admins see it.',
      location: 'Where it was taken. Only photo room admins see it, and only for licensed photos.',
    },
    detailsNotHeld: 'None kept for this photo',
    noDetail: { camera: 'no camera details', location: 'no location' },
    removeDetail: { camera: 'Remove this photo\'s camera details', location: 'Remove this photo\'s location' },
    detailRemoved: { camera: 'Camera details removed.', location: 'Location removed.' },
    alsoEnter: 'Also enter in',
    alsoEnterHint: 'Photos you have not opted out of the contest go in each one you tick.',
    enteredIn: (titles) => `Entered in ${andList(titles, 'and')}.`,
    notEnteredIn: (title, why) => `Not entered in ${title}: ${why}`,
    postIsUp: 'Your post is up.',
    tagPeople: 'Tag people',
    tagPeopleFor: (i) => `Tag people in photo ${i}`,
    tagSearch: 'Start typing a name',
    tagAdd: (name) => `Tag ${name}`,
    tagRemove: (name) => `Remove ${name}`,
    tagNobody: 'Nobody by that name.',
    tagLimit: 'Up to 20 people in one photo.',
    tagNote:
      'People you tag are told once the post is live. They can remove their tag and keep the photo out of company marketing.',
    taggedWith: 'With',
    youAreTagged: 'You are tagged in this photo.',
    removeMyTag: 'Remove my tag',
    myTagRemoved: 'Your tag is removed.',
    myReviewNote: 'Don’t want the company to use this photo? Press Request review and choose “I’m in this photo”. You can do that for 30 days after you were tagged. Removing your tag on its own does not stop company use.',
    tagFailed: 'That did not save. Try again.',
    tagHint: 'Tap someone in the photo to tag them.',
    tagPlaceHint: (name) => `Tap where ${name} is in the photo.`,
    tagWho: 'Who is this?',
    tagInPhoto: (n) => `In this photo (${n})`,
    tagNoneYet: 'Nobody tagged yet.',
    tagNotPlaced: 'Not placed on the photo',
    tagPlace: 'Place',
    tagMove: 'Move',
    tagAddByName: 'Or add someone by name',
    tagPin: (name) => `${name}. Drag, or use the arrow keys, to move. Delete removes.`,
    tagDone: 'Done',
    tagEdit: 'Edit tags',
    tagMore: (n) => `+${n} more`,
    tagPhotoLoading: 'Loading the photo…',
    showTags: (n) => (n === 1 ? 'Show who is in this photo (1 person)' : `Show who is in this photo (${n} people)`),
    hideTags: 'Hide tags',
    photoCard: (i, n) => `Photo ${i} of ${n}`,
    dropPhotos: 'Drop photos to add them',
    dragHint: 'or drag them here',
    suggestTag: 'Suggest a tag',
    suggestTitle: 'Suggest who is in this photo',
    suggestHint: 'Tap someone in the photo and choose their name. The person who posted it decides; nobody else is told until then.',
    thatsMe: 'That\'s me',
    thatsMeHere: 'That\'s me, here',
    suggestByName: 'Or suggest someone without placing them',
    suggestResult: (outcome, name) => {
      const who = name ?? 'You'
      switch (outcome) {
        case 'suggested': return name ? `Suggested ${name}. The person who posted it decides.` : 'Suggested you. The person who posted it decides.'
        case 'already_tagged': return name ? `${name} is already tagged.` : 'You are already tagged.'
        case 'already_suggested': return name ? `${name} has already been suggested.` : 'You have already been suggested.'
        case 'removed_themselves': return `${who} removed ${name ? 'their' : 'your'} own tag from this photo, so cannot be suggested again.`
        case 'cannot_see': return `${who} cannot see this post, so cannot be tagged in it.`
        default: return `${who} cannot be tagged in this photo.`
      }
    },
    suggestionsWaiting: (n) => (n === 1 ? '1 suggested tag is waiting' : `${n} suggested tags are waiting`),
    suggestedBy: (name) => `suggested by ${name}`,
    yourSuggestion: (name) => `You suggested ${name}. Waiting for the person who posted it.`,
    acceptSuggestion: (name) => `Accept ${name}`,
    declineSuggestion: (name) => `Decline ${name}`,
    accept: 'Accept',
    decline: 'Decline',
    withdraw: 'Withdraw',
    suggestionClosed: 'That suggestion could no longer be accepted, so it was closed.',
    suggestedPin: (name) => `${name}?`,
    needsSomething: 'A post needs words or a photo. Add one, or remove the post from your posts instead.',
    photo: 'Photo',
    photoOf: (i, n) => `Photo ${i} of ${n}`,
    openPhoto: (i, n) => (n > 1 ? `Open photo ${i} of ${n}` : 'Open photo'),
    previousPhoto: 'Previous photo',
    nextPhoto: 'Next photo',
    earnStars: (n) => `+${n.toLocaleString('en-CA')} stars`,
    contestStars: (n, cap) =>
      `Each photo you enter earns ${n.toLocaleString('en-CA')} stars when your post goes live, up to ${cap.toLocaleString('en-CA')} a month.`,
    noOneByThatName: 'No one by that name.',
    showingMatches: (shown, total) =>
      `Showing ${shown} of ${total} matches — keep typing to narrow the list.`,
    showingPeople: (shown, total) =>
      `Showing ${shown} of ${total} people — type a name to narrow the list.`,
    showingWords: (shown, total) =>
      `Showing ${shown} of ${total} — type to narrow the list.`,
  },
  es: {
    editingDraft: 'Editando tu borrador',
    editingLive: 'Editando tu publicación. Ya está publicada',
    liveNote:
      'Tus cambios se revisan según las pautas de contenido antes de reemplazar lo que hay, y la publicación indicará que fue editada. Puedes quitar fotos y agregar hasta cuatro nuevas.',
    liveKeptPhoto: (i) => `Foto ${i} de esta publicación`,
    liveRemoveKeptPhoto: (i) => `Quitar la foto ${i} de esta publicación`,
    editNotSaved: 'Tus cambios no se guardaron. La publicación sigue diciendo lo mismo que antes.',
    editBlockedAppeal: 'Tus cambios siguen aquí. Edítalos e inténtalo de nuevo.',
    saveChanges: 'Guardar cambios',
    checkingEdit: 'Revisando tus cambios…',
    postingAs: (name) => `Publicando como ${name}`,
    postAsLabel: 'Publicar como',
    postAsMe: (name) => `${name} (tú)`,
    editingLiveUnder: (byline) => `Editando una publicación de ${byline}. Ya está publicada`,
    editingDraftUnder: (byline) => `Editando un borrador de ${byline}`,
    bylineNote: (byline, name) =>
      `Todos ven que viene de ${byline}, publicado por ${name}. Quien pueda publicar como ${byline} puede editarla o retirarla. No gana estrellas y sus fotos no entran en concursos.`,
    takeDown: 'Retirar',
    takeDownConfirm: (byline) =>
      `¿Retirar esta publicación? Quien pueda publicar como ${byline} puede hacerlo. Queda en el registro como retirada.`,
    takeDownYes: 'Retirarla',
    takeDownNo: 'Mantenerla',
    takingDown: 'Retirando…',
    headline: 'Título',
    optional: 'opcional',
    headlinePlaceholder: 'Una línea, si ayuda',
    yourPost: 'Tu publicación',
    bodyPlaceholder: 'Qué pasó, dónde y quién lo hizo. Usa #etiquetas para decir dónde o qué.',
    tags: 'Etiquetas:',
    keptPhoto: (i) => `Foto ${i} de este borrador`,
    removeKeptPhoto: (i) => `Quitar la foto ${i} de este borrador`,
    remove: 'Quitar',
    describePhoto: 'Añade un pie de foto (opcional)',
    photoDescription: (i) => `Pie de la foto ${i}`,
    removePhoto: (i) => `Quitar la foto ${i}`,
    crop: 'Recortar',
    cropFor: (i) => `Recortar la foto ${i}`,
    cropCopy: {
      cropTitle: 'Recorta la foto',
      cropFrameLabel: 'La foto. Las flechas la mueven; más y menos hacen zoom.',
      cropHelp: 'Elige una forma y arrástrala para moverla. Pellizca, usa la rueda o el control para hacer zoom. Lo que queda dentro del marco es lo que se publica.',
      zoomLabel: 'Ampliar',
      photoSaving: 'Recortando…',
      usePhoto: 'Usar este recorte',
      cancel: 'Cancelar',
    },
    cropShape: 'Forma',
    cropShapes: { original: 'Forma original', square: 'Cuadrada', portrait: 'Vertical 4:5', wide: 'Panorámica 16:9' },
    cropTagsNote: 'Quien esté etiquetado fuera del nuevo marco sigue etiquetado, sin marcador.',
    cropFailed: 'No se pudo recortar la foto. Quedó como estaba.',
    addPhotos: 'Agregar fotos',
    photoCount: (n, max) => `${n} de ${max}`,
    photoHint: 'Hasta nueve. Se ajustan en tu dispositivo.',
    audience: [
      'Llega a ',
      'todas las personas de Conetec',
      '. Desde aquí no se puede dirigir una publicación a una sola unidad de negocio.',
    ],
    checkedNote: 'Las publicaciones y las fotos se revisan según las pautas de contenido antes de publicarse.',
    notPublished: 'Esto no se publicó.',
    guidelines: (list) => `Pautas ${list}`,
    blockedAppeal:
      'Tu texto y tus fotos siguen aquí. Edítalo e inténtalo de nuevo, o pídele a un moderador que lo revise.',
    working: 'Procesando…',
    post: 'Publicar',
    saveDraft: 'Guardar borrador',
    saveAsDraft: 'Guardar como borrador',
    cancel: 'Cancelar',
    photoLimit: (max) => `El límite es de ${max} fotos por publicación.`,
    preparingPhotos: 'Preparando las fotos…',
    uploadingPhoto: (i, n) => `Subiendo la foto ${i} de ${n}…`,
    uploadingOriginal: (i, n) => `Guardando la copia en tamaño completo de la foto ${i} de ${n} para el concurso…`,
    saving: 'Guardando…',
    checkingPost: 'Revisando tu publicación…',
    checkingPostAndPhotos: 'Revisando tu publicación y tus fotos…',
    lookingForDrafts: 'Buscando tus borradores…',
    noDrafts: 'Todavía no hay nada guardado. Lo que escribas y guardes en vez de publicar aparece aquí.',
    untitled: 'Sin título',
    stoppedByCheck: 'Detenida por la revisión',
    draft: 'Borrador',
    open: 'Abrir',
    delete: 'Eliminar',
    close: 'Cerrar',
    confirmDelete: '¿Eliminar este borrador? Nadie lo ha visto, así que desaparecerá para siempre.',
    confirmRemove: '¿Quitar esta publicación? Ya se publicó, así que se conserva el registro.',
    photoFailed: 'Esta foto no se cargó.',
    photoLine: 'Las fotos que publicas participan en el concurso mensual y quedan con licencia de uso para la empresa.',
    photoHelpTitle: 'Sobre tus fotos',
    photoHelp: (stars) => [
      'Publicar una foto la inscribe en el concurso mensual y le da a la empresa licencia para usarla: marketing, ventas, capacitación interna y otros usos.' +
        (stars?.n ? ` Cada foto con licencia gana ${stars.n.toLocaleString('es-CL')} Stars cuando tu publicación sale, hasta ${stars.cap.toLocaleString('es-CL')} al mes.` : ''),
      'Para dejar una foto fuera del concurso, fuera del uso de la empresa o de ambos, toca "Excluir" debajo de ella. Salir del concurso no cuesta nada; salir del uso de la empresa significa no ganar Stars. Puedes cambiarlo después editando la publicación.',
      'La cámara y la fecha de la foto, y dónde se tomó, solo los ven los administradores de la sala de fotos; nunca el feed, donde cada foto se reduce y se limpia. Cada uno se puede excluir por separado en el mismo menú "Excluir", y quitar después.',
      'Las personas que etiquetes reciben un aviso cuando la publicación esté visible. Pueden quitar su etiqueta y dejar la foto fuera del marketing de la empresa; la foto sigue en los concursos, que decides tú.',
      'Publica fotos de personas solo si están de acuerdo en aparecer.',
    ],
    optOutLink: 'Excluir del concurso, del uso de la empresa o los datos de la foto',
    optOutFor: (i) => `Excluir la foto ${i} del concurso, del uso de la empresa o sus datos`,
    optOutChange: 'Cambiar',
    outOfContest: 'Fuera del concurso de fotos',
    outOfLicence: 'Sin licencia de uso para la empresa',
    optedOutAll: 'Solo en el feed: fuera del concurso y del uso de la empresa',
    optOutTitle: 'Dejar esta foto fuera de…',
    optOutIntro: 'Al publicar una foto, queda incluida. Marca de qué debe quedar fuera.',
    useLabel: { contest: 'Concurso de fotos', licence: 'Marketing, ventas, capacitación interna y otros usos' },
    useHint: { contest: 'El concurso mensual y cualquier concurso temático. Salir no cuesta nada.' },
    licenceHint: (stars) =>
      'Uso libre por la empresa.' + (stars ? ` Esto es lo que gana las ${stars.toLocaleString('es-CL')} Stars.` : ''),
    optOutAll: 'Todo lo anterior',
    optOutReason: '¿Por qué? (opcional; lo ven los administradores de la sala de fotos)',
    optOutLicenceStars: (n) => `Fuera del uso de la empresa, esta foto no gana las ${n.toLocaleString('es-CL')} Stars.`,
    optOutDone: 'Listo',
    detailLabel: { camera: 'Datos de la cámara', location: 'Ubicación (GPS)' },
    detailHint: {
      camera: 'La cámara y cuándo se tomó la foto. Solo los ven los administradores de la sala de fotos.',
      location: 'Dónde se tomó. Solo la ven los administradores de la sala de fotos, y solo en fotos con licencia.',
    },
    detailsNotHeld: 'No se guarda ninguno para esta foto',
    noDetail: { camera: 'sin datos de la cámara', location: 'sin ubicación' },
    removeDetail: { camera: 'Quitar los datos de la cámara de esta foto', location: 'Quitar la ubicación de esta foto' },
    detailRemoved: { camera: 'Se quitaron los datos de la cámara.', location: 'Se quitó la ubicación.' },
    alsoEnter: 'Inscribir también en',
    alsoEnterHint: 'Las fotos que no excluiste del concurso entran en cada uno que marques.',
    enteredIn: (titles) => `Inscrita en ${andList(titles, 'y')}.`,
    notEnteredIn: (title, why) => `No se inscribió en ${title}: ${why}`,
    postIsUp: 'Tu publicación está visible.',
    tagPeople: 'Etiquetar personas',
    tagPeopleFor: (i) => `Etiquetar personas en la foto ${i}`,
    tagSearch: 'Escribe un nombre',
    tagAdd: (name) => `Etiquetar a ${name}`,
    tagRemove: (name) => `Quitar a ${name}`,
    tagNobody: 'No hay nadie con ese nombre.',
    tagLimit: 'Hasta 20 personas por foto.',
    tagNote:
      'Las personas que etiquetes reciben un aviso cuando la publicación esté visible. Pueden quitar su etiqueta y dejar la foto fuera del marketing de la empresa.',
    taggedWith: 'Con',
    youAreTagged: 'Estás etiquetado en esta foto.',
    removeMyTag: 'Quitar mi etiqueta',
    myTagRemoved: 'Se quitó tu etiqueta.',
    myReviewNote: '¿No quieres que la empresa use esta foto? Presiona Pedir revisión y elige “Aparezco en esta foto”. Puedes hacerlo durante 30 días desde que te etiquetaron. Quitar tu etiqueta por sí solo no detiene el uso de la empresa.',
    tagFailed: 'No se guardó. Inténtalo de nuevo.',
    tagHint: 'Toca a alguien en la foto para etiquetarlo.',
    tagPlaceHint: (name) => `Toca dónde está ${name} en la foto.`,
    tagWho: '¿Quién es?',
    tagInPhoto: (n) => `En esta foto (${n})`,
    tagNoneYet: 'Todavía no hay nadie etiquetado.',
    tagNotPlaced: 'Sin marcar en la foto',
    tagPlace: 'Marcar',
    tagMove: 'Mover',
    tagAddByName: 'O agrega a alguien por nombre',
    tagPin: (name) => `${name}. Arrastra o usa las flechas para mover. Supr la quita.`,
    tagDone: 'Listo',
    tagEdit: 'Editar etiquetas',
    tagMore: (n) => `+${n} más`,
    tagPhotoLoading: 'Cargando la foto…',
    showTags: (n) => (n === 1 ? 'Mostrar quién está en esta foto (1 persona)' : `Mostrar quién está en esta foto (${n} personas)`),
    hideTags: 'Ocultar etiquetas',
    photoCard: (i, n) => `Foto ${i} de ${n}`,
    dropPhotos: 'Suelta las fotos para agregarlas',
    dragHint: 'o arrástralas aquí',
    suggestTag: 'Sugerir una etiqueta',
    suggestTitle: 'Sugiere quién está en esta foto',
    suggestHint: 'Toca a alguien en la foto y elige su nombre. Quien la publicó decide; nadie más recibe aviso hasta entonces.',
    thatsMe: 'Soy yo',
    thatsMeHere: 'Soy yo, aquí',
    suggestByName: 'O sugiere a alguien sin marcarlo',
    suggestResult: (outcome, name) => {
      switch (outcome) {
        case 'suggested': return name ? `Sugeriste a ${name}. Quien publicó la foto decide.` : 'Te sugeriste. Quien publicó la foto decide.'
        case 'already_tagged': return name ? `${name} ya está etiquetado.` : 'Ya estás etiquetado.'
        case 'already_suggested': return name ? `Ya se sugirió a ${name}.` : 'Ya se te sugirió.'
        case 'removed_themselves': return name ? `${name} quitó su propia etiqueta de esta foto, así que no se le puede volver a sugerir.` : 'Quitaste tu propia etiqueta de esta foto, así que no se te puede volver a sugerir.'
        case 'cannot_see': return name ? `${name} no puede ver esta publicación, así que no se le puede etiquetar.` : 'No puedes ver esta publicación.'
        default: return name ? `No se puede etiquetar a ${name} en esta foto.` : 'No se te puede etiquetar en esta foto.'
      }
    },
    suggestionsWaiting: (n) => (n === 1 ? '1 etiqueta sugerida espera' : `${n} etiquetas sugeridas esperan`),
    suggestedBy: (name) => `sugerido por ${name}`,
    yourSuggestion: (name) => `Sugeriste a ${name}. Espera la decisión de quien la publicó.`,
    acceptSuggestion: (name) => `Aceptar a ${name}`,
    declineSuggestion: (name) => `Rechazar a ${name}`,
    accept: 'Aceptar',
    decline: 'Rechazar',
    withdraw: 'Retirar',
    suggestionClosed: 'Esa sugerencia ya no se podía aceptar, así que se cerró.',
    suggestedPin: (name) => `¿${name}?`,
    needsSomething: 'Una publicación necesita texto o una foto. Agrega uno, o elimina la publicación desde tus publicaciones.',
    photo: 'Foto',
    photoOf: (i, n) => `Foto ${i} de ${n}`,
    openPhoto: (i, n) => (n > 1 ? `Abrir la foto ${i} de ${n}` : 'Abrir la foto'),
    previousPhoto: 'Foto anterior',
    nextPhoto: 'Foto siguiente',
    earnStars: (n) => `+${n.toLocaleString('es-CL')} Stars`,
    contestStars: (n, cap) =>
      `Cada foto que inscribas gana ${n.toLocaleString('es-CL')} Stars cuando tu publicación sale, hasta ${cap.toLocaleString('es-CL')} al mes.`,
    noOneByThatName: 'No hay nadie con ese nombre.',
    showingMatches: (shown, total) =>
      `Se muestran ${shown} de ${total} coincidencias: sigue escribiendo para acotar la lista.`,
    showingPeople: (shown, total) =>
      `Se muestran ${shown} de ${total} personas: escribe un nombre para acotar la lista.`,
    showingWords: (shown, total) =>
      `Se muestran ${shown} de ${total}: escribe para acotar la lista.`,
  },
}

/* ── A THREAD: comments on a post, replies on Wiki One ─────────────────────── */

/**
 * Two surfaces share one component (Thread.tsx), and in English they differed
 * only by a noun. Spanish cannot do that — "respuesta" is feminine and
 * "comentario" masculine, so "This {noun} was retracted" has no single Spanish
 * shape — so each surface carries its sentences WHOLE. The English column
 * spells out what the noun used to build, and fixes "Loading replys…", which
 * `${noun}s` had been rendering on every Wiki One thread.
 */
export type ThreadSurfaceKey = 'wiki' | 'post'

export type ThreadSurfaceCopy = {
  submit: string
  empty: string
  placeholder: string
  loading: string
  retracted: string
  confirmRetract: string
}

export type ThreadCopy = {
  surfaces: Record<ThreadSurfaceKey, ThreadSurfaceCopy>
  someone: string
  official: string
  stale: string
  edited: string
  notChecked: string
  flagged: string
  hiddenByModerator: string
  reply: string
  markOfficial: string
  unmarkOfficial: string
  hide: string
  unhide: string
  retract: string
  /** The ⋯ on a reply that holds Hide, Retract, Mark official (12 Oct). */
  moreActions: string
  replyPlaceholder: string
  postReply: string
  showFewer: string
  showMore: (n: number) => string
}

export const THREAD: Record<HubLang, ThreadCopy> = {
  en: {
    surfaces: {
      wiki: {
        submit: 'Reply',
        empty: 'No replies yet.',
        placeholder: 'Add a reply…',
        loading: 'Loading replies…',
        retracted: 'This reply was retracted.',
        confirmRetract: 'Retract this reply? This cannot be undone.',
      },
      post: {
        submit: 'Comment',
        empty: 'No comments yet.',
        placeholder: 'Add a comment…',
        loading: 'Loading comments…',
        retracted: 'This comment was retracted.',
        confirmRetract: 'Retract this comment? This cannot be undone.',
      },
    },
    someone: 'Someone',
    official: '★ Official',
    stale: 'not reviewed in the last year',
    edited: 'edited',
    notChecked: 'not checked',
    flagged: 'flagged',
    hiddenByModerator: 'Hidden by a moderator.',
    reply: 'Reply',
    markOfficial: 'Mark official',
    unmarkOfficial: 'Unmark official',
    hide: 'Hide',
    unhide: 'Unhide',
    retract: 'Retract',
    moreActions: 'More actions',
    replyPlaceholder: 'Reply…',
    postReply: 'Post reply',
    showFewer: 'Show fewer replies',
    showMore: (n) => `Show ${n} more ${n === 1 ? 'reply' : 'replies'}`,
  },
  es: {
    surfaces: {
      wiki: {
        submit: 'Responder',
        empty: 'Todavía no hay respuestas.',
        placeholder: 'Agregar una respuesta…',
        loading: 'Cargando respuestas…',
        retracted: 'Esta respuesta fue retirada.',
        confirmRetract: '¿Retirar esta respuesta? No se puede deshacer.',
      },
      post: {
        submit: 'Comentar',
        empty: 'Todavía no hay comentarios.',
        placeholder: 'Agregar un comentario…',
        loading: 'Cargando comentarios…',
        retracted: 'Este comentario fue retirado.',
        confirmRetract: '¿Retirar este comentario? No se puede deshacer.',
      },
    },
    someone: 'Alguien',
    official: '★ Oficial',
    stale: 'sin revisar en el último año',
    edited: 'editado',
    notChecked: 'sin revisar',
    flagged: 'marcado',
    hiddenByModerator: 'Oculto por un moderador.',
    reply: 'Responder',
    markOfficial: 'Marcar como oficial',
    unmarkOfficial: 'Quitar oficial',
    hide: 'Ocultar',
    unhide: 'Mostrar',
    retract: 'Retirar',
    moreActions: 'Más acciones',
    replyPlaceholder: 'Responder…',
    postReply: 'Publicar respuesta',
    showFewer: 'Mostrar menos respuestas',
    showMore: (n) => `Mostrar ${n} ${n === 1 ? 'respuesta más' : 'respuestas más'}`,
  },
}

/* ── WIKI ONE: the reader's view of #/wiki, and the rail card on the hub ───── */

/**
 * CURATOR MODE IS LEFT ENGLISH, DELIBERATELY, like the admin screens: the
 * filter bar, the question queue, the signals card, "at a glance", the
 * Publication and Official row toggles and the check's temperature. Those are
 * the six people who hold `wiki.curate` or `content.publish`, and a Spanish
 * curator screen nobody has read is worse than an English one. What IS here is
 * everything a reader sees with curator mode off (098: off IS the employee
 * view) — plus the two controls an OWNER sees on their own row, Delete/Remove
 * and "Still true", because an employee owns the questions they asked.
 *
 * The questions and answers themselves are content (resolve_variant), never
 * this file. "the board" is "el foro" throughout, the word ASK already uses.
 */
export type WikiCopy = {
  postHint: string
  searchLabel: string
  searchPlaceholder: string
  postToBoard: string
  searchFaq: string
  backToBoard: string
  sortLabel: string
  sorts: Record<'best' | 'trending' | 'new' | 'mine', string>
  relatedQuestions: string
  matchesInWiki: string
  /** 125 (HLP-23). The heading over the one item a bell row opened. */
  fromNotification: string
  loading: string
  nothingClose: string
  nothingMatchesWording: string
  nothingMatches: string
  /** The board filled WIKI_LIST_LIMIT rows — say it is a page (REC-54). */
  listCapped: (n: number) => string
  // A row
  official: string
  officialTitle: string
  question: string
  unpublished: string
  askedBy: (name: string) => string
  asked: string
  ownedBy: (name: string) => string
  unowned: string
  replies: (n: number) => string
  shownIn: (locale: string) => string
  stale: string
  hideReplies: string
  repliesCount: (n: number) => string
  reply: string
  deleteTitle: string
  removeTitle: string
  confirmDelete: string
  delete: string
  remove: string
  // "Still true", the owner's review stamp
  confirmedOn: (date: string) => string
  neverConfirmed: string
  stillTrueTitle: string
  stillTrue: string
  // 187 (WIK-28): a curator changes an answer's words, and readers see it did
  edit: string
  editTitle: string
  editHeading: string
  editQuestion: string
  editAnswer: string
  editNote: string
  editSave: string
  editSaving: string
  editCancel: string
  editLoading: string
  editLoadFailed: string
  editUnchanged: string
  updatedOn: (date: string) => string
  // The vote
  voteLabel: (iVoted: boolean, votes: number) => string
  // The rail card on the hub
  recent: (n: number) => string
  openWiki: string
  noAnswers: string
  answeredBy: (name: string) => string
  answered: string
  reviewed: (when: string) => string
  cardNote: string
}

export const WIKI: Record<HubLang, WikiCopy> = {
  en: {
    postHint:
      'Ask One first — your question may already be answered. Then you can post it to the board.',
    searchLabel: 'Ask a question or search Wiki One',
    searchPlaceholder: 'Ask anything — trucks, logos, benefits…',
    postToBoard: 'Post to the board',
    searchFaq: 'Search FAQ',
    backToBoard: 'Back to the board',
    sortLabel: 'Sort the board',
    sorts: { best: 'Best', trending: 'Trending', new: 'Newest', mine: 'Yours' },
    relatedQuestions: 'Related questions',
    matchesInWiki: 'Matches in Wiki One',
    fromNotification: 'From your notification',
    loading: 'Loading…',
    nothingClose: 'Nothing in Wiki One is close to that yet.',
    nothingMatchesWording:
      'Nothing matches that wording. Ask One — it reads every answer, not just the words you typed.',
    nothingMatches: 'Nothing matches that yet.',
    listCapped: (n) => `Showing the first ${n}. Search to find the rest.`,
    official: '★ Official',
    officialTitle: 'Official',
    question: 'Question',
    unpublished: 'Unpublished',
    askedBy: (name) => `Asked by ${name}`,
    asked: 'Asked',
    ownedBy: (name) => `Owned by ${name}`,
    unowned: 'Unowned',
    replies: (n) => `${n} replies`,
    shownIn: (locale) => `shown in ${locale}`,
    stale: 'not reviewed in the last year',
    hideReplies: 'Hide replies',
    repliesCount: (n) => `Replies (${n})`,
    reply: 'Reply',
    deleteTitle: 'Nobody has seen this. It will be deleted for good.',
    removeTitle: 'This went out, so it is kept as a record and hidden from every surface.',
    confirmDelete: 'Nobody has seen this, so it will be deleted for good. Continue?',
    delete: 'Delete',
    remove: 'Remove',
    confirmedOn: (date) => `Confirmed ${date}`,
    neverConfirmed: 'Never confirmed',
    stillTrueTitle: 'Record that you have read this and it is still correct today',
    stillTrue: 'Still true',
    edit: 'Edit',
    editTitle: 'Change the words of this answer',
    editHeading: 'Edit this answer',
    editQuestion: 'Question',
    editAnswer: 'Answer',
    editNote: 'Saving changes what everyone reads straight away, and Ask One answers from the new words. The answer is marked as checked today, readers see the date it was updated, and One translates it again.',
    editSave: 'Save changes',
    editSaving: 'Saving…',
    editCancel: 'Cancel',
    editLoading: 'Loading the answer…',
    editLoadFailed: 'The answer could not be loaded. Close this and try again.',
    editUnchanged: 'Nothing has changed yet.',
    updatedOn: (date) => `Updated ${date}`,
    voteLabel: (iVoted, votes) =>
      `${iVoted ? 'Remove your upvote' : 'Upvote'} — ${votes} ${votes === 1 ? 'vote' : 'votes'}`,
    recent: (n) => `${n} recent`,
    openWiki: 'Open Wiki One',
    noAnswers: 'No answers published yet.',
    answeredBy: (name) => `Answered by ${name}`,
    answered: 'Answered',
    reviewed: (when) => `reviewed ${when}`,
    cardNote:
      'Answers are official and carry the name of whoever owns them. The published answer never shows who asked.',
  },
  es: {
    postHint:
      'Primero pregúntale a One: puede que tu pregunta ya tenga respuesta. Después puedes publicarla en el foro.',
    searchLabel: 'Haz una pregunta o busca en Wiki One',
    searchPlaceholder: 'Pregunta lo que quieras: camionetas, logos, beneficios…',
    postToBoard: 'Publicar en el foro',
    searchFaq: 'Buscar en preguntas frecuentes',
    backToBoard: 'Volver al foro',
    sortLabel: 'Ordenar el foro',
    sorts: { best: 'Mejores', trending: 'En tendencia', new: 'Más nuevas', mine: 'Tuyas' },
    relatedQuestions: 'Preguntas relacionadas',
    matchesInWiki: 'Coincidencias en Wiki One',
    fromNotification: 'Desde tu notificación',
    loading: 'Cargando…',
    nothingClose: 'Todavía no hay nada en Wiki One parecido a eso.',
    nothingMatchesWording:
      'Nada coincide con esas palabras. Pregúntale a One: lee cada respuesta, no solo las palabras que escribiste.',
    nothingMatches: 'Todavía no hay coincidencias.',
    listCapped: (n) => `Se muestran los primeros ${n}. Busca para encontrar el resto.`,
    official: '★ Oficial',
    officialTitle: 'Oficial',
    question: 'Pregunta',
    unpublished: 'Sin publicar',
    askedBy: (name) => `Pregunta de ${name}`,
    asked: 'Preguntada',
    ownedBy: (name) => `A cargo de ${name}`,
    unowned: 'Sin responsable',
    replies: (n) => `${n} ${n === 1 ? 'respuesta' : 'respuestas'}`,
    shownIn: (locale) => `solo en ${LANGUAGE_NAMES.es[locale] ?? locale}`,
    stale: 'sin revisar en el último año',
    hideReplies: 'Ocultar respuestas',
    repliesCount: (n) => `Respuestas (${n})`,
    reply: 'Responder',
    deleteTitle: 'Nadie lo ha visto. Se eliminará para siempre.',
    removeTitle: 'Esto ya salió, así que se conserva como registro y se oculta en todas partes.',
    confirmDelete: 'Nadie lo ha visto, así que se eliminará para siempre. ¿Continuar?',
    delete: 'Eliminar',
    remove: 'Quitar',
    confirmedOn: (date) => `Confirmado el ${date}`,
    neverConfirmed: 'Nunca confirmado',
    stillTrueTitle: 'Deja constancia de que lo leíste y sigue siendo correcto hoy',
    stillTrue: 'Sigue vigente',
    edit: 'Editar',
    editTitle: 'Cambiar el texto de esta respuesta',
    editHeading: 'Editar esta respuesta',
    editQuestion: 'Pregunta',
    editAnswer: 'Respuesta',
    editNote: 'Al guardar, cambia de inmediato lo que todos leen, y Ask One responde a partir del nuevo texto. La respuesta queda marcada como revisada hoy, los lectores ven la fecha en que se actualizó y One la vuelve a traducir.',
    editSave: 'Guardar cambios',
    editSaving: 'Guardando…',
    editCancel: 'Cancelar',
    editLoading: 'Cargando la respuesta…',
    editLoadFailed: 'No se pudo cargar la respuesta. Cierra esto y vuelve a intentarlo.',
    editUnchanged: 'Todavía no has cambiado nada.',
    updatedOn: (date) => `Actualizada el ${date}`,
    voteLabel: (iVoted, votes) =>
      `${iVoted ? 'Quitar tu voto' : 'Votar a favor'}: ${votes} ${votes === 1 ? 'voto' : 'votos'}`,
    recent: (n) => `${n} recientes`,
    openWiki: 'Abrir Wiki One',
    noAnswers: 'Todavía no hay respuestas publicadas.',
    answeredBy: (name) => `Respondida por ${name}`,
    answered: 'Respondida',
    reviewed: (when) => `revisada ${when}`,
    cardNote:
      'Las respuestas son oficiales y llevan el nombre de quien está a cargo. La respuesta publicada nunca muestra quién preguntó.',
  },
}

/* ── STAR ONE: the wall, giving, a recognition, your balances, the history ── */

/**
 * "Stars" stays the word in Spanish, capitalised, as HOME already has it
 * ("Stars que diste") — it is the currency's name inside a brand, Star One,
 * and "estrellas" would make the balance and the brand two different things.
 * The one exception is the wall's heading, which is a sentence about a person
 * rather than a currency.
 *
 * The Star One ADMIN tab (earning rules, the economy) is left English — one
 * holder of `stars.admin`, the admin screens' reason.
 */
export type StarsCopy = {
  sections: string
  wall: string
  store: string
  youreAStar: string
  loadingWall: string
  nobodyYet: string
  recognizeColleague: string
  // Balances
  yourStars: string
  toGive: (days: number) => string
  earnedToSpend: string
  onTheWay: (n: string) => string
  // Giving
  whatFor: string
  // Endorsements (128): pick a preset or type your own, up to three.
  endorseHint: string
  endorsePlaceholder: string
  endorseAdd: (word: string) => string
  endorseOfficial: string
  endorseUses: (n: number) => string
  endorseMax: string
  endorseRemove: (word: string) => string
  endorseNothing: string
  /** 336: the panel's foot when more matched than it shows, and the modal it opens. */
  endorseShowAll: (n: number) => string
  endorseChosen: (n: number, max: number) => string
  endorseDone: string
  inYourWords: string
  bodyPlaceholder: string
  addStars: string
  optional: string
  none: string
  other: string
  howMany: string
  upTo: (n: string) => string
  overAllowance: (n: string) => string
  thanks: (name: string) => string
  sending: string
  give: string
  withYourName: string
  who: string
  removePerson: (name: string) => string
  searchColleague: string
  /** 335: the person picker matches name, unit and title, so "by that name" is not the whole truth. */
  noOneMatches: string
  // A recognition on the wall
  recognizedJoin: string
  editableFor: (mins: number) => string
  confirmTakeDownStars: (name: string, stars: number) => string
  confirmTakeDown: string
  edit: string
  takingDown: string
  takeDown: string
  editingRecognition: string
  saving: string
  save: string
  cancel: string
  today: string
  yesterday: string
  daysAgo: (n: number) => string
  weeksAgo: (n: number) => string
  // The chart (106 — every credit, not only recognitions)
  historyNothing: (period: string) => string
  historyAmount: (period: string, stars: number) => string
  weekOf: (date: string) => string
  rangeTotal: Record<'1m' | '3m' | '12m' | 'all', string>
  rangeName: Record<'1m' | '3m' | '12m' | 'all', string>
  rangePicker: string
  nothingInRange: string
  // The earnings list (106)
  noneEarnedYet: string
  recognizedYou: (name: string) => string
  someoneRecognizedYou: string
  activity: Record<string, string>
  activityOther: string
  takenBack: (n: number) => string
  earnedHeading: string
  seeAllEarned: string
  earnedDialog: string
  everythingEarned: string
  shownOf: (shown: number, total: number) => string
  showMore: string
  loadingEarned: string
  couldNotLoadEarned: string
  // The pill (106, priority 2)
  pulse: (n: number) => string
  pulseSr: (n: number) => string
}

export const STARS: Record<HubLang, StarsCopy> = {
  en: {
    sections: 'Star One sections',
    wall: 'Wall',
    store: 'Store',
    youreAStar: 'You’re a Star',
    loadingWall: 'Loading the wall…',
    nobodyYet: 'Nobody has been recognized yet. You could be first.',
    recognizeColleague: 'Recognize a colleague',
    yourStars: 'Your stars',
    toGive: (days) => `to give — resets in ${days} ${days === 1 ? 'day' : 'days'}`,
    earnedToSpend: 'earned — yours to spend in the shop',
    onTheWay: (n) => `★ ${n} on the way — lands once the giver's edit window shuts`,
    whatFor: 'What for',
    endorseHint: 'Pick one, or type your own — up to three.',
    endorsePlaceholder: 'Type to find one, or add your own',
    endorseAdd: (w) => `Add “${w}”`,
    endorseOfficial: 'Official',
    endorseUses: (n) => (n === 1 ? 'used once' : `used ${n.toLocaleString()} times`),
    endorseMax: 'Three is the most one recognition can carry.',
    endorseRemove: (w) => `Remove ${w}`,
    endorseNothing: 'Nothing like that yet — add it as your own.',
    endorseShowAll: (n) => `Show all ${n.toLocaleString()}`,
    endorseChosen: (n, max) => `${n} of ${max} chosen`,
    endorseDone: 'Done',
    inYourWords: 'In your words',
    bodyPlaceholder: 'What did they do, and why did it matter?',
    addStars: 'Add stars',
    optional: 'optional',
    none: 'None',
    other: 'Other',
    howMany: 'How many',
    upTo: (n) => `up to ★ ${n} left to give this month`,
    overAllowance: (n) => `That is more than the ${n} you have left this month.`,
    thanks: (name) => `Thank you — ${name} will see it on the wall.`,
    sending: 'Sending…',
    give: 'Give recognition',
    withYourName: 'Goes on the wall with your name on it.',
    who: 'Who',
    removePerson: (name) => `Remove ${name}`,
    searchColleague: 'Search by name, unit or job title',
    noOneMatches: 'No one matches that.',
    recognizedJoin: ' recognized ',
    editableFor: (mins) => `Editable for ${mins} mins`,
    confirmTakeDownStars: (name, stars) =>
      `Take this down? ${name} will not see it, and your ${stars} stars come back.`,
    confirmTakeDown: 'Take this down? It will disappear from the wall.',
    edit: 'Edit',
    takingDown: 'Taking down…',
    takeDown: 'Take down',
    editingRecognition: 'Editing your recognition of ',
    saving: 'Saving…',
    save: 'Save',
    cancel: 'Cancel',
    today: 'today',
    yesterday: 'yesterday',
    daysAgo: (n) => `${n} days ago`,
    weeksAgo: (n) => `${n} ${n === 1 ? 'week' : 'weeks'} ago`,
    historyNothing: (period) => `${period}: nothing`,
    historyAmount: (period, stars) =>
      `${period}: ${stars.toLocaleString()} ${stars === 1 ? 'star' : 'stars'}`,
    weekOf: (date) => `Week of ${date}`,
    rangeTotal: {
      '1m': 'in the last 30 days',
      '3m': 'in the last 3 months',
      '12m': 'in the last 12 months',
      all: 'since you joined',
    },
    rangeName: { '1m': '1 mo', '3m': '3 mo', '12m': '12 mo', all: 'All time' },
    rangePicker: 'Show stars over',
    nothingInRange: 'Nothing earned in this period.',
    noneEarnedYet: 'Nothing earned yet. Stars you earn by taking part or being recognized show up here.',
    recognizedYou: (name) => `${name} recognized you`,
    someoneRecognizedYou: 'A colleague recognized you',
    activity: {
      'signin.first': 'Welcome to OneConetec',
      'post.published': 'Your post went live',
      'photo.contest_entry': 'Your photo was entered in the photo contest',
      'photo.used': 'Your photo was downloaded from the photo room to be used',
      'photo.contest_win': 'Your photo won the photo contest',
      'question.published': 'Your question went up on the board',
      'comment.posted': 'You commented',
      'question.answered': 'You answered a question on the board',
      'one_question.answered': 'You answered One Question',
      'support.ticket_thanks': 'Thanks for your support ticket',
      'bis.course_completed': 'BIS Safety course completed',
    },
    activityOther: 'Taking part in OneConetec',
    takenBack: (n) => `${n.toLocaleString()} taken back when it was removed`,
    earnedHeading: 'Recently earned',
    seeAllEarned: 'See all your stars',
    earnedDialog: 'Stars you earned',
    everythingEarned: 'Everything you have earned',
    shownOf: (shown, total) => `Showing ${shown.toLocaleString()} of ${total.toLocaleString()}`,
    showMore: 'Show more',
    loadingEarned: 'Loading your stars…',
    couldNotLoadEarned: 'Your stars could not be loaded just now.',
    pulse: (n) => `+${n.toLocaleString()} ${n === 1 ? 'star' : 'stars'}`,
    pulseSr: (n) => `You just earned ${n.toLocaleString()} ${n === 1 ? 'star' : 'stars'}.`,
  },
  es: {
    sections: 'Secciones de Star One',
    wall: 'Muro',
    store: 'Tienda',
    youreAStar: 'Eres una estrella',
    loadingWall: 'Cargando el muro…',
    nobodyYet: 'Todavía no se ha reconocido a nadie. Puedes empezar tú.',
    recognizeColleague: 'Reconocer a un colega',
    yourStars: 'Tus Stars',
    toGive: (days) => `por dar: se reinician en ${days} ${days === 1 ? 'día' : 'días'}`,
    earnedToSpend: 'ganadas: tuyas para gastar en la tienda',
    onTheWay: (n) =>
      `★ ${n} en camino: llegan cuando se cierra el plazo de edición de quien las dio`,
    whatFor: 'Por qué',
    endorseHint: 'Elige uno o escribe el tuyo, hasta tres.',
    endorsePlaceholder: 'Escribe para buscar o agregar uno',
    endorseAdd: (w) => `Agregar «${w}»`,
    endorseOfficial: 'Oficial',
    endorseUses: (n) => (n === 1 ? 'usado una vez' : `usado ${n.toLocaleString()} veces`),
    endorseMax: 'Un reconocimiento lleva como máximo tres.',
    endorseRemove: (w) => `Quitar ${w}`,
    endorseNothing: 'Todavía no hay nada parecido: agrégalo como tuyo.',
    endorseShowAll: (n) => `Ver las ${n.toLocaleString()}`,
    endorseChosen: (n, max) => `${n} de ${max} elegidos`,
    endorseDone: 'Listo',
    inYourWords: 'Con tus palabras',
    bodyPlaceholder: '¿Qué hizo y por qué importó?',
    addStars: 'Agregar Stars',
    optional: 'opcional',
    none: 'Ninguna',
    other: 'Otra cantidad',
    howMany: 'Cuántas',
    upTo: (n) => `hasta ★ ${n} que te quedan por dar este mes`,
    overAllowance: (n) => `Eso es más que las ${n} que te quedan este mes.`,
    thanks: (name) => `Gracias: ${name} lo verá en el muro.`,
    sending: 'Enviando…',
    give: 'Dar reconocimiento',
    withYourName: 'Va al muro con tu nombre.',
    who: 'A quién',
    removePerson: (name) => `Quitar a ${name}`,
    searchColleague: 'Busca por nombre, unidad o cargo',
    noOneMatches: 'Nadie coincide con eso.',
    recognizedJoin: ' reconoció a ',
    editableFor: (mins) => `Editable durante ${mins} min`,
    confirmTakeDownStars: (name, stars) =>
      `¿Retirarlo? ${name} no lo verá y tus ${stars} Stars vuelven a ti.`,
    confirmTakeDown: '¿Retirarlo? Desaparecerá del muro.',
    edit: 'Editar',
    takingDown: 'Retirando…',
    takeDown: 'Retirar',
    editingRecognition: 'Editando tu reconocimiento a ',
    saving: 'Guardando…',
    save: 'Guardar',
    cancel: 'Cancelar',
    today: 'hoy',
    yesterday: 'ayer',
    daysAgo: (n) => `hace ${n} días`,
    weeksAgo: (n) => `hace ${n} ${n === 1 ? 'semana' : 'semanas'}`,
    historyNothing: (period) => `${period}: nada`,
    historyAmount: (period, stars) =>
      `${period}: ${stars.toLocaleString()} ${stars === 1 ? 'Star' : 'Stars'}`,
    weekOf: (date) => `Semana del ${date}`,
    rangeTotal: {
      '1m': 'en los últimos 30 días',
      '3m': 'en los últimos 3 meses',
      '12m': 'en los últimos 12 meses',
      all: 'desde que te uniste',
    },
    rangeName: { '1m': '1 mes', '3m': '3 meses', '12m': '12 meses', all: 'Todo' },
    rangePicker: 'Ver Stars de',
    nothingInRange: 'No ganaste Stars en este período.',
    noneEarnedYet: 'Todavía no has ganado Stars. Las que ganes al participar o al ser reconocido aparecen aquí.',
    recognizedYou: (name) => `${name} te reconoció`,
    someoneRecognizedYou: 'Un colega te reconoció',
    activity: {
      'signin.first': 'Tu bienvenida a OneConetec',
      'post.published': 'Tu publicación salió en el feed',
      'photo.contest_entry': 'Tu foto entró al concurso de fotos',
      'photo.used': 'Tu foto se descargó de la sala de fotos para usarla',
      'photo.contest_win': 'Tu foto ganó el concurso de fotos',
      'question.published': 'Tu pregunta se publicó en el tablero',
      'comment.posted': 'Comentaste',
      'question.answered': 'Respondiste una pregunta del tablero',
      'one_question.answered': 'Respondiste One Question',
      'support.ticket_thanks': 'Gracias por tu ticket de soporte',
      'bis.course_completed': 'Curso de BIS Safety completado',
    },
    activityOther: 'Participar en OneConetec',
    takenBack: (n) => `${n.toLocaleString()} descontadas cuando se retiró`,
    earnedHeading: 'Ganadas hace poco',
    seeAllEarned: 'Ver todas tus Stars',
    earnedDialog: 'Stars que ganaste',
    everythingEarned: 'Todo lo que has ganado',
    shownOf: (shown, total) => `Se muestran ${shown.toLocaleString()} de ${total.toLocaleString()}`,
    showMore: 'Ver más',
    loadingEarned: 'Cargando tus Stars…',
    couldNotLoadEarned: 'No se pudieron cargar tus Stars en este momento.',
    pulse: (n) => `+${n.toLocaleString()} ${n === 1 ? 'Star' : 'Stars'}`,
    pulseSr: (n) => `Acabas de ganar ${n.toLocaleString()} ${n === 1 ? 'Star' : 'Stars'}.`,
  },
}

/* ── THE SHOP FLOOR AND YOUR ORDERS ────────────────────────────────────────── */

/**
 * A stock room is "bodega" — the word in Chile and Peru for the room a box
 * sits in. Item names, descriptions, axis and option labels are the shop
 * admin's data, typed in one language, and are not here. The shop ADMIN
 * screens are left English.
 */
export type ShopCopy = {
  collectFrom: string
  everywhere: string
  loadingShop: string
  emptyShop: string
  nothingThere: string
  storeLabel: string
  inStock: (n: string, rooms: number) => string
  orderInOnly: string
  outOfStock: string
  notAvailable: string
  reservedAt: (room: string) => string
  waitingListAt: (room: string) => string
  moreNeeded: (n: string) => string
  ordersClose: (date: string) => string
  whereVariant: (label: string) => string
  whereItIs: string
  retired: string
  lastsHead: string
  lastsBody: string
  comboNotSold: string
  noRoom: string
  here: (n: string) => string
  noneHereOrderIn: string
  noneHere: string
  sending: string
  chooseToSee: (axes: number) => string
  // offerFor — the button
  chooseOption: string
  notInAnyRoom: string
  chooseWhere: string
  redeemHere: string
  joinWaitingList: string
  noneThere: string
  // 129: pre-orders and pledges
  opensOn: (date: string) => string
  notOpenYet: string
  preorderUntil: (date: string) => string
  preorderHere: string
  preorderedAt: (room: string) => string
  preordered: string
  pledgeHead: string
  pledgeAgree: string
  pledgeFirst: string
  // blockSentence
  ordersClosed: string
  oneEach: string
  nEach: (max: number, mine: number) => string
  otherPart: string
  noLonger: string
  // Your orders, and orderState
  yourOrders: string
  loadingOrders: string
  cancelling: string
  cancel: string
  readyToCollect: string
  waiting: string
  nextInLine: string
  inLine: (n: number) => string
  collected: string
  cancelled: string
  // REC-49: Pickup with code, and the sheet it opens
  pickupWithCode: string
  pickupTitle: string
  pickupAt: (room: string, city: string) => string
  pickupHow: string
  pickupPrivate: string
  pickupCode: string
  pickupValidUntil: (date: string) => string
  pickupQrAlt: (item: string) => string
  pickupQrFailed: string
  pickupDone: string
  pickupGone: string
}

function enOrdinal(n: number): string {
  const rem100 = n % 100
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`
  switch (n % 10) {
    case 1: return `${n}st`
    case 2: return `${n}nd`
    case 3: return `${n}rd`
    default: return `${n}th`
  }
}

export const SHOP: Record<HubLang, ShopCopy> = {
  en: {
    collectFrom: 'Collect from',
    everywhere: 'Everywhere',
    loadingShop: 'Loading the shop…',
    emptyShop: 'Nothing is in the shop yet.',
    nothingThere: 'Nothing can be collected from there. Try Everywhere.',
    storeLabel: 'The store',
    inStock: (n, rooms) => `${n} in stock${rooms > 1 ? ` across ${rooms} rooms` : ''}`,
    orderInOnly: 'None on the shelf — can be ordered in',
    outOfStock: 'Out of stock',
    notAvailable: 'Not available to you',
    reservedAt: (room) => `Reserved for you at ${room}.`,
    waitingListAt: (room) => `On the waiting list at ${room}.`,
    moreNeeded: (n) => `${n} more stars needed`,
    ordersClose: (date) => `Orders close ${date}.`,
    whereVariant: (label) => `Where ${label} is`,
    whereItIs: 'Where it is',
    retired: 'Retired',
    lastsHead: 'Get it while it lasts.',
    lastsBody:
      'This one is not being made any more, so what is on the shelf is all there is. It cannot be ordered in.',
    comboNotSold: 'That combination is not sold.',
    noRoom: 'No stock room holds this one, and none will order it in.',
    here: (n) => `${n} here`,
    noneHereOrderIn: 'none here — can be ordered in',
    noneHere: 'none here',
    sending: 'Sending…',
    chooseToSee: (axes) =>
      `Choose ${axes === 1 ? 'an option' : 'your options'} to see where it is and redeem.`,
    chooseOption: 'Choose an option',
    notInAnyRoom: 'Not in any stock room',
    chooseWhere: 'Choose where to collect',
    redeemHere: 'Redeem from this store',
    joinWaitingList: 'Join the waiting list',
    noneThere: 'None there',
    opensOn: (date) => `Pre-orders open ${date}.`,
    notOpenYet: 'Not open for orders yet.',
    preorderUntil: (date) => `Pre-order until ${date}`,
    preorderHere: 'Pre-order for pickup here',
    preorderedAt: (room) => `Pre-ordered. We will tell you when it is ready at ${room}.`,
    preordered: 'Pre-ordered',
    pledgeHead: 'To order this, you agree that:',
    pledgeAgree: 'I agree',
    pledgeFirst: 'Agree to the statements above first',
    ordersClosed: 'Orders for this have closed.',
    oneEach: 'One each, and you have yours.',
    nEach: (max, mine) => `${max} each, and you have ${mine}.`,
    otherPart: 'This one is offered to a different part of the company.',
    noLonger: 'This is no longer in the shop.',
    yourOrders: 'Your orders',
    loadingOrders: 'Loading your orders…',
    cancelling: 'Cancelling…',
    cancel: 'Cancel',
    readyToCollect: 'Ready to collect',
    waiting: 'Waiting',
    nextInLine: 'Next in line',
    inLine: (n) => `${enOrdinal(n)} in line`,
    collected: 'Collected',
    cancelled: 'Cancelled',
    pickupWithCode: 'Pickup with code',
    pickupTitle: 'Your pickup code',
    pickupAt: (room, city) => `Show this at ${room}, ${city}.`,
    pickupHow: 'Whoever hands it over scans this with their phone camera. They do not need to sign in.',
    pickupPrivate: 'Anyone who scans it can mark your order collected, so only show it at the store room.',
    pickupCode: 'Code',
    pickupValidUntil: (date) => `Valid until ${date}`,
    pickupQrAlt: (item) => `QR code to collect ${item}`,
    pickupQrFailed: 'The QR code did not draw. Close this and open it again.',
    pickupDone: 'Collected. Enjoy it.',
    pickupGone: 'This code is no longer valid. Your orders show what happened.',
  },
  es: {
    collectFrom: 'Retirar en',
    everywhere: 'Todas',
    loadingShop: 'Cargando la tienda…',
    emptyShop: 'Todavía no hay nada en la tienda.',
    nothingThere: 'No hay nada para retirar ahí. Prueba con «Todas».',
    storeLabel: 'La tienda',
    inStock: (n, rooms) => `${n} disponibles${rooms > 1 ? ` en ${rooms} bodegas` : ''}`,
    orderInOnly: 'No hay en bodega: se puede pedir',
    outOfStock: 'Agotado',
    notAvailable: 'No disponible para ti',
    reservedAt: (room) => `Reservado para ti en ${room}.`,
    waitingListAt: (room) => `En la lista de espera de ${room}.`,
    moreNeeded: (n) => `Te faltan ${n} Stars`,
    ordersClose: (date) => `Los pedidos cierran el ${date}.`,
    whereVariant: (label) => `Dónde hay ${label}`,
    whereItIs: 'Dónde hay',
    retired: 'Descontinuado',
    lastsHead: 'Hasta agotar stock.',
    lastsBody:
      'Ya no se fabrica, así que lo que hay en bodega es todo lo que queda. No se puede pedir.',
    comboNotSold: 'Esa combinación no está a la venta.',
    noRoom: 'Ninguna bodega lo tiene y ninguna lo va a pedir.',
    here: (n) => `${n} aquí`,
    noneHereOrderIn: 'no hay aquí: se puede pedir',
    noneHere: 'no hay aquí',
    sending: 'Enviando…',
    chooseToSee: (axes) =>
      `Elige ${axes === 1 ? 'una opción' : 'tus opciones'} para ver dónde hay y canjear.`,
    chooseOption: 'Elige una opción',
    notInAnyRoom: 'No está en ninguna bodega',
    chooseWhere: 'Elige dónde retirar',
    redeemHere: 'Canjear en esta bodega',
    joinWaitingList: 'Unirme a la lista de espera',
    noneThere: 'No hay ahí',
    opensOn: (date) => `La preventa abre el ${date}.`,
    notOpenYet: 'Todavía no se puede pedir.',
    preorderUntil: (date) => `Preventa hasta el ${date}`,
    preorderHere: 'Reservar para retirar aquí',
    preorderedAt: (room) => `Reservado. Te avisaremos cuando esté listo en ${room}.`,
    preordered: 'Reservado en preventa',
    pledgeHead: 'Para pedirlo, aceptas que:',
    pledgeAgree: 'Acepto',
    pledgeFirst: 'Primero acepta las condiciones de arriba',
    ordersClosed: 'Los pedidos de esto ya cerraron.',
    oneEach: 'Uno por persona, y ya tienes el tuyo.',
    nEach: (max, mine) => `${max} por persona, y ya tienes ${mine}.`,
    otherPart: 'Esto se ofrece a otra parte de la empresa.',
    noLonger: 'Ya no está en la tienda.',
    yourOrders: 'Tus pedidos',
    loadingOrders: 'Cargando tus pedidos…',
    cancelling: 'Cancelando…',
    cancel: 'Cancelar',
    readyToCollect: 'Listo para retirar',
    waiting: 'En espera',
    nextInLine: 'Siguiente en la fila',
    inLine: (n) => `${n}.º en la fila`,
    collected: 'Retirado',
    cancelled: 'Cancelado',
    pickupWithCode: 'Retirar con código',
    pickupTitle: 'Tu código de retiro',
    pickupAt: (room, city) => `Muéstralo en ${room}, ${city}.`,
    pickupHow: 'Quien te lo entrega lo escanea con la cámara de su teléfono. No necesita iniciar sesión.',
    pickupPrivate: 'Cualquiera que lo escanee puede marcar tu pedido como retirado, así que muéstralo solo en la bodega.',
    pickupCode: 'Código',
    pickupValidUntil: (date) => `Válido hasta el ${date}`,
    pickupQrAlt: (item) => `Código QR para retirar ${item}`,
    pickupQrFailed: 'No se pudo dibujar el código QR. Cierra esto y vuelve a abrirlo.',
    pickupDone: 'Retirado. ¡Que lo disfrutes!',
    pickupGone: 'Este código ya no es válido. Tus pedidos muestran qué pasó.',
  },
}

/* ── YOUR PROFILE: what the company holds, your phone, signature and card ──── */

/**
 * The SIGNATURE ITSELF is not translated, and that is not an oversight: it is
 * Marketing's approved template (docs/email-signature-template.md), rendered
 * from legal and roster facts, and a Spanish signature is their call. What is
 * here is the screen around it. "Outlook under Settings → Mail → Compose and
 * reply" uses the names Outlook's own Spanish menus carry.
 */
export type ProfileCopy = {
  eyebrow: string
  // 11 Oct — the profile reworked: language and reading, card details, header
  languageHead: string
  languageFailed: string
  readHead: string
  readLede: string
  readOption: (language: string) => string
  readSaved: string
  readFailed: string
  cardDetailsNone: string
  photoOnlyOnCard: string
  // 143 (FED-49) — photos you are tagged in
  photosHead: string
  photosLede: string
  photosTaggedBy: (name: string) => string
  photosTagRemoved: string
  photosOpen: string
  // 170 (David, 11 Oct): no box per photo — a button, a list, and the post.
  photosCount: (n: number) => string
  photosSee: string
  photosDialogTitle: string
  photosHowTo: string
  photosConsentUntil: (date: string) => string
  photosConsentPassed: string
  photosAsked: string
  photosAskedNoConsent: string
  photosOwn: string
  whatWeHold: string
  name: string
  jobTitle: string
  legalEntity: string
  location: string
  workEmail: string
  factsNote: string
  phoneHead: string
  phoneLede: string
  phoneLabel: string
  saving: string
  save: string
  remove: string
  // The card itself is a modal since 4 Oct (BusinessCard.tsx, copy in CARD);
  // #/profile only carries the way to open it.
  cardHead: string
  cardLede: string
  noAddressHead: string
  noAddressBody: string
  personalAddressHead: string
  personalAddressBody: string
  failedHead: string
  failedBody: string
  // While the generator is held (lib/signature.ts SIGNATURE_RELEASED)
  // The signature card
  signatureHead: string
  signatureLede: string
  copySignature: string
  copied: string
  selectIt: string
  logoNote: string
}

export const PROFILE: Record<HubLang, ProfileCopy> = {
  en: {
    eyebrow: 'You',
    languageHead: 'Language and translation',
    languageFailed: 'Your language could not be changed. Try again in a moment.',
    readHead: 'Languages you read as written',
    readLede:
      'One translates posts, Wiki One answers and private questions into your language. Tick a language you read, and anything written in it is shown exactly as written instead. Everything else is still translated.',
    readOption: (language) => `${language} — show as written`,
    readSaved: 'Saved',
    readFailed: 'That did not save. Try again in a moment.',
    cardDetailsNone: 'Not added',
    photoOnlyOnCard: 'Your photo appears only on your own card and in the contact you share.',
    photosHead: 'Photos you are tagged in',
    photosLede:
      'Photos colleagues tagged you in. Open one to remove your tag, or to ask for a review — including asking the company not to use a photo you are in.',
    photosTaggedBy: (name) => `Tagged by ${name}`,
    photosTagRemoved: 'You removed your tag',
    photosOpen: 'Open the post',
    photosCount: (n) => (n === 1 ? '1 photo' : `${n} photos`),
    photosSee: 'See the photos',
    photosDialogTitle: 'Photos you are tagged in',
    photosHowTo:
      'Open a post to remove your tag or to request a review. If you don’t want the company to use a photo you are in, choose “I’m in this photo” in the review — you can do that for 30 days after the photo is posted or you are tagged, whichever is later.',
    photosConsentUntil: (date) => `You can ask the company not to use it until ${date}.`,
    photosConsentPassed: 'The 30 days to ask the company not to use it have passed. You can still request a review for another reason.',
    photosAsked: 'You asked for a review.',
    photosAskedNoConsent: 'You asked the company not to use it.',
    photosOwn: 'Your own photo — you decide its uses on the post.',
    whatWeHold: 'What the company holds',
    name: 'Name',
    jobTitle: 'Job title',
    legalEntity: 'Legal entity',
    location: 'Location',
    workEmail: 'Work email',
    factsNote:
      'These come from BambooHR and are refreshed here every night, so a change made on this screen would not survive. If something is wrong, ask HR to correct it in BambooHR and it will be right here the next morning.',
    phoneHead: 'Your published phone number',
    phoneLede:
      'Optional, and yours to set. It appears on your signature and your business card and nowhere else. BambooHR neither supplies it nor overwrites it.',
    phoneLabel: 'Phone number',
    saving: 'Saving…',
    save: 'Save',
    remove: 'Remove',
    cardHead: 'Your digital business card',
    cardLede:
      'Show it to anyone you meet: they scan the code and your details save straight to their phone. Your mobile, office phone and office address are optional, and appear only on your card. The card is also in the menu under your initials.',
    noAddressHead: 'We have no work address for you',
    noAddressBody:
      'A signature has to state an address people can reach you at, and there is none on file. Ask your manager to have your company address added — once it is there this screen will work without anything else changing.',
    personalAddressHead: 'Your address is not a company address',
    personalAddressBody:
      'Signatures and business cards are generated only for company addresses, because they are a statement about who you work for. Ask your manager about a company mailbox.',
    failedHead: 'Your signature could not be prepared',
    failedBody:
      'Something went wrong rather than something being missing. Send this screen to the build team.',
    signatureHead: 'Your email signature',
    signatureLede:
      'Copy this, then paste it into Outlook under Settings → Mail → Compose and reply. It is built from what the company holds about you, so if something is wrong here it is wrong everywhere.',
    copySignature: 'Copy signature',
    copied: 'Copied',
    selectIt: 'Select the signature above and copy it',
    logoNote:
      'The logo is loaded from oneconetec.com. Some mail programs hide images until the reader allows them — the signature is written so it still reads correctly when that happens.',
  },
  es: {
    eyebrow: 'Tú',
    languageHead: 'Idioma y traducción',
    languageFailed: 'No se pudo cambiar tu idioma. Inténtalo de nuevo en un momento.',
    readHead: 'Idiomas que lees tal como se escribieron',
    readLede:
      'One traduce las publicaciones, las respuestas de Wiki One y las preguntas privadas a tu idioma. Marca un idioma que lees y todo lo escrito en él se mostrará tal como se escribió. Todo lo demás se sigue traduciendo.',
    readOption: (language) => `${language}: mostrar tal como se escribió`,
    readSaved: 'Guardado',
    readFailed: 'No se guardó. Inténtalo de nuevo en un momento.',
    cardDetailsNone: 'Sin agregar',
    photoOnlyOnCard: 'Tu foto aparece solo en tu propia tarjeta y en el contacto que compartes.',
    photosHead: 'Fotos en las que te etiquetaron',
    photosLede:
      'Fotos en las que tus colegas te etiquetaron. Abre una para quitar tu etiqueta o pedir una revisión, incluso para pedir que la empresa no use una foto en la que apareces.',
    photosTaggedBy: (name) => `Etiquetado por ${name}`,
    photosTagRemoved: 'Quitaste tu etiqueta',
    photosOpen: 'Abrir la publicación',
    photosCount: (n) => (n === 1 ? '1 foto' : `${n} fotos`),
    photosSee: 'Ver las fotos',
    photosDialogTitle: 'Fotos en las que te etiquetaron',
    photosHowTo:
      'Abre una publicación para quitar tu etiqueta o pedir una revisión. Si no quieres que la empresa use una foto en la que apareces, elige “Aparezco en esta foto” en la revisión: puedes hacerlo durante 30 días desde que se publicó la foto o desde que te etiquetaron, lo que sea más tarde.',
    photosConsentUntil: (date) => `Puedes pedir que la empresa no la use hasta el ${date}.`,
    photosConsentPassed: 'Ya pasaron los 30 días para pedir que la empresa no la use. Igual puedes pedir una revisión por otro motivo.',
    photosAsked: 'Pediste una revisión.',
    photosAskedNoConsent: 'Pediste que la empresa no la use.',
    photosOwn: 'Es tu propia foto: sus usos los decides en la publicación.',
    whatWeHold: 'Lo que la empresa tiene registrado',
    name: 'Nombre',
    jobTitle: 'Cargo',
    legalEntity: 'Entidad legal',
    location: 'Ubicación',
    workEmail: 'Correo de trabajo',
    factsNote:
      'Estos datos vienen de BambooHR y se actualizan aquí cada noche, así que un cambio hecho en esta pantalla no duraría. Si algo está mal, pídele a RR. HH. que lo corrija en BambooHR y a la mañana siguiente estará bien aquí.',
    phoneHead: 'Tu teléfono publicado',
    phoneLede:
      'Es opcional y lo defines tú. Aparece en tu firma y en tu tarjeta de presentación, y en ningún otro lugar. BambooHR no lo entrega ni lo sobrescribe.',
    phoneLabel: 'Número de teléfono',
    saving: 'Guardando…',
    save: 'Guardar',
    remove: 'Quitar',
    cardHead: 'Tu tarjeta de presentación digital',
    cardLede:
      'Muéstrala a quien conozcas: escanea el código y tus datos se guardan directo en su teléfono. Tu celular, tu teléfono de oficina y tu dirección de oficina son opcionales y aparecen solo en tu tarjeta. La tarjeta también está en el menú bajo tus iniciales.',
    noAddressHead: 'No tenemos un correo de trabajo tuyo',
    noAddressBody:
      'Una firma tiene que indicar un correo donde puedan contactarte, y no hay ninguno registrado. Pídele a tu jefatura que agreguen tu correo de la empresa: cuando esté, esta pantalla funcionará sin que cambie nada más.',
    personalAddressHead: 'Tu correo no es de la empresa',
    personalAddressBody:
      'Las firmas y las tarjetas de presentación solo se generan para correos de la empresa, porque dicen para quién trabajas. Consulta con tu jefatura por un buzón de la empresa.',
    failedHead: 'No se pudo preparar tu firma',
    failedBody:
      'Algo falló; no es que falte un dato. Envía esta pantalla al equipo que construye el portal.',
    signatureHead: 'Tu firma de correo',
    signatureLede:
      'Cópiala y pégala en Outlook, en Configuración → Correo → Redactar y responder. Se arma con lo que la empresa tiene registrado sobre ti, así que si algo está mal aquí, está mal en todas partes.',
    copySignature: 'Copiar firma',
    copied: 'Copiada',
    selectIt: 'Selecciona la firma de arriba y cópiala',
    logoNote:
      'El logo se carga desde oneconetec.com. Algunos programas de correo ocultan las imágenes hasta que quien lee lo permite; la firma está escrita para que se lea bien incluso así.',
  },
}

/* ── THE DIGITAL BUSINESS CARD (4 Oct) ─────────────────────────────────────── */

/**
 * BusinessCard.tsx's words. Its own table rather than more PROFILE keys
 * because the card is no longer on #/profile — it is a dialog the shell owns,
 * opened from the hub, the identity menu and #/profile alike.
 *
 * The refusals are ACC-24's two populations again, reworded for a card: the
 * PROFILE ones say "a signature", which is held and so not on screen.
 */
export type CardCopy = {
  dialogTitle: string
  /** The accessible name of the card face. */
  cardLabel: (name: string) => string
  loading: string
  preparingCode: string
  scan: string
  emailLabel: string
  phoneLabel: string
  placeLabel: string
  howItWorks: string
  saveContact: string
  share: string
  /** 6 Oct — the card as a picture, for the camera roll. */
  saveImage: string
  imageFailed: string
  addPhone: string
  changePhone: string
  phoneField: string
  phoneHint: string
  save: string
  saving: string
  cancel: string
  remove: string
  /** 132 — the card photo, set inside the dialog like the phone. */
  addPhoto: string
  changePhoto: string
  removePhoto: string
  photoSaving: string
  photoHint: string
  photoNotImage: string
  photoFailed: string
  /** 134 — the edit-card dialog (David, 6 Oct). */
  editCard: string
  editTitle: string
  detailsHint: string
  photoField: string
  mobileField: string
  officePhoneField: string
  officeAddressHead: string
  /** Shown under the address heading; `fallback` is what the card says today. */
  addressHint: (fallback: string) => string
  streetField: string
  cityField: string
  regionField: string
  postcodeField: string
  countryField: string
  mobileLabel: string
  officeLabel: string
  phoneInvalid: string
  addressIncomplete: string
  detailsFailed: string
  /** The crop-and-zoom dialog (6 Oct). */
  cropTitle: string
  cropFrameLabel: string
  cropHelp: string
  zoomLabel: string
  usePhoto: string
  /** The hub rail card and #/profile's button. */
  widgetTitle: string
  widgetLede: string
  show: string
  noAddressHead: string
  noAddressBody: string
  personalAddressHead: string
  personalAddressBody: string
  failedHead: string
  failedBody: string
}

export const CARD: Record<HubLang, CardCopy> = {
  en: {
    dialogTitle: 'Your digital business card',
    cardLabel: (name) => `${name}, Conetec business card`,
    loading: 'Getting your card…',
    preparingCode: 'Preparing the code…',
    scan: 'Scan to save contact',
    emailLabel: 'Email',
    phoneLabel: 'Phone',
    placeLabel: 'Location',
    howItWorks:
      'The code is the contact card itself. Nothing is looked up when somebody scans it, so it works on a site with no signal.',
    saveContact: 'Download contact',
    share: 'Share',
    saveImage: 'Save as image',
    imageFailed: 'The picture of your card could not be made. Try again in a moment.',
    addPhone: 'Add a phone number',
    changePhone: 'Change phone number',
    phoneField: 'Phone number',
    phoneHint: 'Optional, and yours to set. BambooHR neither supplies it nor overwrites it.',
    save: 'Save',
    saving: 'Saving…',
    cancel: 'Cancel',
    remove: 'Remove',
    addPhoto: 'Add a photo',
    changePhoto: 'Change photo',
    removePhoto: 'Remove photo',
    photoSaving: 'Saving your photo…',
    photoHint:
      'Optional, and yours to choose. It shows on your card and in the contact you share, nowhere else in the hub, and BambooHR never sees it.',
    photoNotImage: 'That file could not be read as a picture. Try a JPEG or PNG.',
    photoFailed: 'Your photo could not be saved. Try again in a moment.',
    editCard: 'Edit card',
    editTitle: 'Edit your card',
    detailsHint: 'All optional, and all yours to set. BambooHR neither supplies nor overwrites any of it. Your name, title and email come from HR.',
    photoField: 'Photo',
    mobileField: 'Mobile',
    officePhoneField: 'Office phone',
    officeAddressHead: 'Office address',
    addressHint: (fallback) =>
      fallback
        ? `Leave empty and your card shows ${fallback}. If you fill it in, a city and a country are needed.`
        : 'If you fill it in, a city and a country are needed.',
    streetField: 'Street address',
    cityField: 'City',
    regionField: 'Province, state or region',
    postcodeField: 'Postal code',
    countryField: 'Country',
    mobileLabel: 'Mobile',
    officeLabel: 'Office',
    phoneInvalid: 'A phone number can use digits, spaces and + ( ) - . x only, 7 to 32 characters.',
    addressIncomplete: 'An office address needs at least a city and a country — or leave every address field empty.',
    detailsFailed: 'Your card could not be saved. Try again in a moment.',
    cropTitle: 'Position your photo',
    cropFrameLabel: 'Your photo. Arrow keys move it, plus and minus zoom.',
    cropHelp: 'Drag to move it. Pinch, scroll or use the slider to zoom. What is inside the circle is what people see on your card.',
    zoomLabel: 'Zoom',
    usePhoto: 'Use this photo',
    widgetTitle: 'Your business card',
    widgetLede: 'Meeting someone? Show your card and they can scan your details straight into their phone.',
    show: 'Show my card',
    noAddressHead: 'We have no work address for you',
    noAddressBody:
      'A business card has to give an address people can reach you at, and there is none on file. Ask your manager to have your company address added — once it is there your card will appear here without anything else changing.',
    personalAddressHead: 'Your address is not a company address',
    personalAddressBody:
      'Business cards are made only for company addresses, because a card is a statement about who you work for. Ask your manager about a company mailbox.',
    failedHead: 'Your card could not be prepared',
    failedBody:
      'Something went wrong rather than something being missing. Close this and try again; if it keeps happening, send a screenshot to the build team.',
  },
  es: {
    dialogTitle: 'Tu tarjeta de presentación digital',
    cardLabel: (name) => `Tarjeta de presentación de ${name}, Conetec`,
    loading: 'Cargando tu tarjeta…',
    preparingCode: 'Preparando el código…',
    scan: 'Escanea para guardar el contacto',
    emailLabel: 'Correo',
    phoneLabel: 'Teléfono',
    placeLabel: 'Ubicación',
    howItWorks:
      'El código es la tarjeta de contacto en sí. No se consulta nada cuando alguien lo escanea, así que funciona en terreno sin señal.',
    saveContact: 'Descargar contacto',
    share: 'Compartir',
    saveImage: 'Guardar como imagen',
    imageFailed: 'No se pudo crear la imagen de tu tarjeta. Inténtalo de nuevo en un momento.',
    addPhone: 'Agregar un número de teléfono',
    changePhone: 'Cambiar número de teléfono',
    phoneField: 'Número de teléfono',
    phoneHint: 'Es opcional y lo defines tú. BambooHR no lo entrega ni lo sobrescribe.',
    save: 'Guardar',
    saving: 'Guardando…',
    cancel: 'Cancelar',
    remove: 'Quitar',
    addPhoto: 'Agregar una foto',
    changePhoto: 'Cambiar foto',
    removePhoto: 'Quitar foto',
    photoSaving: 'Guardando tu foto…',
    photoHint:
      'Es opcional y la eliges tú. Aparece en tu tarjeta y en el contacto que compartes, en ninguna otra parte del portal, y BambooHR nunca la ve.',
    photoNotImage: 'Ese archivo no se pudo leer como imagen. Prueba con un JPEG o PNG.',
    photoFailed: 'No se pudo guardar tu foto. Inténtalo de nuevo en un momento.',
    editCard: 'Editar tarjeta',
    editTitle: 'Edita tu tarjeta',
    detailsHint: 'Todo es opcional y lo defines tú. BambooHR no lo entrega ni lo sobrescribe. Tu nombre, cargo y correo vienen de RR. HH.',
    photoField: 'Foto',
    mobileField: 'Celular',
    officePhoneField: 'Teléfono de oficina',
    officeAddressHead: 'Dirección de la oficina',
    addressHint: (fallback) =>
      fallback
        ? `Si la dejas vacía, tu tarjeta muestra ${fallback}. Si la completas, se necesitan una ciudad y un país.`
        : 'Si la completas, se necesitan una ciudad y un país.',
    streetField: 'Dirección',
    cityField: 'Ciudad',
    regionField: 'Región, provincia o estado',
    postcodeField: 'Código postal',
    countryField: 'País',
    mobileLabel: 'Celular',
    officeLabel: 'Oficina',
    phoneInvalid: 'Un teléfono puede usar dígitos, espacios y + ( ) - . x solamente, de 7 a 32 caracteres.',
    addressIncomplete: 'La dirección de la oficina necesita al menos una ciudad y un país, o deja vacíos todos sus campos.',
    detailsFailed: 'No se pudo guardar tu tarjeta. Inténtalo de nuevo en un momento.',
    cropTitle: 'Ajusta tu foto',
    cropFrameLabel: 'Tu foto. Las flechas la mueven; más y menos hacen zoom.',
    cropHelp: 'Arrástrala para moverla. Pellizca, usa la rueda o el control para hacer zoom. Lo que queda dentro del círculo es lo que se ve en tu tarjeta.',
    zoomLabel: 'Ampliar',
    usePhoto: 'Usar esta foto',
    widgetTitle: 'Tu tarjeta de presentación',
    widgetLede: '¿Conoces a alguien? Muéstrale tu tarjeta y podrá escanear tus datos directo en su teléfono.',
    show: 'Mostrar mi tarjeta',
    noAddressHead: 'No tenemos un correo de trabajo tuyo',
    noAddressBody:
      'Una tarjeta de presentación tiene que indicar un correo donde puedan contactarte, y no hay ninguno registrado. Pídele a tu jefatura que agreguen tu correo de la empresa: cuando esté, tu tarjeta aparecerá aquí sin que cambie nada más.',
    personalAddressHead: 'Tu correo no es de la empresa',
    personalAddressBody:
      'Las tarjetas de presentación solo se generan para correos de la empresa, porque una tarjeta dice para quién trabajas. Consulta con tu jefatura por un buzón de la empresa.',
    failedHead: 'No se pudo preparar tu tarjeta',
    failedBody:
      'Algo falló; no es que falte un dato. Cierra esto e inténtalo otra vez; si sigue pasando, envía una captura al equipo que construye el portal.',
  },
}

/* ── YOUR QUESTIONS: the asker's list and a private thread ─────────────────── */

/**
 * The asker's side of HLP-22. The lane-holder's inbox (SupportInbox, the lane
 * chips on it, the answer box) is left English with the other admin screens —
 * one person holds all three lanes today (HLP-14). The THREAD is shared by
 * both readers, so it is here whole; the lane's own name comes from laneWords.
 *
 * THE VERDICT RULE HOLDS IN SPANISH: "no" must never read as a failure, so
 * "Todavía no" is plain and the confirmation says the team can see it.
 */
export type SupportCopy = {
  yourQuestions: string
  yourQuestionsLede: string
  noneAsked: string
  stillOpen: string
  answered: string
  openNoReply: string
  openReplies: (n: number) => string
  showing: (shown: number, total: number) => string
  privateQuestion: string
  opening: string
  privateSuffix: (lane: string) => string
  allYourQuestions: string
  /** 118 — the holder's way back to the desk from a thread opened from the bell. */
  backToQueue: string
  somebody: string
  whatOneSaid: string
  /** 116 — where the asker was, and their pictures */
  pageDetailsHead: string
  pageDetailLabels: { route: string; viewport: string; app_version: string; locale: string; browser: string }
  picturesHead: (n: number) => string
  noReplyAsker: string
  noReplyHolder: string
  withdrawn: string
  replyToTeam: string
  replyToQuestion: string
  placeholderAsker: string
  placeholderHolder: string
  sending: string
  reply: string
  didItAnswer: string
  yesThatsIt: string
  notYet: string
  markedAnswered: string
  actuallyNotYet: string
  saidNotAnswered: string
  answeredNow: string
  /** 118 — whose turn it is, from the ASKER's side. The lane's side of the
   *  same three words is TURN_LABELS in lib/support.ts (a staff screen,
   *  English on purpose). Both read `app.support_turn()`. */
  turn: { holder: string; asker: string; closed: string; answered: string }
  /** The lane's side, for the thread head a holder opens — here rather than
   *  in support.ts because the thread is one component for both readers. */
  turnHolder: { holder: string; asker: string; closed: string; answered: string }
  /** 174 — the lane closes a thread; the asker is asked to confirm. */
  closedAsk: string
  closedAskLede: string
  yesSolved: string
  noStillNeedHelp: string
  closeThread: string
  reopenThread: string
  replyAndClose: string
  details: string
  closedHolderNote: string
  waitingFor: (duration: string) => string
  /** 118 — one box, two destinations (the reply, or a note the asker never sees). */
  replyTo: (firstName: string) => string
  noteForTeam: string
  replyGoesTo: (firstName: string) => string
  noteGoesTo: (firstName: string) => string
  notePlaceholder: string
  addNote: string
  /** 122 — the saved-reply picker above a holder's reply box */
  insertSavedReply: string
  /** 119 — a question handed to one named colleague */
  handedTo: (name: string) => string
  handedToYou: string
  handedToYouLede: string
  /** 172 — the asker's line on a platform ticket that was awarded stars */
  starsOnTicket: (level: string, stars: number) => string
}

/**
 * 172. The three levels of stars on a platform support ticket (David, 11 Oct),
 * as a phrase that follows "marked your ticket" / "raised your ticket to". The
 * one home for the words: the bell, the asker's line and the staff picker
 * all read it.
 */
export type SupportStarLevel = 'thanks' | 'important' | 'critical'
export const SUPPORT_STAR_LEVELS: Record<HubLang, Record<SupportStarLevel, string>> = {
  en: {
    thanks: 'a good idea or bug report',
    important: 'a good idea or important bug report',
    critical: 'a great idea or critical bug report',
  },
  es: {
    thanks: 'una buena idea o un reporte de error',
    important: 'una buena idea o un reporte de error importante',
    critical: 'una gran idea o un reporte de error crítico',
  },
}

export function supportStarLevelWords(lang: HubLang, level: string): string {
  return (SUPPORT_STAR_LEVELS[lang] as Record<string, string>)[level] ?? level
}

export const SUPPORT: Record<HubLang, SupportCopy> = {
  en: {
    yourQuestions: 'Your questions',
    yourQuestionsLede:
      'Questions you sent privately. Only you and the team you asked can read them.',
    noneAsked:
      'You haven’t sent anything privately. To ask a person, press New above and choose Ticket.',
    stillOpen: 'Still open',
    answered: 'Answered',
    openNoReply: 'Open — no reply yet',
    openReplies: (n) => `Open — ${n} ${n === 1 ? 'reply' : 'replies'}`,
    showing: (shown, total) => `Showing ${shown} of ${total}. The newest are first.`,
    privateQuestion: 'Private question',
    opening: 'Opening…',
    privateSuffix: (lane) => `${lane} — private`,
    allYourQuestions: 'All your questions',
    backToQueue: 'Support inbox',
    somebody: 'Somebody',
    whatOneSaid: 'What One told them first',
    pageDetailsHead: 'Where they were',
    pageDetailLabels: { route: 'Screen', viewport: 'Window', app_version: 'App version', locale: 'Language', browser: 'Browser' },
    picturesHead: (n) => `${n} ${n === 1 ? 'picture' : 'pictures'}`,
    noReplyAsker: 'No reply yet. You will get a notification when there is one.',
    noReplyHolder: 'Nobody has replied yet. They will get a notification when you do.',
    withdrawn: 'This reply was withdrawn.',
    replyToTeam: 'Reply to the team',
    replyToQuestion: 'Reply to this question',
    placeholderAsker: 'Your reply. Only the team you asked can see it.',
    placeholderHolder: 'Your reply. They see it and nobody else does.',
    sending: 'Sending…',
    reply: 'Reply',
    didItAnswer: 'Did this answer your question?',
    yesThatsIt: "Yes, that's it",
    notYet: 'Not yet',
    markedAnswered: 'You marked this answered.',
    actuallyNotYet: 'Actually, not yet',
    saidNotAnswered: "You've said this isn't answered yet — the team can see that.",
    answeredNow: "It's answered now",
    turn: { holder: 'Waiting on them', asker: 'They replied', closed: 'Closed by support', answered: 'Answered' },
    turnHolder: { holder: 'Needs reply', asker: 'Waiting on asker', closed: 'Closed · not confirmed', answered: 'Confirmed by asker' },
    closedAsk: 'Support closed this question.',
    closedAskLede: 'If it’s solved, tell them so. If you still need help, it goes straight back to the top of their list. Replying reopens it too.',
    yesSolved: 'Yes, it’s solved',
    noStillNeedHelp: 'No, I still need help',
    closeThread: 'Close',
    reopenThread: 'Reopen',
    replyAndClose: 'Reply and close',
    details: 'Details',
    closedHolderNote: 'Closed, and they were asked to confirm. A reply from either side reopens it.',
    waitingFor: (d) => `waiting ${d}`,
    replyTo: (name) => `Reply to ${name}`,
    noteForTeam: 'Note for the team',
    replyGoesTo: (name) => `${name} sees this and gets a notification. Nobody else does.`,
    noteGoesTo: (name) => `Only people who answer this lane see notes. ${name} never does.`,
    notePlaceholder: 'What a colleague should know before they pick this up.',
    addNote: 'Add note',
    insertSavedReply: 'Insert a saved reply…',
    handedTo: (name) => `Handed to ${name}`,
    handedToYou: 'Handed to you',
    handedToYouLede: 'Private questions a support lane handed to you. You can read and reply to these, and nothing else in that lane.',
    starsOnTicket: (level, stars) =>
      `Platform support marked this ${level}` +
      (stars > 0 ? ` — ${stars.toLocaleString()} stars. Thank you.` : '. Thank you. This month’s stars for support tickets had already reached the limit.'),
  },
  es: {
    yourQuestions: 'Tus preguntas',
    yourQuestionsLede:
      'Preguntas que enviaste en privado. Solo tú y el equipo al que le preguntaste pueden leerlas.',
    noneAsked:
      'No has enviado nada en privado. Para preguntarle a una persona, pulsa Nuevo arriba y elige Solicitud.',
    stillOpen: 'Sigue abierta',
    answered: 'Respondida',
    openNoReply: 'Abrir: todavía sin respuesta',
    openReplies: (n) => `Abrir: ${n} ${n === 1 ? 'respuesta' : 'respuestas'}`,
    showing: (shown, total) => `Se muestran ${shown} de ${total}. Las más nuevas primero.`,
    privateQuestion: 'Pregunta privada',
    opening: 'Abriendo…',
    privateSuffix: (lane) => `${lane}: privada`,
    allYourQuestions: 'Todas tus preguntas',
    backToQueue: 'Bandeja de soporte',
    somebody: 'Alguien',
    whatOneSaid: 'Lo que One respondió primero',
    pageDetailsHead: 'Dónde estaba',
    pageDetailLabels: { route: 'Pantalla', viewport: 'Ventana', app_version: 'Versión', locale: 'Idioma', browser: 'Navegador' },
    picturesHead: (n) => `${n} ${n === 1 ? 'imagen' : 'imágenes'}`,
    noReplyAsker: 'Todavía no hay respuesta. Recibirás una notificación cuando la haya.',
    noReplyHolder:
      'Nadie ha respondido todavía. Quien preguntó recibirá una notificación cuando respondas.',
    withdrawn: 'Esta respuesta fue retirada.',
    replyToTeam: 'Responder al equipo',
    replyToQuestion: 'Responder esta pregunta',
    placeholderAsker: 'Tu respuesta. Solo la ve el equipo al que le preguntaste.',
    placeholderHolder: 'Tu respuesta. La ve esa persona y nadie más.',
    sending: 'Enviando…',
    reply: 'Responder',
    didItAnswer: '¿Esto respondió tu pregunta?',
    yesThatsIt: 'Sí, eso era',
    notYet: 'Todavía no',
    markedAnswered: 'La marcaste como respondida.',
    actuallyNotYet: 'En realidad, todavía no',
    saidNotAnswered: 'Dijiste que todavía no está respondida; el equipo puede verlo.',
    answeredNow: 'Ya está respondida',
    turn: { holder: 'Esperando al equipo', asker: 'Te respondieron', closed: 'Cerrada por soporte', answered: 'Respondida' },
    turnHolder: { holder: 'Necesita respuesta', asker: 'Esperando a quien preguntó', closed: 'Cerrada · sin confirmar', answered: 'Confirmada por quien preguntó' },
    closedAsk: 'Soporte cerró esta pregunta.',
    closedAskLede: 'Si está resuelta, avísales. Si todavía necesitas ayuda, vuelve de inmediato al primer lugar de su lista. Responder también la reabre.',
    yesSolved: 'Sí, está resuelta',
    noStillNeedHelp: 'No, todavía necesito ayuda',
    closeThread: 'Cerrar',
    reopenThread: 'Reabrir',
    replyAndClose: 'Responder y cerrar',
    details: 'Detalles',
    closedHolderNote: 'Cerrada, y se le pidió a quien preguntó que lo confirme. Una respuesta de cualquiera de los dos la reabre.',
    waitingFor: (d) => `esperando ${d}`,
    replyTo: (name) => `Responder a ${name}`,
    noteForTeam: 'Nota para el equipo',
    replyGoesTo: (name) => `${name} la ve y recibe una notificación. Nadie más.`,
    noteGoesTo: (name) => `Solo quienes atienden esta vía ven las notas. ${name} nunca las ve.`,
    notePlaceholder: 'Lo que un colega debería saber antes de tomar esta pregunta.',
    addNote: 'Agregar nota',
    insertSavedReply: 'Insertar una respuesta guardada…',
    handedTo: (name) => `Pasada a ${name}`,
    handedToYou: 'Te las pasaron',
    handedToYouLede: 'Preguntas privadas que una vía de soporte te pasó. Puedes leerlas y responderlas, y nada más de esa vía.',
    starsOnTicket: (level, stars) =>
      `Soporte de la plataforma marcó esto como ${level}` +
      (stars > 0 ? ` — ${stars.toLocaleString()} Stars. Gracias.` : '. Gracias. Este mes las Stars por tickets de soporte ya habían llegado al límite.'),
  },
}

/* ── THE HERO BANNER: the campaign line over the two rigs ──────────────────── */

/**
 * The English is lib/copy.ts's PROVISIONAL_CAMPAIGN_COPY, imported rather than
 * retyped — that file's own rule is that the campaign line lives in exactly
 * one place, and B12 closing must still change only it. What is here is the
 * Spanish beside it, and the frame (date, picture description, the pending
 * state).
 *
 * THE SPANISH CAMPAIGN LINE IS A TRANSLATION, NOT AN APPROVED TAGLINE. B12
 * settled "Same ground. Same crews. One name." in English; nobody has settled
 * it in Spanish, and a tagline is Marketing's to choose. "Terreno" was picked
 * because in Chile and Peru it is the word for the field itself ("trabajo en
 * terreno"), which is what "ground" means here; "equipos" for crews (David,
 * 24 Sep, over "cuadrillas") — the word the rest of this file already uses for
 * a team. Because "equipos" now means PEOPLE here, the picture's description
 * calls the rigs "máquinas", so one word does not mean two things on one
 * banner.
 * Both are questions for the Spanish reader on SHL-25.
 */
/* ── ONE QUESTION: the weekly question card (105, 131) ────────────────────────
 *
 * "One Question" and "One Voice" are NAMES (the activity and what
 * Communications makes from the answers, per the schedule document) and stay
 * English in both columns, like Star One — OneQuestionCard renders the first
 * as a constant, and `about` uses the second as written.
 *
 * `named` is said BEFORE anybody types, and that is the point (034's rule):
 * who reads the answer, that it carries your name, and what may happen to the
 * words. Since 131 (David, 5 Oct) the answer goes to Communications only and
 * nobody else ever sees it; One Voice may quote it WITHOUT a name. Saying so
 * after the button is pressed is too late to matter.
 */
export type OneQuestionCopy = {
  about: string
  weekOf: (n: number) => string
  answerFor: (stars: number) => string
  placeholder: string
  submit: string
  save: string
  cancel: string
  named: (closes: string) => string
  yourAnswer: string
  edit: string
  withdraw: string
  removedByMod: string
  answeredSoFar: (n: number) => string
  earned: (n: number) => string
  saved: string
  lastWeek: string
  answers: (n: number) => string
  failed: string
  /** The hub widget (11 Oct): the hook's count, and its two buttons. */
  answeredShort: (n: number) => string
  seeYourAnswer: string
  seeLastWeek: string
}

export const ONE_QUESTION: Record<HubLang, OneQuestionCopy> = {
  en: {
    about:
      "A new question every Monday. Answer in a sentence or two — it takes a minute. Communications brings everyone's answers together as One Voice, so we can all see what the company said.",
    weekOf: (n) => `Week ${n}`,
    answerFor: (stars) => (stars > 0 ? `Answer for ${stars} stars` : 'Answer this week'),
    placeholder: 'A short answer…',
    submit: 'Answer',
    save: 'Save changes',
    cancel: 'Cancel',
    named: (closes) =>
      `Only the Communications team sees your answer and your name — nobody else here does. Answers may be quoted in One Voice without names. You can change yours until ${closes}.`,
    yourAnswer: 'Your answer',
    edit: 'Change it',
    withdraw: 'Take it back',
    removedByMod: 'A moderator removed your answer to this question.',
    answeredSoFar: (n) => (n === 1 ? '1 person has answered so far' : `${n} people have answered so far`),
    earned: (n) => `Saved. +${n} stars.`,
    saved: 'Saved.',
    lastWeek: 'Last week',
    answers: (n) => (n === 1 ? '1 person answered' : `${n} people answered`),
    failed: 'That did not work:',
    answeredShort: (n) => (n === 1 ? '1 answer so far' : `${n} answers so far`),
    seeYourAnswer: 'You answered · see it',
    seeLastWeek: "Last week's question",
  },
  es: {
    about:
      'Una pregunta nueva cada lunes. Responde en una o dos frases: toma un minuto. Comunicaciones reúne las respuestas de todos en One Voice, para que todos veamos lo que dijo la empresa.',
    weekOf: (n) => `Semana ${n}`,
    answerFor: (stars) => (stars > 0 ? `Responde y gana ${stars} Stars` : 'Responde esta semana'),
    placeholder: 'Una respuesta corta…',
    submit: 'Responder',
    save: 'Guardar cambios',
    cancel: 'Cancelar',
    named: (closes) =>
      `Solo el equipo de Comunicaciones ve tu respuesta y tu nombre; nadie más aquí los ve. Las respuestas pueden citarse en One Voice sin nombres. Puedes cambiar la tuya hasta el ${closes}.`,
    yourAnswer: 'Tu respuesta',
    edit: 'Cambiarla',
    withdraw: 'Retirarla',
    removedByMod: 'Un moderador retiró tu respuesta a esta pregunta.',
    answeredSoFar: (n) => (n === 1 ? 'Hasta ahora respondió 1 persona' : `Hasta ahora respondieron ${n} personas`),
    earned: (n) => `Guardado. +${n} Stars.`,
    saved: 'Guardado.',
    lastWeek: 'La semana pasada',
    answers: (n) => (n === 1 ? 'Respondió 1 persona' : `Respondieron ${n} personas`),
    failed: 'No funcionó:',
    answeredShort: (n) => (n === 1 ? '1 respuesta hasta ahora' : `${n} respuestas hasta ahora`),
    seeYourAnswer: 'Ya respondiste · verla',
    seeLastWeek: 'La pregunta de la semana pasada',
  },
}

export type HeroCopy = {
  headline: string
  subhead: string
  launchDate: string
  imageAlt: string
  pendingTag: string
  pendingTitle: string
  pendingSub: string
}

export const HERO: Record<HubLang, HeroCopy> = {
  en: {
    headline: PROVISIONAL_CAMPAIGN_COPY.headline,
    subhead: PROVISIONAL_CAMPAIGN_COPY.subhead,
    launchDate: '15 April 2027',
    imageAlt:
      'Two Conetec rigs in the field — a track-mounted drill rig on open sand and a CPT truck at sunrise',
    pendingTag: 'Headline pending sign-off — blocker B12',
    pendingTitle: 'Campaign headline goes here',
    pendingSub:
      'Brand voice is still being decided, so no line is set. This block takes the approved headline and subhead as props the moment B12 closes.',
  },
  es: {
    headline: 'El mismo terreno. Los mismos equipos. Un solo nombre.',
    subhead:
      'El 15 de abril, Conetec, Mud Bay Drilling, Geotech Drilling y Lankelma salen al mercado con una sola identidad. Nada cambia en la forma de hacer el trabajo.',
    launchDate: '15 de abril de 2027',
    imageAlt:
      'Dos máquinas de Conetec en terreno: una perforadora sobre orugas en la arena y un camión CPT al amanecer',
    pendingTag: 'Titular pendiente de aprobación: bloqueo B12',
    pendingTitle: 'Aquí va el titular de la campaña',
    pendingSub:
      'La voz de la marca todavía se está definiendo, así que no hay una frase fijada. Este bloque toma el titular y la bajada aprobados en cuanto se cierre B12.',
  },
}

/**
 * Every table, for the suite's completeness check. A section added above and
 * not listed here is a section nobody checks, so the suite also counts the
 * `Record<HubLang,` declarations in this file against this list.
 */
/* ── 127 (FED-25): WHAT THE CHECK SAID, IN MODERATOR MODE ─────────────────── */

export type CheckNoteCopy = {
  heading: string
  pass: string
  flag: string
  block: string
  /** The reply version of a block: it was live, so it was taken DOWN. */
  replyBlock: string
  notChecked: string
  noRecord: string
  rules: (list: string) => string
  policy: (v: number) => string
  resolved: (how: string) => string
  resolutions: Record<string, string>
  tookDown: string
  dismiss: string
  restore: string
  uphold: string
  /** Shown to the AUTHOR under their own reply the check took down. */
  yourReplyTakenDown: string
  noReasonGiven: string
}

export const CHECK_NOTE: Record<HubLang, CheckNoteCopy> = {
  en: {
    heading: 'The check',
    pass: 'Passed',
    flag: 'Flagged',
    block: 'Blocked',
    replyBlock: 'Taken down',
    notChecked: 'Not checked',
    noRecord: 'No check on record — this went out before the check existed.',
    rules: (list) => `Rules: ${list}`,
    policy: (v) => `guidelines v${v}`,
    resolved: (how) => `Resolved: ${how}`,
    resolutions: {
      dismiss: 'flag cleared',
      restore: 'put back',
      uphold: 'kept down',
      release: 'released',
      remove: 'removed',
    },
    tookDown: 'What it took down:',
    dismiss: 'Clear the flag',
    restore: 'Put it back',
    uphold: 'Keep it down',
    yourReplyTakenDown: 'The content check took this down:',
    noReasonGiven: 'it appears to hit one of the content guidelines. A moderator will look at it.',
  },
  es: {
    heading: 'La revisión',
    pass: 'Aprobado',
    flag: 'Marcado',
    block: 'Bloqueado',
    replyBlock: 'Retirado',
    notChecked: 'Sin revisar',
    noRecord: 'Sin revisión registrada: salió antes de que existiera la revisión.',
    rules: (list) => `Reglas: ${list}`,
    policy: (v) => `pautas v${v}`,
    resolved: (how) => `Resuelto: ${how}`,
    resolutions: {
      dismiss: 'marca quitada',
      restore: 'restituido',
      uphold: 'se mantiene retirado',
      release: 'liberado',
      remove: 'eliminado',
    },
    tookDown: 'Lo que retiró:',
    dismiss: 'Quitar la marca',
    restore: 'Restituir',
    uphold: 'Mantener retirado',
    yourReplyTakenDown: 'La revisión de contenido retiró esto:',
    noReasonGiven: 'parece infringir una de las pautas de contenido. Un moderador lo revisará.',
  },
}

/* ── THE PHOTO ROOM (145, FED-48; 146) ─────────────────────────────────────── */

export type PhotoRoomCopy = {
  tabPhotos: string
  tabContests: string
  /** The gallery's ? menu (David, 10 Oct: less text on the page). */
  helpLabel: string
  helpTitle: string
  help: string[]
  /** The sentence about stars, only when photo.used is on (off since 146). */
  ledeStars: (stars: number) => string
  refused: string
  loading: string
  empty: string
  emptyFiltered: string
  shownOf: (shown: number, total: number) => string
  showMore: string
  noCaption: string
  withPeople: string
  openPost: string
  // search, sort, filter
  searchLabel: string
  searchPlaceholder: string
  search: string
  searching: string
  clearSearch: string
  scoredNote: (scored: number, total: number) => string
  textOrderNote: string
  noMatches: string
  match: (score: number) => string
  sortLabel: string
  sort: { relevance: string; posted: string; taken: string; downloads: string; winners: string; resolution: string }
  filterLabel: string
  clearFilters: string
  /** The compact toolbar: one Filters button, a date preset, a custom range. */
  filtersButton: string
  dateLabel: string
  datePreset: Record<'any' | 'd7' | 'd30' | 'y1' | 'custom', string>
  dateFrom: string
  dateTo: string
  downloadsShort: (n: number) => string
  facet: { brand: string; equipment: string; sector: string; feature: string }
  describing: (n: number) => string
  fullSizeBadge: string
  wonBadge: string
  /** 304 (FED-71). Add photos straight to the Gallery (its admins), and a
   *  Gallery upload in the viewer. */
  addPhotos: string
  addTitle: string
  addIntro: string[]
  dropHere: string
  choosePhotos: string
  chooseFolder: string
  acceptNote: string
  forEvery: string
  takenLabel: string
  takenHint: string
  takenBad: string
  brandLabel: string
  noBrand: string
  tagsLabel: string
  /* 321: every tag is chosen by searching. */
  brandPick: string
  tagsFilter: string
  captionFromName: string
  captionPlaceholder: string
  removeRow: string
  status: Record<'waiting' | 'known' | 'uploading' | 'added' | 'failed' | 'stopped', string>
  counts: (waiting: number, known: number, failed: number) => string
  addCount: (n: number) => string
  hashing: (done: number, total: number) => string
  adding: (done: number, total: number) => string
  reading: (done: number, total: number) => string
  summary: (added: number, known: number, failed: number) => string
  stop: string
  stopped: string
  retryFailed: string
  close: string
  exifTaken: (when: string) => string
  willBeDated: (when: string) => string
  noExif: string
  addedBy: string
  addedOn: string
  fileName: string
  removeFromGallery: string
  removeConfirm: string
  removeYes: string
  removeNo: string
  removedNote: string
  /** 304c. The Gallery upload's post page is where people are tagged; and
   *  how many photos One has not read, with the offer to read them now. */
  tagPeople: string
  unreadLine: (n: number) => string
  readRest: string
  readingRest: (done: number, total: number) => string
  readStop: string
  // the viewer
  photo: string
  previous: string
  next: string
  showFullSize: string
  showingFullSize: string
  description: string
  byOneConetec: string
  byPerson: string
  noDescription: string
  edit: string
  save: string
  cancel: string
  english: string
  spanish: string
  askAgain: string
  asking: string
  whatIsInIt: string
  suggested: (pct: number) => string
  yes: string
  no: string
  notInPhoto: string
  add: string
  addNew: string
  newLabelEn: string
  newLabelEs: string
  people: string
  nobodyTagged: string
  consent: (tagged: number) => string
  details: string
  postedBy: string
  posted: string
  taken: string
  notRecorded: string
  feedCopy: string
  fullSize: string
  noFullSize: string
  printsAt: (w: number, h: number) => string
  orientation: { landscape: string; portrait: string; square: string }
  hashtags: string
  wins: string
  place: (n: number) => string
  downloads: string
  neverDownloaded: string
  downloadedTimes: (n: number) => string
  whoDownloaded: string
  fullTag: string
  // 148 (FED-53) — a download says what it is for
  useFor: string
  useLabel: Record<'marketing' | 'sales' | 'other', string>
  usesLine: (ok: string[], out: string[]) => string
  reasonLine: (r: string) => string
  // 149 (FED-54) — where it was taken (photo room admins)
  where: string
  whereNotSent: string
  wherePending: string
  coordinates: (lat: number, lng: number) => string
  site: string
  siteSuggested: (pct: number) => string
  siteYes: string
  siteNo: string
  siteRename: string
  siteNone: string
  /* 321 (FED-74): the site for document users too, named from a searchable list. */
  siteConfirmedBy: (name: string | null) => string
  siteNoneSaid: (name: string | null) => string
  siteOneUnsure: string
  siteNotYet: string
  siteChange: string
  sitePick: string
  sitePhotos: (n: number) => string
  siteFailed: string
  download: string
  downloadFull: string
  downloadFeed: string
  downloading: string
  starsPaid: (stars: number | null) => string
  outcome: {
    awarded: (name: string, stars: number) => string
    duplicate: string
    own_photo: string
    /** 300. A photo on a byline post pays nobody. */
    byline: string
    capped: (name: string) => string
    rule_off: string
    not_member: (name: string) => string
    failed: string
  }
  plainDownloaded: string
  notSaved: string
  withdrawn: string
  couldNotLoad: string
  couldNotSave: string
  // contests
  contestsLede: string
  newContest: string
  contestTitle: string
  defaultTitle: (month: string) => string
  from: string
  to: string
  timezone: string
  places: string
  starsFor: (place: string) => string
  addPlace: string
  removePlace: string
  saveContest: string
  noContests: string
  contestRange: (from: string, to: string) => string
  entries: (n: number) => string
  statusOpen: string
  statusJudged: (date: string, name: string) => string
  statusCancelled: string
  openContest: string
  editContest: string
  cancelContest: string
  confirmCancel: string
  allContests: string
  choosePlace: string
  noPlace: string
  awardButton: (n: number) => string
  awardTitle: string
  awardFinal: string
  awardConfirm: string
  awardResult: (name: string, place: string, stars: number, told: boolean) => string
  awardNoStars: (reason: string) => string
  ownPhotoNote: string
  entriesShown: (shown: number, total: number) => string
  noEntries: string
  winners: string
}

const placeEn = (n: number) => (n === 1 ? '1st' : n === 2 ? '2nd' : n === 3 ? '3rd' : `${n}th`)

export const PHOTO_ROOM: Record<HubLang, PhotoRoomCopy> = {
  en: {
    tabPhotos: 'Photos',
    tabContests: 'Contests',
    helpLabel: 'How it works',
    helpTitle: 'About the gallery',
    help: [
      'Photos colleagues post to the feed, licensed for company use by posting them. A photo leaves the gallery if the person who posted it opts it out, or anyone tagged in it says no.',
      'Search in plain words — OneConetec scores the closest matches. Narrow by when it was posted and by what is in it, and sort the way you need.',
      'Open a photo for its details and to download it. A download says what it is for, and every download is recorded with your name.',
      'Map shows where each photo was taken, when the person who posted it sent the location. Press a pin or a numbered group to list just those photos; Show all brings the rest back. Photos with no location stay in the list, and the map says how many; open one to add where it was taken.',
    ],
    ledeStars: (stars) =>
      ` The first download of a photo earns the person who posted it ${stars.toLocaleString()} stars — once per photo.`,
    refused: 'The gallery needs the Gallery document user role. Ask the person who runs People and roles.',
    loading: 'Loading the gallery…',
    empty: 'No photos in the gallery yet. They arrive as people post photos, unless a photo is opted out of every use.',
    emptyFiltered: 'No photo matches all of those filters.',
    shownOf: (shown, total) => `Showing ${shown.toLocaleString()} of ${total.toLocaleString()}`,
    showMore: 'Show more',
    noCaption: 'No caption',
    withPeople: 'With',
    openPost: 'Open the post',
    searchLabel: 'Search the gallery',
    searchPlaceholder: 'Describe a photo — crew on a track rig at a mine',
    search: 'Search',
    searching: 'OneConetec is looking…',
    clearSearch: 'Clear search',
    scoredNote: (scored, total) =>
      total > scored
        ? `${total.toLocaleString()} photos match a word of your search. OneConetec scored the closest ${scored.toLocaleString()}.`
        : `OneConetec scored the ${scored.toLocaleString()} ${scored === 1 ? 'photo' : 'photos'} that match a word of your search.`,
    textOrderNote: 'OneConetec could not score these just now, so they are in order of how many of your words they match.',
    noMatches: 'No photo matches any word of that. Try other words, or fewer.',
    match: (score) => `${score}% match`,
    sortLabel: 'Sort by',
    sort: {
      relevance: 'Best match',
      posted: 'Date posted',
      taken: 'Date taken',
      downloads: 'Most downloaded',
      winners: 'Contest winners first',
      resolution: 'Largest first',
    },
    filterLabel: 'What is in the photo',
    clearFilters: 'Clear filters',
    filtersButton: 'Filters',
    dateLabel: 'Posted',
    datePreset: { any: 'Any time', d7: 'Last 7 days', d30: 'Last 30 days', y1: 'Last 12 months', custom: 'Custom dates…' },
    dateFrom: 'From',
    dateTo: 'To',
    downloadsShort: (n) => (n === 0 ? 'Not downloaded' : `${n.toLocaleString()} ${n === 1 ? 'download' : 'downloads'}`),
    facet: { brand: 'Brand', equipment: 'Equipment', sector: 'Sector / site', feature: 'In the photo' },
    describing: (n) => `OneConetec is describing ${n} new ${n === 1 ? 'photo' : 'photos'}…`,
    fullSizeBadge: 'Full size',
    wonBadge: 'Winner',
    photo: 'Photo',
    previous: 'Previous photo',
    next: 'Next photo',
    showFullSize: 'Show full size',
    showingFullSize: 'Showing the full-size copy',
    description: 'Description',
    byOneConetec: 'Written by OneConetec from the photo. Edit it if it is wrong.',
    byPerson: 'Written or corrected by a person.',
    noDescription: 'OneConetec has not described this photo yet.',
    edit: 'Edit',
    save: 'Save',
    cancel: 'Cancel',
    english: 'English',
    spanish: 'Spanish',
    askAgain: 'Ask OneConetec again',
    asking: 'OneConetec is looking at the photo…',
    whatIsInIt: 'What is in it',
    suggested: (pct) => `OneConetec suggests this (${pct}% sure). Is it in the photo?`,
    yes: 'Yes',
    no: 'No',
    notInPhoto: 'Not in this photo',
    add: 'Add…',
    addNew: 'Something new…',
    newLabelEn: 'New value (English)',
    newLabelEs: 'Spanish',
    people: 'People',
    nobodyTagged: 'Nobody is tagged.',
    consent: (tagged) =>
      (tagged === 0
        ? 'The person who posted it licensed it for company use.'
        : `The person who posted it licensed it; ${tagged === 1 ? 'the colleague tagged in it has' : `all ${tagged} colleagues tagged in it have`} not objected.`) +
      ' Anyone in it who is not tagged has not been asked.',
    details: 'Details',
    postedBy: 'Posted by',
    posted: 'Posted',
    taken: 'Taken',
    notRecorded: 'Not recorded',
    feedCopy: 'Feed copy',
    fullSize: 'Full size',
    noFullSize: 'Not kept — posted before full-size copies were kept, or the upload did not finish.',
    printsAt: (w, h) => `prints ${w} × ${h} in at 300 dpi`,
    orientation: { landscape: 'Landscape', portrait: 'Portrait', square: 'Square' },
    hashtags: 'Hashtags',
    wins: 'Contests',
    place: (n) => `${placeEn(n)} place`,
    downloads: 'Downloads',
    neverDownloaded: 'Not downloaded yet',
    downloadedTimes: (n) => `Downloaded ${n.toLocaleString()} ${n === 1 ? 'time' : 'times'}`,
    whoDownloaded: 'Who downloaded it',
    fullTag: 'full size',
    useFor: 'What is it for?',
    useLabel: { marketing: 'Marketing materials', sales: 'Sales', other: 'Training and other' },
    usesLine: (ok, out) =>
      (andList(ok, 'and') ? `May be used for ${andList(ok, 'and')}.` : 'Not available for any download.') +
      (andList(out, 'and') ? ` The poster opted it out of ${andList(out, 'and')}.` : ''),
    reasonLine: (r) => `Their reason: “${r}”`,
    where: 'Where',
    whereNotSent: 'Not sent',
    wherePending: 'OneConetec has not named the place yet',
    coordinates: (lat, lng) => `${Number(lat).toFixed(5)}, ${Number(lng).toFixed(5)}`,
    site: 'Site',
    siteSuggested: (pct) => `OneConetec's guess, ${pct}% sure`,
    siteYes: 'Yes, that is it',
    siteNo: 'Not this site',
    siteRename: 'Name it',
    siteNone: 'Not named',
    siteConfirmedBy: (n) => (n ? `Confirmed by ${n}.` : 'Confirmed.'),
    siteNoneSaid: (n) => (n ? `Not a named site — ${n}.` : 'Not a named site.'),
    siteOneUnsure: 'OneConetec could not tell which site this is.',
    siteNotYet: 'OneConetec has not looked yet.',
    siteChange: 'Change',
    sitePick: 'Find a site, or type a new one',
    sitePhotos: (n) => (n === 1 ? '1 photo' : `${n.toLocaleString()} photos`),
    siteFailed: 'The site could not be saved.',
    download: 'Download',
    downloadFull: 'Download full size',
    downloadFeed: 'Download 2,000 px',
    downloading: 'Downloading…',
    starsPaid: (stars) => (stars ? `${stars.toLocaleString()} stars paid` : 'Stars paid'),
    outcome: {
      awarded: (name, stars) => `Downloaded. ${name} earned ${stars.toLocaleString()} stars.`,
      duplicate: 'Downloaded. This photo had already earned its stars.',
      own_photo: 'Downloaded. Your own photo earns you nothing.',
      byline: 'Downloaded. A photo posted under a byline such as Communications earns nobody stars.',
      capped: (name) => `Downloaded. ${name} has reached this month's limit for used photos, so no stars this time.`,
      rule_off: 'Downloaded.',
      not_member: (name) => `Downloaded. ${name} is no longer with the company, so no stars.`,
      failed: 'Downloaded. The stars could not be paid just now; it has been logged.',
    },
    plainDownloaded: 'Downloaded.',
    notSaved: 'The download was recorded, but the file did not come down. Try again — it will not pay twice.',
    withdrawn: 'That photo is no longer in the room — its permission was withdrawn. Nothing was downloaded.',
    couldNotLoad: 'The photo room could not be loaded just now.',
    couldNotSave: 'That could not be saved just now.',
    contestsLede:
      'A contest is a date range. Its photos are the room\'s photos posted in that range — entering is the box people tick when they post. Choose the winners and each gets a bell and their place\'s stars. Choosing is final.',
    newContest: 'New contest',
    contestTitle: 'Title',
    defaultTitle: (month) => `Photo contest — ${month}`,
    from: 'From',
    to: 'To',
    timezone: 'Days are read in time zone',
    places: 'Places and stars',
    starsFor: (place) => `${place} place stars`,
    addPlace: 'Add a place',
    removePlace: 'Remove the last place',
    saveContest: 'Save contest',
    noContests: 'No contests yet.',
    contestRange: (from, to) => `${from} to ${to}`,
    entries: (n) => `${n.toLocaleString()} ${n === 1 ? 'photo' : 'photos'}`,
    statusOpen: 'Not judged yet',
    statusJudged: (date, name) => `Judged ${date} by ${name}`,
    statusCancelled: 'Cancelled',
    openContest: 'Choose winners',
    editContest: 'Edit',
    cancelContest: 'Cancel contest',
    confirmCancel: 'Cancel this contest? It cannot be judged afterwards.',
    allContests: 'All contests',
    choosePlace: 'Place',
    noPlace: '— none —',
    awardButton: (n) => `Award ${n} ${n === 1 ? 'winner' : 'winners'}`,
    awardTitle: 'Award the winners',
    awardFinal: 'This is final. Each winner gets a bell and their place\'s stars now, and the contest cannot be changed afterwards.',
    awardConfirm: 'Award',
    awardResult: (name, place, stars, told) =>
      `${place}: ${name}${stars > 0 ? ` — ${stars.toLocaleString()} stars` : ''}${told ? ', told' : ', bell not sent'}`,
    awardNoStars: (reason) => `no stars (${reason})`,
    ownPhotoNote: 'Your own photo — another judge has to choose it.',
    entriesShown: (shown, total) => `Showing ${shown.toLocaleString()} of ${total.toLocaleString()} photos`,
    noEntries: 'No photos in the room were posted in this range.',
    winners: 'Winners',
    // 304 (FED-71): add photos straight to the Gallery
    addPhotos: 'Add photos',
    addTitle: 'Add photos to the Gallery',
    addIntro: [
      'Photos added here go straight into the Gallery — they are not posted to the feed, and they earn no stars. Each one is licensed for company use (marketing, sales, training and other uses) and is not entered in the monthly photo contest.',
      'Anyone you tag in a photo afterwards is told, can remove the tag, and can ask for a review, exactly as with a feed photo.',
      'OneConetec reads each photo once it is in and suggests what is in it, so colleagues can find it.',
    ],
    dropHere: 'Drop photos or a folder here, or',
    choosePhotos: 'Choose photos',
    chooseFolder: 'Choose a folder',
    acceptNote: 'JPEG, PNG or WebP. A folder is read with its subfolders. Photos already in the Gallery are skipped, so a batch you stopped can be picked again.',
    forEvery: 'For every photo in this batch',
    takenLabel: 'Taken',
    takenHint: 'For photos with no date of their own: a year, a month or a day — 1998, 1998-06 or 1998-06-15.',
    takenBad: 'Write a year, a month or a day: 1998, 1998-06 or 1998-06-15.',
    brandLabel: 'Brand',
    noBrand: 'No brand',
    tagsLabel: 'Also tag with',
    brandPick: 'Find a brand',
    tagsFilter: 'Find a tag',
    captionFromName: 'Use the file name as the caption when none is typed',
    captionPlaceholder: 'Caption (optional)',
    removeRow: 'Remove from this batch',
    status: {
      waiting: 'Ready',
      known: 'Already in the Gallery',
      uploading: 'Adding…',
      added: 'Added',
      failed: 'Failed',
      stopped: 'Not added',
    },
    counts: (waiting, known, failed) =>
      [
        `${waiting.toLocaleString()} to add`,
        known > 0 ? `${known.toLocaleString()} already here` : null,
        failed > 0 ? `${failed.toLocaleString()} failed` : null,
      ]
        .filter(Boolean)
        .join(' · '),
    addCount: (n) => (n === 1 ? 'Add 1 photo' : `Add ${n.toLocaleString()} photos`),
    hashing: (done, total) => `Checking which are already here: ${done.toLocaleString()} of ${total.toLocaleString()}…`,
    adding: (done, total) => `Adding ${done.toLocaleString()} of ${total.toLocaleString()}…`,
    reading: (done, total) =>
      `OneConetec is reading the photos: ${done.toLocaleString()} of ${total.toLocaleString()}. You can close this — the rest are read the next time the Gallery opens.`,
    summary: (added, known, failed) =>
      `${added.toLocaleString()} added` +
      (known > 0 ? `, ${known.toLocaleString()} already here` : '') +
      (failed > 0 ? `, ${failed.toLocaleString()} failed` : '') +
      '.',
    stop: 'Stop',
    stopped: 'Stopped — the photos already added stay.',
    retryFailed: 'Retry the failed ones',
    close: 'Close',
    exifTaken: (when) => `Taken ${when} (camera)`,
    willBeDated: (when) => `Will be dated ${when}`,
    noExif: 'No date in the file',
    addedBy: 'Added to the Gallery by',
    addedOn: 'Added',
    fileName: 'File',
    removeFromGallery: 'Remove from the Gallery',
    removeConfirm: 'Remove this photo from the Gallery? It is not shown anywhere afterwards. Anyone tagged in it keeps their notice.',
    removeYes: 'Remove',
    removeNo: 'Keep it',
    removedNote: 'Removed from the Gallery.',
    tagPeople: 'Tag people',
    unreadLine: (n) => `OneConetec has not read ${n.toLocaleString()} ${n === 1 ? 'photo' : 'photos'} yet.`,
    readRest: 'Read them now',
    readingRest: (done, total) => `OneConetec is reading the rest: ${done.toLocaleString()} of ${total.toLocaleString()}…`,
    readStop: 'Stop',
  },
  es: {
    tabPhotos: 'Fotos',
    tabContests: 'Concursos',
    helpLabel: 'Cómo funciona',
    helpTitle: 'Sobre la galería',
    help: [
      'Fotos que colegas publican en el feed; al publicarlas, las licencian para uso de la empresa. Una foto sale de la galería si quien la publicó la excluye, o si alguien etiquetado en ella dice que no.',
      'Busca con tus palabras — OneConetec puntúa las más cercanas. Acota por fecha de publicación y por lo que hay en la foto, y ordena como necesites.',
      'Abre una foto para ver sus detalles y descargarla. Cada descarga indica para qué es y queda registrada con tu nombre.',
      'Mapa muestra dónde se tomó cada foto, cuando quien la publicó envió la ubicación. Presiona un marcador o un grupo numerado para listar solo esas fotos; Mostrar todas trae el resto de vuelta. Las fotos sin ubicación siguen en la lista, y el mapa dice cuántas son; abre una para agregar dónde se tomó.',
    ],
    ledeStars: (stars) =>
      ` La primera descarga de una foto le da ${stars.toLocaleString()} Stars a quien la publicó — una vez por foto.`,
    refused: 'La galería requiere el rol Usuario de documentos de la Galería. Pídelo a quien administra Personas y roles.',
    loading: 'Cargando la galería…',
    empty: 'Todavía no hay fotos en la galería. Llegan cuando alguien publica fotos, salvo las excluidas de todo uso.',
    emptyFiltered: 'Ninguna foto cumple todos esos filtros.',
    shownOf: (shown, total) => `Mostrando ${shown.toLocaleString()} de ${total.toLocaleString()}`,
    showMore: 'Ver más',
    noCaption: 'Sin descripción del autor',
    withPeople: 'Con',
    openPost: 'Abrir la publicación',
    searchLabel: 'Buscar en la galería',
    searchPlaceholder: 'Describe la foto — p. ej. equipo sonriendo junto a un equipo sobre orugas en una mina',
    search: 'Buscar',
    searching: 'OneConetec está buscando…',
    clearSearch: 'Borrar búsqueda',
    scoredNote: (scored, total) =>
      total > scored
        ? `${total.toLocaleString()} fotos coinciden con alguna palabra. OneConetec evaluó las ${scored.toLocaleString()} más cercanas.`
        : `OneConetec evaluó ${scored === 1 ? 'la foto que coincide' : `las ${scored.toLocaleString()} fotos que coinciden`} con alguna palabra de tu búsqueda.`,
    textOrderNote: 'OneConetec no pudo evaluarlas ahora; están ordenadas por cuántas de tus palabras contienen.',
    noMatches: 'Ninguna foto coincide con esas palabras. Prueba otras, o menos.',
    match: (score) => `${score}% de coincidencia`,
    sortLabel: 'Ordenar por',
    sort: {
      relevance: 'Mejor coincidencia',
      posted: 'Fecha de publicación',
      taken: 'Fecha de la foto',
      downloads: 'Más descargadas',
      winners: 'Ganadoras primero',
      resolution: 'Más grandes primero',
    },
    filterLabel: 'Lo que hay en la foto',
    clearFilters: 'Quitar filtros',
    filtersButton: 'Filtros',
    dateLabel: 'Publicada',
    datePreset: { any: 'Cualquier fecha', d7: 'Últimos 7 días', d30: 'Últimos 30 días', y1: 'Últimos 12 meses', custom: 'Fechas a elegir…' },
    dateFrom: 'Desde',
    dateTo: 'Hasta',
    downloadsShort: (n) => (n === 0 ? 'Sin descargas' : `${n.toLocaleString()} ${n === 1 ? 'descarga' : 'descargas'}`),
    facet: { brand: 'Marca', equipment: 'Equipo', sector: 'Sector / sitio', feature: 'En la foto' },
    describing: (n) => `OneConetec está describiendo ${n} ${n === 1 ? 'foto nueva' : 'fotos nuevas'}…`,
    fullSizeBadge: 'Tamaño completo',
    wonBadge: 'Ganadora',
    photo: 'Foto',
    previous: 'Foto anterior',
    next: 'Foto siguiente',
    showFullSize: 'Ver en tamaño completo',
    showingFullSize: 'Mostrando la copia en tamaño completo',
    description: 'Descripción',
    byOneConetec: 'Escrita por OneConetec a partir de la foto. Corrígela si está mal.',
    byPerson: 'Escrita o corregida por una persona.',
    noDescription: 'OneConetec todavía no ha descrito esta foto.',
    edit: 'Editar',
    save: 'Guardar',
    cancel: 'Cancelar',
    english: 'Inglés',
    spanish: 'Español',
    askAgain: 'Pedirle a OneConetec otra vez',
    asking: 'OneConetec está mirando la foto…',
    whatIsInIt: 'Qué hay en la foto',
    suggested: (pct) => `OneConetec lo sugiere (${pct}% de seguridad). ¿Está en la foto?`,
    yes: 'Sí',
    no: 'No está',
    notInPhoto: 'No está en esta foto',
    add: 'Agregar…',
    addNew: 'Algo nuevo…',
    newLabelEn: 'Nuevo valor (inglés)',
    newLabelEs: 'Español',
    people: 'Personas',
    nobodyTagged: 'No hay nadie etiquetado.',
    consent: (tagged) =>
      (tagged === 0
        ? 'Quien la publicó le dio licencia de uso a la empresa.'
        : `Quien la publicó le dio licencia; ${tagged === 1 ? 'el colega etiquetado no se ha opuesto' : `ninguno de los ${tagged} colegas etiquetados se ha opuesto`}.`) +
      ' A quien aparece sin estar etiquetado no se le ha preguntado.',
    details: 'Detalles',
    postedBy: 'Publicada por',
    posted: 'Publicada',
    taken: 'Tomada',
    notRecorded: 'No registrada',
    feedCopy: 'Copia del feed',
    fullSize: 'Tamaño completo',
    noFullSize: 'No se guardó — se publicó antes de que se guardaran copias completas, o la subida no terminó.',
    printsAt: (w, h) => `se imprime a ${w} × ${h} pulg. a 300 ppp`,
    orientation: { landscape: 'Horizontal', portrait: 'Vertical', square: 'Cuadrada' },
    hashtags: 'Etiquetas #',
    wins: 'Concursos',
    place: (n) => `${n}.º lugar`,
    downloads: 'Descargas',
    neverDownloaded: 'Aún no se ha descargado',
    downloadedTimes: (n) => `Descargada ${n.toLocaleString()} ${n === 1 ? 'vez' : 'veces'}`,
    whoDownloaded: 'Quién la descargó',
    fullTag: 'tamaño completo',
    useFor: '¿Para qué es?',
    useLabel: { marketing: 'Materiales de marketing', sales: 'Ventas', other: 'Capacitación y otros' },
    usesLine: (ok, out) =>
      (andList(ok, 'y') ? `Se puede usar para ${andList(ok, 'y')}.` : 'No está disponible para ninguna descarga.') +
      (andList(out, 'y') ? ` Quien la publicó la excluyó de ${andList(out, 'y')}.` : ''),
    reasonLine: (r) => `Su motivo: «${r}»`,
    where: 'Dónde',
    whereNotSent: 'No se envió',
    wherePending: 'OneConetec aún no ha nombrado el lugar',
    coordinates: (lat, lng) =>
      [lat, lng].map((x) => Number(x).toLocaleString('es-CL', { minimumFractionDigits: 5, maximumFractionDigits: 5 })).join('; '),
    site: 'Sitio',
    siteSuggested: (pct) => `Suposición de OneConetec, ${pct}% de certeza`,
    siteYes: 'Sí, es ese',
    siteNo: 'No es este sitio',
    siteRename: 'Nombrarlo',
    siteNone: 'Sin nombre',
    siteConfirmedBy: (n) => (n ? `Confirmado por ${n}.` : 'Confirmado.'),
    siteNoneSaid: (n) => (n ? `No es un sitio con nombre — ${n}.` : 'No es un sitio con nombre.'),
    siteOneUnsure: 'OneConetec no pudo saber qué sitio es.',
    siteNotYet: 'OneConetec todavía no lo ha mirado.',
    siteChange: 'Cambiar',
    sitePick: 'Busca un sitio o escribe uno nuevo',
    sitePhotos: (n) => (n === 1 ? '1 foto' : `${n.toLocaleString()} fotos`),
    siteFailed: 'No se pudo guardar el sitio.',
    download: 'Descargar',
    downloadFull: 'Descargar tamaño completo',
    downloadFeed: 'Descargar 2.000 px',
    downloading: 'Descargando…',
    starsPaid: (stars) => (stars ? `${stars.toLocaleString()} Stars pagadas` : 'Stars pagadas'),
    outcome: {
      awarded: (name, stars) => `Descargada. ${name} ganó ${stars.toLocaleString()} Stars.`,
      duplicate: 'Descargada. Esta foto ya había ganado sus Stars.',
      own_photo: 'Descargada. Tu propia foto no te da Stars.',
      byline: 'Descargada. Una foto publicada a nombre de un área, como Comunicaciones, no le da Stars a nadie.',
      capped: (name) => `Descargada. ${name} llegó al límite del mes por fotos usadas, así que esta vez no hay Stars.`,
      rule_off: 'Descargada.',
      not_member: (name) => `Descargada. ${name} ya no está en la empresa, así que no hay Stars.`,
      failed: 'Descargada. No se pudieron pagar las Stars ahora; quedó registrado.',
    },
    plainDownloaded: 'Descargada.',
    notSaved: 'La descarga quedó registrada, pero el archivo no llegó. Inténtalo de nuevo — no pagará dos veces.',
    withdrawn: 'Esa foto ya no está en la sala — se retiró su permiso. No se descargó nada.',
    couldNotLoad: 'No se pudo cargar la sala de fotos en este momento.',
    couldNotSave: 'No se pudo guardar en este momento.',
    contestsLede:
      'Un concurso es un rango de fechas. Sus fotos son las de la sala publicadas en ese rango — inscribirse es la casilla que se marca al publicar. Elige a los ganadores y cada uno recibe un aviso y las Stars de su lugar. La elección es definitiva.',
    newContest: 'Nuevo concurso',
    contestTitle: 'Título',
    defaultTitle: (month) => `Concurso de fotos — ${month}`,
    from: 'Desde',
    to: 'Hasta',
    timezone: 'Los días se leen en la zona horaria',
    places: 'Lugares y Stars',
    starsFor: (place) => `Stars del ${place}`,
    addPlace: 'Agregar un lugar',
    removePlace: 'Quitar el último lugar',
    saveContest: 'Guardar concurso',
    noContests: 'Todavía no hay concursos.',
    contestRange: (from, to) => `${from} a ${to}`,
    entries: (n) => `${n.toLocaleString()} ${n === 1 ? 'foto' : 'fotos'}`,
    statusOpen: 'Sin evaluar',
    statusJudged: (date, name) => `Evaluado el ${date} por ${name}`,
    statusCancelled: 'Cancelado',
    openContest: 'Elegir ganadores',
    editContest: 'Editar',
    cancelContest: 'Cancelar concurso',
    confirmCancel: '¿Cancelar este concurso? Ya no se podrá evaluar.',
    allContests: 'Todos los concursos',
    choosePlace: 'Lugar',
    noPlace: '— ninguno —',
    awardButton: (n) => `Premiar a ${n} ${n === 1 ? 'ganador' : 'ganadores'}`,
    awardTitle: 'Premiar a los ganadores',
    awardFinal: 'Es definitivo. Cada ganador recibe ahora un aviso y las Stars de su lugar, y el concurso ya no se puede cambiar.',
    awardConfirm: 'Premiar',
    awardResult: (name, place, stars, told) =>
      `${place}: ${name}${stars > 0 ? ` — ${stars.toLocaleString()} Stars` : ''}${told ? ', avisado' : ', sin aviso'}`,
    awardNoStars: (reason) => `sin Stars (${reason})`,
    ownPhotoNote: 'Tu propia foto — otro jurado tiene que elegirla.',
    entriesShown: (shown, total) => `Mostrando ${shown.toLocaleString()} de ${total.toLocaleString()} fotos`,
    noEntries: 'Ninguna foto de la sala se publicó en este rango.',
    winners: 'Ganadores',
    // 304 (FED-71): agregar fotos directamente a la galería
    addPhotos: 'Agregar fotos',
    addTitle: 'Agregar fotos a la galería',
    addIntro: [
      'Las fotos que agregas aquí entran directamente a la galería: no se publican en el feed y no ganan estrellas. Cada una queda autorizada para uso de la empresa (marketing, ventas, capacitación y otros usos) y no participa en el concurso mensual de fotos.',
      'Cualquier persona que etiquetes después en una foto es avisada, puede quitar la etiqueta y puede pedir una revisión, igual que con una foto del feed.',
      'OneConetec lee cada foto cuando entra y sugiere qué hay en ella, para que los colegas puedan encontrarla.',
    ],
    dropHere: 'Arrastra fotos o una carpeta aquí, o',
    choosePhotos: 'Elegir fotos',
    chooseFolder: 'Elegir una carpeta',
    acceptNote: 'JPEG, PNG o WebP. Una carpeta se lee con sus subcarpetas. Las fotos que ya están en la galería se omiten, así que un lote que detuviste se puede volver a elegir.',
    forEvery: 'Para todas las fotos de este lote',
    takenLabel: 'Tomada',
    takenHint: 'Para fotos sin fecha propia: un año, un mes o un día — 1998, 1998-06 o 1998-06-15.',
    takenBad: 'Escribe un año, un mes o un día: 1998, 1998-06 o 1998-06-15.',
    brandLabel: 'Marca',
    noBrand: 'Sin marca',
    tagsLabel: 'Etiquetar también con',
    brandPick: 'Buscar una marca',
    tagsFilter: 'Buscar una etiqueta',
    captionFromName: 'Usar el nombre del archivo como leyenda cuando no se escriba una',
    captionPlaceholder: 'Leyenda (opcional)',
    removeRow: 'Quitar de este lote',
    status: {
      waiting: 'Lista',
      known: 'Ya está en la galería',
      uploading: 'Agregando…',
      added: 'Agregada',
      failed: 'Falló',
      stopped: 'No agregada',
    },
    counts: (waiting, known, failed) =>
      [
        `${waiting.toLocaleString()} por agregar`,
        known > 0 ? `${known.toLocaleString()} ya aquí` : null,
        failed > 0 ? `${failed.toLocaleString()} fallaron` : null,
      ]
        .filter(Boolean)
        .join(' · '),
    addCount: (n) => (n === 1 ? 'Agregar 1 foto' : `Agregar ${n.toLocaleString()} fotos`),
    hashing: (done, total) => `Revisando cuáles ya están aquí: ${done.toLocaleString()} de ${total.toLocaleString()}…`,
    adding: (done, total) => `Agregando ${done.toLocaleString()} de ${total.toLocaleString()}…`,
    reading: (done, total) =>
      `OneConetec está leyendo las fotos: ${done.toLocaleString()} de ${total.toLocaleString()}. Puedes cerrar esto; el resto se lee la próxima vez que se abra la galería.`,
    summary: (added, known, failed) =>
      `${added.toLocaleString()} agregadas` +
      (known > 0 ? `, ${known.toLocaleString()} ya estaban` : '') +
      (failed > 0 ? `, ${failed.toLocaleString()} fallaron` : '') +
      '.',
    stop: 'Detener',
    stopped: 'Detenido: las fotos ya agregadas se quedan.',
    retryFailed: 'Reintentar las que fallaron',
    close: 'Cerrar',
    exifTaken: (when) => `Tomada ${when} (cámara)`,
    willBeDated: (when) => `Quedará fechada ${when}`,
    noExif: 'Sin fecha en el archivo',
    addedBy: 'Agregada a la galería por',
    addedOn: 'Agregada',
    fileName: 'Archivo',
    removeFromGallery: 'Quitar de la galería',
    removeConfirm: '¿Quitar esta foto de la galería? Después no se muestra en ningún lado. Quien esté etiquetado conserva su aviso.',
    removeYes: 'Quitar',
    removeNo: 'Dejarla',
    removedNote: 'Quitada de la galería.',
    tagPeople: 'Etiquetar personas',
    unreadLine: (n) => `OneConetec aún no ha leído ${n.toLocaleString()} ${n === 1 ? 'foto' : 'fotos'}.`,
    readRest: 'Leerlas ahora',
    readingRest: (done, total) => `OneConetec está leyendo el resto: ${done.toLocaleString()} de ${total.toLocaleString()}…`,
    readStop: 'Detener',
  },
}

/* ── PHOTO CONTESTS (147, CMP-6) ───────────────────────────────────────────── */

export type ContestsCopy = {
  eyebrow: string
  lede: string
  back: string
  /** The Photos sub-nav (David, 10 Oct): Contests and Gallery under Photos. */
  photosNav: string
  tabContests: string
  tabGallery: string
  /** The page's ? menu, and the organiser/judge side panel. */
  howItWorks: string
  howTitle: string
  monthlyRunning: string
  /** The Manage side tray and its collapsible sections (David, 10 Oct). */
  manage: string
  manageTitle: string
  manageContest: string
  newSummary: string
  secMonthly: string
  monthlyNotRunning: string
  secYours: string
  yoursSummary: (n: number) => string
  secDetails: string
  detailsSummary: string
  secFeed: string
  feedOn: string
  feedOff: string
  judgeSummary: (toConfirm: boolean) => string
  /** 151. How long the monthly vote runs; announcing in the feed. */
  votingRuns: string
  voteWholeMonth: string
  voteFirstDays: string
  daysUnit: string
  monthlyVotes: (days: number | null) => string
  announceInFeed: string
  announceHint: string
  monthlyAnnounces: string
  seePost: string
  feedEyebrow: string
  seeAllPhotos: string
  /** Entering behind a button; voting buttons that say why they are off. */
  submitPhotos: string
  voteReasonMine: string
  voteReasonJudge: string
  voteReasonNotOpen: (opens: string) => string
  voteReasonClosed: string
  voteReasonUsed: string
  votesStatus: (left: number, of: number) => string
  votesOpenOn: (opens: string, of: number) => string
  /** A contest tile's corner: where it is, in a word. */
  phaseTag: Record<'upcoming' | 'entering' | 'voting' | 'confirming' | 'final' | 'cancelled', string>
  allContests: string
  empty: string
  loading: string
  couldNotLoad: string
  monthlyName: (title: string, month: string) => string
  kindVote: string
  kindJudged: string
  monthlyTag: string
  // where a contest is
  upcoming: (opens: string) => string
  entering: (closes: string) => string
  enteringMonthly: (voteOpens: string) => string
  voting: (closes: string) => string
  confirmingVote: string
  confirmingJudged: string
  final: (on: string) => string
  cancelled: string
  prize: (stars: number, text: string | null) => string
  judgesAwardPrize: (stars: number) => string
  counts: (entries: number, votes: number | null) => string
  judgedBy: (names: string) => string
  notShown: string
  /** CMP-8 — the page's explanations, behind one button */
  showDetails: string
  detailsTitle: string
  // what the reader can do
  enterCta: string
  voteCta: string
  winnersCta: string
  photosCta: string
  confirmCta: string
  votesLeft: (n: number) => string
  votesUsed: string
  monthlyHow: string
  /** 12 Oct (David): the details dialog describes THIS contest's kind, not
   *  the photo of the month on every contest. */
  aboutContest: (monthly: boolean, everyoneVotes: boolean) => string
  // the gallery
  voteFor: string
  youVoted: string
  takeBack: string
  yourPhoto: string
  votes: (n: number) => string
  placeName: (rank: number) => string
  judgesAward: (rank: number, many: boolean) => string
  postedBy: (name: string) => string
  openPost: string
  /** The tile's name: it opens the contest viewer (11 Oct). */
  viewPhoto: (i: number, n: number) => string
  shown: (shown: number, total: number) => string
  showMore: string
  noPhotos: string
  // entering
  enterLede: (max: number, since: string) => string
  enterNone: string
  enterButton: string
  withdrawButton: string
  entered: string
  enteredCount: (n: number, max: number) => string
  // judging
  judgeHeading: string
  judgeLede: string
  disqualify: string
  reinstate: string
  disqualified: string
  reason: string
  reasons: Record<'off_objective' | 'not_our_work' | 'safety' | 'quality' | 'other', string>
  note: string
  noteRequired: string
  disqualifyNote: string
  disqualifyConfirm: string
  // confirming the results
  confirmHeading: string
  confirmLedeVote: string
  confirmLedeJudged: string
  tiedNote: string
  moveUp: string
  noVotes: string
  pickJudgesAward: (rank: number) => string
  pickPlace: (rank: number) => string
  nobody: string
  ownPhoto: string
  confirmFinal: string
  confirmButton: string
  resultLine: (label: string, name: string, stars: number, told: boolean) => string
  noStars: (why: string) => string
  // organising
  organise: string
  newContest: string
  startMonthly: string
  edit: string
  cancelContest: string
  confirmCancel: string
  formKind: string
  formWordsEn: string
  formWordsEs: string
  formTitle: string
  formObjective: string
  formPrize: string
  formEntriesOpen: string
  formEntriesClose: string
  formVotingCloses: string
  formPhotosSince: string
  formTimezone: string
  formVotesPerPerson: string
  formMaxEntries: string
  formPlaces: string
  formJudgedPlaces: string
  formJudgesAwards: string
  starsFor: (label: string) => string
  addPlace: string
  removePlace: string
  addJudgesAward: string
  removeJudgesAward: string
  formJudges: string
  judgeSearch: string
  addJudge: (name: string) => string
  removeJudge: (name: string) => string
  save: string
  cancel: string
  monthlyDatesNote: string
  monthlySettings: string
  saveVoteDays: string
  endMonthly: string
  confirmEndMonthly: string
  monthlyEnded: string
}

const ordEn = (n: number) => (n === 1 ? '1st' : n === 2 ? '2nd' : n === 3 ? '3rd' : `${n}th`)
const nf = (n: number) => n.toLocaleString()

export const CONTESTS: Record<HubLang, ContestsCopy> = {
  en: {
    eyebrow: 'Photo contest',
    lede:
      'There are two kinds of photo contest. The photo of the month runs every month: every photo posted that month is in unless its poster opted it out, and everyone votes during the next month. Themed contests have their own subject, dates and prizes, and you enter your own photos while entries are open; each one says whether everyone votes or the judges choose. In every contest the judges confirm the results and the winners get stars.',
    back: 'All contests',
    photosNav: 'Photos',
    tabContests: 'Contests',
    tabGallery: 'Gallery',
    howItWorks: 'How it works',
    howTitle: 'How photo contests work',
    phaseTag: { upcoming: 'Upcoming', entering: 'Open', voting: 'Voting', confirming: 'Closing', final: 'Results', cancelled: 'Cancelled' },
    monthlyRunning: 'The monthly contest is running. Change or stop it from its own page.',
    manage: 'Manage',
    manageTitle: 'Manage contests',
    manageContest: 'Manage this contest',
    newSummary: 'Words, dates, prizes and judges',
    secMonthly: 'The monthly contest',
    monthlyNotRunning: 'Not running — start it here',
    secYours: 'Contests you organise',
    yoursSummary: (n) => `${n} still open`,
    secDetails: 'Details',
    detailsSummary: 'Words, dates, prizes and judges',
    secFeed: 'Winners in the feed',
    feedOn: 'Posted when a judge confirms',
    feedOff: 'Not posted',
    judgeSummary: (toConfirm) => (toConfirm ? 'The results are waiting for you to confirm' : 'Disqualify from the photos; confirm once voting closes'),
    votingRuns: 'Voting runs',
    voteWholeMonth: 'The whole next month',
    voteFirstDays: 'Only the first days',
    daysUnit: 'days',
    monthlyVotes: (days) => (days === null ? 'Votes all next month' : `Votes the first ${days} ${days === 1 ? 'day' : 'days'} of next month`),
    announceInFeed: 'Announce the winners in the feed',
    announceHint: 'When a judge confirms the results, a post congratulates the winners and shows their photos, under that judge\'s name.',
    monthlyAnnounces: 'The winners are posted to the feed, with their photos, when a judge confirms.',
    seePost: 'See the post',
    feedEyebrow: 'Photo contest results',
    seeAllPhotos: 'See every photo',
    submitPhotos: 'Submit your photos',
    voteReasonMine: 'This is your photo — you can\'t vote for it.',
    voteReasonJudge: 'You\'re a judge — judges don\'t vote.',
    voteReasonNotOpen: (opens) => `Voting opens ${opens}.`,
    voteReasonClosed: 'Voting has closed.',
    voteReasonUsed: 'You have used your votes — take one back to move it.',
    votesStatus: (left, of) => `${left} of ${of} ${of === 1 ? 'vote' : 'votes'} left`,
    votesOpenOn: (opens, of) => `${of} ${of === 1 ? 'vote' : 'votes'} each · voting opens ${opens}`,
    showDetails: 'Show details',
    detailsTitle: 'About this contest',
    allContests: 'All contests',
    empty: 'No contests yet.',
    loading: 'Loading…',
    couldNotLoad: 'The contests did not load. Try again in a moment.',
    monthlyName: (title, month) => `${title} — ${month}`,
    kindVote: 'Everyone votes',
    kindJudged: 'The judges choose',
    monthlyTag: 'Every month',
    upcoming: (opens) => `Entries open ${opens}`,
    entering: (closes) => `Entries close ${closes}`,
    enteringMonthly: (voteOpens) => `Every photo posted this month is in, unless its poster opted it out. Voting opens ${voteOpens}.`,
    voting: (closes) => `Voting is open until ${closes}`,
    confirmingVote: 'Voting has closed. The results are on their way.',
    confirmingJudged: 'Entries have closed. The judges are choosing.',
    final: (on) => `Results confirmed ${on}`,
    cancelled: 'Cancelled',
    prize: (stars, text) => (text ? `Prize: ${text} and ${nf(stars)} stars` : `Prize: ${nf(stars)} stars`),
    judgesAwardPrize: (stars) => `Judges' award: ${nf(stars)} stars`,
    counts: (entries, votes) =>
      `${nf(entries)} ${entries === 1 ? 'photo' : 'photos'}` + (votes === null ? '' : ` · ${nf(votes)} ${votes === 1 ? 'vote' : 'votes'}`),
    judgedBy: (names) => `Judges: ${names}`,
    notShown:
      'While voting is open nobody sees who posted a photo or how many votes it has — judges included. The photos are still on the feed with their posters’ names, so this keeps the vote fair rather than secret.',
    enterCta: 'Enter a photo',
    voteCta: 'Vote',
    winnersCta: 'See the winners',
    photosCta: 'See the photos',
    confirmCta: 'Confirm the results',
    votesLeft: (n) => (n === 1 ? 'You have 1 vote left.' : `You have ${n} votes left.`),
    votesUsed: 'You have used your votes. Take one back to move it.',
    monthlyHow: 'You do not enter the monthly contest here: posting a photo enters it, unless you opt it out.',
    aboutContest: (monthly, everyoneVotes) =>
      monthly
        ? 'The photo of the month: every photo posted this month is in unless its poster opted it out. Everyone votes during the next month, and the judges confirm the result.'
        : everyoneVotes
          ? 'A themed contest: you enter your own photos that fit the theme while entries are open, then everyone votes. The judges confirm the result.'
          : 'A themed contest: you enter your own photos that fit the theme while entries are open, and the judges choose the winners.',
    voteFor: 'Vote for this photo',
    youVoted: 'You voted for this',
    takeBack: 'Take my vote back',
    yourPhoto: 'Your photo',
    votes: (n) => (n === 1 ? '1 vote' : `${nf(n)} votes`),
    placeName: (rank) => `${ordEn(rank)} place`,
    judgesAward: (rank, many) => (many ? `Judges' award ${rank}` : "Judges' award"),
    postedBy: (name) => `Posted by ${name}`,
    openPost: 'Open the post',
    viewPhoto: (i, n) => `View photo ${i} of ${n}`,
    shown: (shown, total) => `Showing ${nf(shown)} of ${nf(total)}`,
    showMore: 'Show more',
    noPhotos: 'No photos are in this contest yet.',
    enterLede: (max, since) =>
      `Photos you posted from ${since} on, on posts everyone can see, that you have not opted out of photo contests. Up to ${max} from each person.`,
    enterNone: 'None of your photos can go in yet. Post one to the whole company, then come back.',
    enterButton: 'Enter',
    withdrawButton: 'Take it out',
    entered: 'Entered',
    enteredCount: (n, max) => `You have entered ${n} of ${max}.`,
    judgeHeading: 'You are a judge',
    judgeLede:
      'You see who posted each photo. You do not vote, and you cannot rule on your own photo. Counts appear once voting closes, so you can confirm the results.',
    disqualify: 'Disqualify',
    reinstate: 'Put it back',
    disqualified: 'Disqualified',
    reason: 'Why',
    reasons: {
      off_objective: 'Not what this contest asked for',
      not_our_work: 'Not our work',
      safety: 'A safety concern in the photo',
      quality: 'Quality',
      other: 'Something else',
    },
    note: 'Note to the person who posted it',
    noteRequired: 'Say why in the note.',
    disqualifyNote: 'The person who posted it gets a bell with your reason. Nobody else sees it. Votes for it are returned to the voters.',
    disqualifyConfirm: 'Disqualify the photo',
    confirmHeading: 'Confirm the results',
    confirmLedeVote:
      'The vote decides the places. Where photos are level on votes you choose the order; then choose the judges’ award from the photos the vote did not place.',
    confirmLedeJudged: 'Choose a photo for each place.',
    tiedNote: 'Level on votes — you choose the order.',
    moveUp: 'Move up',
    noVotes: 'No photo has a vote, so there is no vote place to give.',
    pickJudgesAward: (rank) => `Judges' award ${rank}`,
    pickPlace: (rank) => `${ordEn(rank)} place`,
    nobody: 'Nobody',
    ownPhoto: 'Your own photo — another judge has to confirm this.',
    confirmFinal:
      'This is final. Each winner is paid their stars and gets a bell now, the winners go on the hub for a week, and every individual vote is deleted — only the counts are kept.',
    confirmButton: 'Confirm and announce',
    resultLine: (label, name, stars, told) => `${label}: ${name}${stars > 0 ? ` — ${nf(stars)} stars` : ''}${told ? ', told' : ', not told'}`,
    noStars: (why) => `no stars (${why})`,
    organise: 'Organise',
    newContest: 'New contest',
    startMonthly: 'Start the monthly contest',
    edit: 'Edit',
    cancelContest: 'Cancel the contest',
    confirmCancel: 'Cancel this contest? Its votes are deleted and it cannot be confirmed afterwards.',
    formKind: 'How the winners are chosen',
    formWordsEn: 'English',
    formWordsEs: 'Spanish',
    formTitle: 'Title',
    formObjective: 'Objective (what to photograph)',
    formPrize: 'Prize, besides the stars (optional)',
    formEntriesOpen: 'Entries open',
    formEntriesClose: 'Entries close',
    formVotingCloses: 'Voting closes',
    formPhotosSince: 'Photos posted from',
    formTimezone: 'Time zone',
    formVotesPerPerson: 'Votes per person',
    formMaxEntries: 'Photos per person',
    formPlaces: 'Places, by votes',
    formJudgedPlaces: 'Places',
    formJudgesAwards: "Judges' awards",
    starsFor: (label) => `${label} — stars`,
    addPlace: 'Add a place',
    removePlace: 'Remove the last place',
    addJudgesAward: "Add a judges' award",
    removeJudgesAward: "Remove the last judges' award",
    formJudges: 'Judges',
    judgeSearch: 'Add a judge — start typing a name',
    addJudge: (name) => `Add ${name}`,
    removeJudge: (name) => `Remove ${name}`,
    save: 'Save',
    cancel: 'Cancel',
    monthlyDatesNote:
      'A month of the monthly contest takes its dates from the series. Changes to its words, places and judges carry on into the next months.',
    monthlySettings: 'The monthly contest',
    saveVoteDays: 'Save for next month',
    endMonthly: 'Stop the monthly contest',
    confirmEndMonthly: 'Stop the monthly contest? No new month starts; the months already running finish.',
    monthlyEnded: 'The monthly contest is stopped.',
  },
  es: {
    eyebrow: 'Concurso de fotos',
    lede:
      'Hay dos tipos de concurso de fotos. La foto del mes se hace cada mes: participa toda foto publicada ese mes, salvo que quien la publicó la haya excluido, y todos votan durante el mes siguiente. Los concursos temáticos tienen su propio tema, fechas y premios, y tú inscribes tus fotos mientras las inscripciones están abiertas; cada uno indica si votan todos o eligen los jueces. En todos los concursos los jueces confirman los resultados y los ganadores reciben estrellas.',
    back: 'Todos los concursos',
    photosNav: 'Fotos',
    tabContests: 'Concursos',
    tabGallery: 'Galería',
    howItWorks: 'Cómo funciona',
    howTitle: 'Cómo funcionan los concursos de fotos',
    phaseTag: { upcoming: 'Próximo', entering: 'Abierto', voting: 'Votación', confirming: 'Cerrando', final: 'Resultados', cancelled: 'Cancelado' },
    monthlyRunning: 'El concurso mensual está en marcha. Cámbialo o deténlo desde su propia página.',
    manage: 'Administrar',
    manageTitle: 'Administrar concursos',
    manageContest: 'Administrar este concurso',
    newSummary: 'Textos, fechas, premios y jurado',
    secMonthly: 'El concurso mensual',
    monthlyNotRunning: 'No está en marcha: inícialo aquí',
    secYours: 'Concursos que organizas',
    yoursSummary: (n) => `${n} abiertos`,
    secDetails: 'Detalles',
    detailsSummary: 'Textos, fechas, premios y jurado',
    secFeed: 'Ganadores en el feed',
    feedOn: 'Se publican cuando un jurado confirma',
    feedOff: 'No se publican',
    judgeSummary: (toConfirm) => (toConfirm ? 'Los resultados esperan tu confirmación' : 'Descalifica desde las fotos; confirma al cerrar la votación'),
    votingRuns: 'La votación dura',
    voteWholeMonth: 'Todo el mes siguiente',
    voteFirstDays: 'Solo los primeros días',
    daysUnit: 'días',
    monthlyVotes: (days) => (days === null ? 'Se vota todo el mes siguiente' : `Se vota los primeros ${days} ${days === 1 ? 'día' : 'días'} del mes siguiente`),
    announceInFeed: 'Anunciar los ganadores en el feed',
    announceHint: 'Cuando un jurado confirma los resultados, una publicación felicita a los ganadores y muestra sus fotos, con el nombre de ese jurado.',
    monthlyAnnounces: 'Los ganadores se publican en el feed, con sus fotos, cuando un jurado confirma.',
    seePost: 'Ver la publicación',
    feedEyebrow: 'Resultados del concurso de fotos',
    seeAllPhotos: 'Ver todas las fotos',
    submitPhotos: 'Inscribe tus fotos',
    voteReasonMine: 'Es tu foto: no puedes votar por ella.',
    voteReasonJudge: 'Eres jurado: el jurado no vota.',
    voteReasonNotOpen: (opens) => `La votación abre el ${opens}.`,
    voteReasonClosed: 'La votación cerró.',
    voteReasonUsed: 'Ya usaste tus votos: retira uno para cambiarlo.',
    votesStatus: (left, of) => `Te quedan ${left} de ${of} ${of === 1 ? 'voto' : 'votos'}`,
    votesOpenOn: (opens, of) => `${of} ${of === 1 ? 'voto' : 'votos'} por persona · la votación abre el ${opens}`,
    showDetails: 'Ver detalles',
    detailsTitle: 'Sobre este concurso',
    allContests: 'Todos los concursos',
    empty: 'Todavía no hay concursos.',
    loading: 'Cargando…',
    couldNotLoad: 'Los concursos no se cargaron. Inténtalo de nuevo en un momento.',
    monthlyName: (title, month) => `${title}, ${month}`,
    kindVote: 'Votan todos',
    kindJudged: 'Elige el jurado',
    monthlyTag: 'Cada mes',
    upcoming: (opens) => `Las inscripciones abren el ${opens}`,
    entering: (closes) => `Las inscripciones cierran el ${closes}`,
    enteringMonthly: (voteOpens) => `Participa toda foto publicada este mes, salvo las excluidas por quien las publicó. La votación abre el ${voteOpens}.`,
    voting: (closes) => `La votación está abierta hasta el ${closes}`,
    confirmingVote: 'La votación cerró. Los resultados vienen en camino.',
    confirmingJudged: 'Las inscripciones cerraron. El jurado está eligiendo.',
    final: (on) => `Resultados confirmados el ${on}`,
    cancelled: 'Cancelado',
    prize: (stars, text) => (text ? `Premio: ${text} y ${nf(stars)} Stars` : `Premio: ${nf(stars)} Stars`),
    judgesAwardPrize: (stars) => `Premio del jurado: ${nf(stars)} Stars`,
    counts: (entries, votes) =>
      `${nf(entries)} ${entries === 1 ? 'foto' : 'fotos'}` + (votes === null ? '' : ` · ${nf(votes)} ${votes === 1 ? 'voto' : 'votos'}`),
    judgedBy: (names) => `Jurado: ${names}`,
    notShown:
      'Mientras la votación está abierta nadie ve quién publicó una foto ni cuántos votos tiene, tampoco el jurado. Las fotos siguen en el feed con el nombre de quien las publicó: esto hace la votación justa, no secreta.',
    enterCta: 'Inscribir una foto',
    voteCta: 'Votar',
    winnersCta: 'Ver los ganadores',
    photosCta: 'Ver las fotos',
    confirmCta: 'Confirmar los resultados',
    votesLeft: (n) => (n === 1 ? 'Te queda 1 voto.' : `Te quedan ${n} votos.`),
    votesUsed: 'Usaste tus votos. Retira uno para cambiarlo.',
    monthlyHow: 'El concurso del mes no se inscribe aquí: publicar una foto la inscribe, salvo que la excluyas.',
    aboutContest: (monthly, everyoneVotes) =>
      monthly
        ? 'La foto del mes: participa toda foto publicada este mes, salvo que quien la publicó la haya excluido. Todos votan durante el mes siguiente y los jueces confirman el resultado.'
        : everyoneVotes
          ? 'Un concurso temático: inscribes tus propias fotos que encajen con el tema mientras las inscripciones están abiertas, y luego votan todos. Los jueces confirman el resultado.'
          : 'Un concurso temático: inscribes tus propias fotos que encajen con el tema mientras las inscripciones están abiertas, y los jueces eligen a los ganadores.',
    voteFor: 'Votar por esta foto',
    youVoted: 'Votaste por esta',
    takeBack: 'Retirar mi voto',
    yourPhoto: 'Tu foto',
    votes: (n) => (n === 1 ? '1 voto' : `${nf(n)} votos`),
    placeName: (rank) => `${rank}.º lugar`,
    judgesAward: (rank, many) => (many ? `Premio del jurado ${rank}` : 'Premio del jurado'),
    postedBy: (name) => `Publicada por ${name}`,
    openPost: 'Abrir la publicación',
    viewPhoto: (i, n) => `Ver la foto ${i} de ${n}`,
    shown: (shown, total) => `Mostrando ${nf(shown)} de ${nf(total)}`,
    showMore: 'Mostrar más',
    noPhotos: 'Todavía no hay fotos en este concurso.',
    enterLede: (max, since) =>
      `Fotos que publicaste desde el ${since}, en publicaciones que todos pueden ver, que no excluiste de los concursos. Hasta ${max} por persona.`,
    enterNone: 'Todavía ninguna de tus fotos puede participar. Publica una para toda la empresa y vuelve.',
    enterButton: 'Inscribir',
    withdrawButton: 'Retirarla',
    entered: 'Inscrita',
    enteredCount: (n, max) => `Inscribiste ${n} de ${max}.`,
    judgeHeading: 'Eres jurado',
    judgeLede:
      'Ves quién publicó cada foto. No votas y no puedes decidir sobre tu propia foto. Los conteos aparecen cuando cierra la votación, para que confirmes los resultados.',
    disqualify: 'Descalificar',
    reinstate: 'Volver a incluirla',
    disqualified: 'Descalificada',
    reason: 'Motivo',
    reasons: {
      off_objective: 'No es lo que pedía este concurso',
      not_our_work: 'No es trabajo nuestro',
      safety: 'Un problema de seguridad en la foto',
      quality: 'Calidad',
      other: 'Otro motivo',
    },
    note: 'Nota para quien la publicó',
    noteRequired: 'Explica el motivo en la nota.',
    disqualifyNote: 'Quien la publicó recibe un aviso con tu motivo. Nadie más lo ve. Los votos que tenía vuelven a quienes votaron.',
    disqualifyConfirm: 'Descalificar la foto',
    confirmHeading: 'Confirmar los resultados',
    confirmLedeVote:
      'La votación decide los lugares. Donde hay empate en votos eliges el orden; luego elige el premio del jurado entre las fotos que la votación no premió.',
    confirmLedeJudged: 'Elige una foto para cada lugar.',
    tiedNote: 'Empate en votos: tú eliges el orden.',
    moveUp: 'Subir',
    noVotes: 'Ninguna foto tiene votos, así que no hay lugar por votación que entregar.',
    pickJudgesAward: (rank) => `Premio del jurado ${rank}`,
    pickPlace: (rank) => `${rank}.º lugar`,
    nobody: 'Nadie',
    ownPhoto: 'Tu propia foto: otro jurado tiene que confirmar esto.',
    confirmFinal:
      'Esto es definitivo. Cada ganador recibe sus Stars y un aviso ahora, los ganadores aparecen en el portal durante una semana y se borra cada voto individual; solo quedan los conteos.',
    confirmButton: 'Confirmar y anunciar',
    resultLine: (label, name, stars, told) => `${label}: ${name}${stars > 0 ? ` — ${nf(stars)} Stars` : ''}${told ? ', avisado' : ', sin aviso'}`,
    noStars: (why) => `sin Stars (${why})`,
    organise: 'Organizar',
    newContest: 'Nuevo concurso',
    startMonthly: 'Iniciar el concurso del mes',
    edit: 'Editar',
    cancelContest: 'Cancelar el concurso',
    confirmCancel: '¿Cancelar este concurso? Se borran sus votos y no se podrá confirmar después.',
    formKind: 'Cómo se eligen los ganadores',
    formWordsEn: 'Inglés',
    formWordsEs: 'Español',
    formTitle: 'Título',
    formObjective: 'Objetivo (qué fotografiar)',
    formPrize: 'Premio, además de las Stars (opcional)',
    formEntriesOpen: 'Las inscripciones abren',
    formEntriesClose: 'Las inscripciones cierran',
    formVotingCloses: 'La votación cierra',
    formPhotosSince: 'Fotos publicadas desde',
    formTimezone: 'Zona horaria',
    formVotesPerPerson: 'Votos por persona',
    formMaxEntries: 'Fotos por persona',
    formPlaces: 'Lugares, por votos',
    formJudgedPlaces: 'Lugares',
    formJudgesAwards: 'Premios del jurado',
    starsFor: (label) => `${label}: Stars`,
    addPlace: 'Agregar un lugar',
    removePlace: 'Quitar el último lugar',
    addJudgesAward: 'Agregar un premio del jurado',
    removeJudgesAward: 'Quitar el último premio del jurado',
    formJudges: 'Jurado',
    judgeSearch: 'Agrega a alguien al jurado: escribe un nombre',
    addJudge: (name) => `Agregar a ${name}`,
    removeJudge: (name) => `Quitar a ${name}`,
    save: 'Guardar',
    cancel: 'Cancelar',
    monthlyDatesNote:
      'Un mes del concurso del mes toma sus fechas de la serie. Los cambios a textos, lugares y jurado siguen en los meses siguientes.',
    monthlySettings: 'El concurso del mes',
    saveVoteDays: 'Guardar para el próximo mes',
    endMonthly: 'Detener el concurso del mes',
    confirmEndMonthly: '¿Detener el concurso del mes? No empieza un mes nuevo; los meses en curso terminan.',
    monthlyEnded: 'El concurso del mes está detenido.',
  },
}

/* ── 152: photo review, and the Gallery's browser view ──────────────────────── */

type ReviewReasonKey = 'no_consent' | 'unsafe' | 'identifiable' | 'other'
type ReviewOutcomeKey = 'keep' | 'restrict' | 'remove' | 'moderation'
type RestrictableUseKey = 'marketing' | 'sales' | 'other'

export type PhotoReviewCopy = {
  requestButton: string
  requested: string
  withdraw: string
  withdrawn: string
  dialogTitle: string
  whyLegend: string
  reason: Record<ReviewReasonKey, string>
  /** Short form, for chips in the queue. */
  reasonShort: Record<ReviewReasonKey, string>
  noteLabel: string
  noteHint: string
  noteRequired: string
  privacy: string
  consentNow: string
  /** 170. The consent reason's window, in the dialog. */
  consentUntil: (date: string) => string
  consentClosed: (date: string) => string
  send: string
  sending: string
  cancel: string
  sent: string
  couldNotSend: string
  reviewPaused: string
  placeLabel: string
  placeNone: string
  reviewsTitle: string
  reviewsNone: string
  queueLink: string
  queueTitle: string
  queueHelpLabel: string
  queueHelp: string[]
  tabOpen: string
  tabClosed: string
  empty: string
  emptyClosed: string
  loading: string
  refused: string
  askedBy: (name: string) => string
  askedOn: (date: string) => string
  postedBy: (name: string) => string
  consentStanding: string
  handedTo: (name: string) => string
  handedToYou: string
  handWho: string
  handNote: string
  handButton: string
  hand: string
  takeBack: string
  giveBack: string
  decideTitle: string
  outcome: Record<ReviewOutcomeKey, string>
  outcomePast: Record<ReviewOutcomeKey, string>
  restrictLegend: string
  useLabel: Record<RestrictableUseKey, string>
  identity: string
  identityNote: string
  outcomeNoteLabel: string
  decide: string
  saving: string
  couldNotSave: string
  decidedBy: (outcome: string, name: string, date: string) => string
  withdrawnLine: string
  moderationOpen: string
  openPost: string
  showMore: string
  shownOf: (shown: number, total: number) => string
}

export const PHOTO_REVIEW: Record<HubLang, PhotoReviewCopy> = {
  en: {
    requestButton: 'Request review',
    requested: 'You asked for a review of this photo.',
    withdraw: 'Withdraw my request',
    withdrawn: 'Request withdrawn.',
    dialogTitle: 'Request a review of this photo',
    whyLegend: 'Why? Pick any that apply.',
    reason: {
      no_consent: 'I’m in this photo and don’t consent to it being used',
      unsafe: 'It shows something unsafe',
      identifiable: 'A client or site can be identified',
      other: 'Something else',
    },
    reasonShort: { no_consent: 'No consent', unsafe: 'Unsafe', identifiable: 'Client or site', other: 'Something else' },
    noteLabel: 'Tell the reviewers more',
    noteHint: 'Optional, unless you picked “Something else”.',
    noteRequired: 'Say what is wrong — it is needed with “Something else”.',
    privacy:
      'The Gallery admins will see your name and reason. The person who posted it will not see who asked. The photo stays up while it is reviewed.',
    consentNow:
      'Because you don’t consent, the photo comes out of the Gallery and every company use except the feed now, and a review cannot put it back unless it isn’t you.',
    consentUntil: (date) => `You can choose this until ${date}.`,
    consentClosed: (date) =>
      `Asking for this photo not to be used was open for 30 days and closed on ${date}. If you still have a concern, choose “Something else” and say why — a reviewer will decide.`,
    send: 'Send for review',
    sending: 'Sending…',
    cancel: 'Cancel',
    sent: 'Sent. The Gallery admins will look at it, and you will hear what they decide.',
    couldNotSend: 'Could not send the request.',
    reviewPaused: 'Under review — downloads are paused until a Gallery admin has looked.',
    placeLabel: 'Place',
    placeNone: 'Not recorded',
    reviewsTitle: 'Review requests',
    reviewsNone: 'No review requests.',
    queueLink: 'Review requests',
    queueTitle: 'Photo review requests',
    queueHelpLabel: 'How reviews work',
    queueHelp: [
      'Anyone who can see a photo can ask for it to be reviewed. A request never removes the photo; downloads are paused until somebody has decided.',
      '“I don’t consent” takes the photo out of every Gallery use at once. You cannot overrule it — only find that it is not the person (a mistaken identity).',
      'Hand a request to a named colleague — an unsafe photo usually goes to an HSE Manager. They see the reasons, not who asked.',
      'The person who asked hears the outcome. The poster hears only if their photo changes, and never who asked.',
    ],
    tabOpen: 'Open',
    tabClosed: 'Closed',
    empty: 'Nothing waiting for review.',
    emptyClosed: 'No closed reviews yet.',
    loading: 'Loading…',
    refused: 'The review queue is for Gallery admins, and for anyone a review was handed to.',
    askedBy: (name) => `Asked by ${name}`,
    askedOn: (date) => `Asked ${date}`,
    postedBy: (name) => `Posted by ${name}`,
    consentStanding: 'Consent withdrawn — out of every Gallery use unless you find it is not them.',
    handedTo: (name) => `Handed to ${name}`,
    handedToYou: 'Handed to you',
    handWho: 'Who',
    handNote: 'Why them?',
    handButton: 'Hand to a colleague',
    hand: 'Hand over',
    takeBack: 'Take it back',
    giveBack: 'Give it back',
    decideTitle: 'Decide',
    outcome: {
      keep: 'Keep it',
      restrict: 'Restrict its uses',
      remove: 'Take it out of the Gallery (it stays on the post)',
      moderation: 'Send it to moderation',
    },
    outcomePast: { keep: 'Kept', restrict: 'Uses restricted', remove: 'Taken out of the Gallery', moderation: 'Sent to moderation' },
    restrictLegend: 'Take away',
    useLabel: { marketing: 'Marketing materials', sales: 'Sales', other: 'Training and other' },
    identity: 'It isn’t them — a mistaken identity',
    identityNote: 'A review cannot overrule a consent withdrawal. Only a mistaken identity lifts it.',
    outcomeNoteLabel: 'Note to the person who asked',
    decide: 'Save the decision',
    saving: 'Saving…',
    couldNotSave: 'Could not save that.',
    decidedBy: (outcome, name, date) => `${outcome} by ${name}, ${date}`,
    withdrawnLine: 'Withdrawn by the person who asked.',
    moderationOpen: 'With the moderators — downloads stay paused.',
    openPost: 'Open the post',
    showMore: 'Show more',
    shownOf: (shown, total) => `Showing ${shown.toLocaleString()} of ${total.toLocaleString()}`,
  },
  es: {
    requestButton: 'Pedir revisión',
    requested: 'Pediste una revisión de esta foto.',
    withdraw: 'Retirar mi solicitud',
    withdrawn: 'Solicitud retirada.',
    dialogTitle: 'Pedir una revisión de esta foto',
    whyLegend: '¿Por qué? Marca todas las que correspondan.',
    reason: {
      no_consent: 'Aparezco en esta foto y no doy mi consentimiento para que se use',
      unsafe: 'Muestra algo inseguro',
      identifiable: 'Se puede identificar a un cliente o una faena',
      other: 'Otra cosa',
    },
    reasonShort: { no_consent: 'Sin consentimiento', unsafe: 'Inseguro', identifiable: 'Cliente o faena', other: 'Otra cosa' },
    noteLabel: 'Cuéntales más a quienes revisan',
    noteHint: 'Opcional, salvo que marques “Otra cosa”.',
    noteRequired: 'Di qué está mal: es necesario con “Otra cosa”.',
    privacy:
      'Los administradores de la Galería verán tu nombre y el motivo. Quien la publicó no verá quién lo pidió. La foto sigue publicada mientras se revisa.',
    consentNow:
      'Como no das tu consentimiento, la foto sale ahora de la Galería y de todo uso de la empresa salvo el feed, y una revisión no puede volver a ponerla salvo que no seas tú.',
    consentUntil: (date) => `Puedes elegir esto hasta el ${date}.`,
    consentClosed: (date) =>
      `Pedir que esta foto no se use estuvo abierto 30 días y cerró el ${date}. Si todavía te preocupa, elige “Otra cosa” y explica por qué: una persona revisora decidirá.`,
    send: 'Enviar a revisión',
    sending: 'Enviando…',
    cancel: 'Cancelar',
    sent: 'Enviado. Los administradores de la Galería la revisarán y te contarán qué deciden.',
    couldNotSend: 'No se pudo enviar la solicitud.',
    reviewPaused: 'En revisión: las descargas están en pausa hasta que un administrador de la Galería la revise.',
    placeLabel: 'Lugar',
    placeNone: 'Sin registrar',
    reviewsTitle: 'Solicitudes de revisión',
    reviewsNone: 'Sin solicitudes de revisión.',
    queueLink: 'Solicitudes de revisión',
    queueTitle: 'Solicitudes de revisión de fotos',
    queueHelpLabel: 'Cómo funcionan las revisiones',
    queueHelp: [
      'Cualquiera que pueda ver una foto puede pedir que se revise. Una solicitud nunca quita la foto; las descargas quedan en pausa hasta que alguien decida.',
      '“No doy mi consentimiento” saca la foto de todo uso de la Galería al instante. No puedes anularlo: solo constatar que no es esa persona (una identidad equivocada).',
      'Entrega una solicitud a un colega con nombre: una foto insegura suele ir a un Gerente de HSE. Ve los motivos, no quién lo pidió.',
      'Quien lo pidió conoce el resultado. Quien la publicó solo se entera si su foto cambia, y nunca de quién lo pidió.',
    ],
    tabOpen: 'Abiertas',
    tabClosed: 'Cerradas',
    empty: 'No hay nada esperando revisión.',
    emptyClosed: 'Aún no hay revisiones cerradas.',
    loading: 'Cargando…',
    refused: 'La cola de revisión es para los administradores de la Galería y para quien recibió una revisión.',
    askedBy: (name) => `Pedida por ${name}`,
    askedOn: (date) => `Pedida el ${date}`,
    postedBy: (name) => `Publicada por ${name}`,
    consentStanding: 'Consentimiento retirado: fuera de todo uso de la Galería salvo que constates que no es esa persona.',
    handedTo: (name) => `Entregada a ${name}`,
    handedToYou: 'Entregada a ti',
    handWho: 'Quién',
    handNote: '¿Por qué esa persona?',
    handButton: 'Entregar a un colega',
    hand: 'Entregar',
    takeBack: 'Recuperarla',
    giveBack: 'Devolverla',
    decideTitle: 'Decidir',
    outcome: {
      keep: 'Mantenerla',
      restrict: 'Restringir sus usos',
      remove: 'Sacarla de la Galería (sigue en la publicación)',
      moderation: 'Enviarla a moderación',
    },
    outcomePast: { keep: 'Mantenida', restrict: 'Usos restringidos', remove: 'Sacada de la Galería', moderation: 'Enviada a moderación' },
    restrictLegend: 'Quitar',
    useLabel: { marketing: 'Materiales de marketing', sales: 'Ventas', other: 'Capacitación y otros' },
    identity: 'No es esa persona: una identidad equivocada',
    identityNote: 'Una revisión no puede anular un retiro de consentimiento. Solo una identidad equivocada lo levanta.',
    outcomeNoteLabel: 'Nota para quien lo pidió',
    decide: 'Guardar la decisión',
    saving: 'Guardando…',
    couldNotSave: 'No se pudo guardar.',
    decidedBy: (outcome, name, date) => `${outcome} por ${name}, ${date}`,
    withdrawnLine: 'Retirada por quien la pidió.',
    moderationOpen: 'Con los moderadores: las descargas siguen en pausa.',
    openPost: 'Abrir la publicación',
    showMore: 'Ver más',
    shownOf: (shown, total) => `Mostrando ${shown.toLocaleString()} de ${total.toLocaleString()}`,
  },
}

export type GalleryBrowseCopy = {
  searchLabel: string
  searchPlaceholder: string
  search: string
  clearSearch: string
  sortLabel: string
  sort: { newest: string; oldest: string }
  loading: string
  empty: string
  noMatches: string
  shownOf: (shown: number, total: number) => string
  showMore: string
  noCaption: string
  openPost: string
  refused: string
  couldNotLoad: string
  helpLabel: string
  helpTitle: string
  help: string[]
  policyLabel: string
  policyNote: string
}

export const GALLERY_BROWSE: Record<HubLang, GalleryBrowseCopy> = {
  en: {
    searchLabel: 'Search the captions',
    searchPlaceholder: 'Search the captions',
    search: 'Search',
    clearSearch: 'Clear search',
    sortLabel: 'Order',
    sort: { newest: 'Newest first', oldest: 'Oldest first' },
    loading: 'Loading the Gallery…',
    empty: 'No photos in the Gallery yet.',
    noMatches: 'No caption has a word of that. Try another.',
    shownOf: (shown, total) => `Showing ${shown.toLocaleString()} of ${total.toLocaleString()}`,
    showMore: 'Show more',
    noCaption: 'No caption',
    openPost: 'Open the post',
    refused: 'The Gallery is not open to you yet.',
    couldNotLoad: 'Could not load the Gallery.',
    helpLabel: 'How it works',
    helpTitle: 'About the Gallery',
    help: [
      'Photos colleagues post to the feed and license for company use. Look, and search their captions; downloading them is for Gallery document users.',
      'Nothing is recorded while you browse.',
      'If a photo should not be here — you are in it and do not consent, it shows something unsafe, or a client can be identified — use Request review under it.',
    ],
    policyLabel: 'Open browsing to every employee',
    policyNote:
      'Every active colleague can then scroll the Gallery at feed size and search captions. No downloads, and nothing recorded. Off again returns it to the Gallery browser role.',
  },
  es: {
    searchLabel: 'Buscar en los pies de foto',
    searchPlaceholder: 'Buscar en los pies de foto',
    search: 'Buscar',
    clearSearch: 'Borrar búsqueda',
    sortLabel: 'Orden',
    sort: { newest: 'Más recientes', oldest: 'Más antiguas' },
    loading: 'Cargando la Galería…',
    empty: 'Aún no hay fotos en la Galería.',
    noMatches: 'Ningún pie de foto tiene esa palabra. Prueba con otra.',
    shownOf: (shown, total) => `Mostrando ${shown.toLocaleString()} de ${total.toLocaleString()}`,
    showMore: 'Ver más',
    noCaption: 'Sin pie de foto',
    openPost: 'Abrir la publicación',
    refused: 'La Galería aún no está abierta para ti.',
    couldNotLoad: 'No se pudo cargar la Galería.',
    helpLabel: 'Cómo funciona',
    helpTitle: 'Sobre la Galería',
    help: [
      'Fotos que los colegas publican en el feed y licencian para uso de la empresa. Mira y busca en sus pies de foto; descargarlas es para usuarios de documentos de la Galería.',
      'No se registra nada mientras miras.',
      'Si una foto no debería estar aquí —apareces y no das tu consentimiento, muestra algo inseguro o se puede identificar a un cliente— usa Pedir revisión debajo de ella.',
    ],
    policyLabel: 'Abrir la Galería a todos los empleados',
    policyNote:
      'Todos los colegas activos podrán recorrer la Galería en tamaño feed y buscar en los pies de foto. Sin descargas y sin registro. Al desactivarlo vuelve al rol de visitante de la Galería.',
  },
}

/* ── SHL-34: the hub tour (components/Tour.tsx, lib/tour.ts) ──────────────
   One title and one body per stop. Each body has to be TRUE whether the stop
   points at something or is drawn centred (no contest running, One Question
   outside its weeks), so none of them says "here" or "this card". The help
   corpus's `tour_what` names the same six stops - keep them in step. */
export type TourCopy = {
  label: string
  progress: (n: number, total: number) => string
  next: string
  back: string
  done: string
  skip: string
  steps: Record<TourStepKey, { title: string; body: string }>
}

export const TOUR: Record<HubLang, TourCopy> = {
  en: {
    label: 'Tour of the hub',
    progress: (n, total) => `${n} of ${total}`,
    next: 'Next',
    back: 'Back',
    done: 'Done',
    skip: 'Skip the tour',
    steps: {
      feed: {
        title: 'The company feed',
        body: 'News from across the company. Press the pencil to post something, recognize a colleague or open a draft, and react to and comment on what others share.',
      },
      oneq: {
        title: 'One Question',
        body: 'A new question every Monday, at the top of the hub. Answer in a sentence or two. Only Communications reads the answers - colleagues never see yours.',
      },
      askone: {
        title: 'Ask One',
        body: 'Ask how anything here works, or about the brand change. One is an AI: it answers from the hub\'s guide and Wiki One, and shows where each answer came from.',
      },
      stars: {
        title: 'Star One',
        body: 'Thank a colleague with stars, and see the stars you have received.',
      },
      photos: {
        title: 'Photo contests',
        body: 'Photos posted to the feed go into the monthly photo contest unless you opt them out, and everyone gets a vote.',
      },
      menu: {
        title: 'Your menu',
        body: 'Your profile and language, your digital card, Help and its guides, and this tour again whenever you want it - all under your initials.',
      },
    },
  },
  es: {
    label: 'Recorrido por el portal',
    progress: (n, total) => `${n} de ${total}`,
    next: 'Siguiente',
    back: 'Atrás',
    done: 'Listo',
    skip: 'Saltar el recorrido',
    steps: {
      feed: {
        title: 'Novedades de la empresa',
        body: 'Noticias de toda la empresa. Toca el lápiz para publicar algo, reconocer a un colega o abrir un borrador, y reacciona y comenta lo que otros comparten.',
      },
      oneq: {
        title: 'One Question',
        body: 'Una pregunta nueva cada lunes, arriba en el portal. Responde en una o dos frases. Solo Comunicaciones lee las respuestas: tus colegas nunca ven la tuya.',
      },
      askone: {
        title: 'Ask One',
        body: 'Pregunta cómo funciona cualquier cosa aquí, o sobre el cambio de marca. One es una IA: responde con la guía del portal y Wiki One, y muestra de dónde salió cada respuesta.',
      },
      stars: {
        title: 'Star One',
        body: 'Agradece a un colega con estrellas y mira las estrellas que has recibido.',
      },
      photos: {
        title: 'Concursos de fotos',
        body: 'Las fotos publicadas en las novedades participan en el concurso de fotos del mes, salvo que las excluyas, y todos tienen un voto.',
      },
      menu: {
        title: 'Tu menú',
        body: 'Tu perfil e idioma, tu tarjeta digital, Ayuda y sus guías, y este recorrido otra vez cuando quieras: todo bajo tus iniciales.',
      },
    },
  },
}

/* ── 165: the terms of use and the Star One agreement ──────────────────────
   (components/TermsGate.tsx, components/StarOneAgreement.tsx, routes/Terms.tsx)
   The TERMS THEMSELVES are not here: they are in the database, versioned, and
   read through terms_text(). This is only the chrome around them, and the six
   short points on the Star One card, which restate the terms and must stay in
   step with them (the limit and expiry come from the server, never from here). */
export type TermsCopy = {
  gateLede: string
  agreePlatform: string
  agreeAndContinue: string
  signOut: string
  loading: string
  loadFailed: string
  retry: string
  saveFailed: string
  readEnglish: string
  readOwn: string
  machineNote: string
  versionLine: (version: number, date: string) => string
  starTitle: string
  starLede: string
  starPoints: (limit: string, months: number) => string[]
  starWaiting: string
  readFull: string
  hideFull: string
  agreeStarOne: string
  join: string
  pageTitle: string
  agreedOn: (date: string) => string
  starNotJoined: string
  joinFromStarOne: string
  nothingToShow: string
  /** In the give form, for somebody who has not joined: a shout-out still works. */
  giveNeedsStarOne: string
  goToStarOne: string
}

export const TERMS: Record<HubLang, TermsCopy> = {
  en: {
    gateLede: 'Please read the OneConetec Terms of Use. You need to agree to them before you use the hub.',
    agreePlatform: 'I have read and agree to the OneConetec Terms of Use.',
    agreeAndContinue: 'Agree and continue',
    signOut: 'Sign out',
    loading: 'Loading the terms…',
    loadFailed: 'The terms did not load. Check your connection and try again.',
    retry: 'Try again',
    saveFailed: 'Your agreement was not saved. Try again.',
    readEnglish: 'Read in English',
    readOwn: 'Read in English',
    machineNote: 'Machine translation, not yet reviewed.',
    versionLine: (v, date) => `Version ${v} · in effect from ${date}`,
    starTitle: 'Join Star One',
    starLede: 'Star One is how we recognize each other, with stars you can spend in the Store. Agree to its terms to see your stars, give stars and use the Store.',
    starPoints: (limit, months) => [
      'The program can change from time to time, without notice.',
      'Stars have no cash value.',
      'Stars can only be redeemed in the OneConetec Store.',
      'Stars have no value when your employment or engagement ends.',
      `Your balance can hold up to ${limit} stars. The limit can change.`,
      `Unspent stars expire after ${months} months. The period can change.`,
    ],
    starWaiting: 'Stars colleagues give you are kept until you join.',
    readFull: 'Read the full Star One terms',
    hideFull: 'Hide the full terms',
    agreeStarOne: 'I have read and agree to the Star One Program Terms.',
    join: 'Join Star One',
    pageTitle: 'Terms',
    agreedOn: (date) => `You agreed on ${date}.`,
    starNotJoined: 'You have not joined Star One yet.',
    joinFromStarOne: 'Join from Star One',
    nothingToShow: 'There are no terms to show.',
    giveNeedsStarOne: 'Join Star One to add stars. You can still recognize a colleague without them.',
    goToStarOne: 'Go to Star One',
  },
  es: {
    gateLede: 'Lee los Términos de uso de OneConetec. Debes aceptarlos antes de usar el portal.',
    agreePlatform: 'He leído y acepto los Términos de uso de OneConetec.',
    agreeAndContinue: 'Aceptar y continuar',
    signOut: 'Cerrar sesión',
    loading: 'Cargando los términos…',
    loadFailed: 'No se pudieron cargar los términos. Revisa tu conexión e inténtalo de nuevo.',
    retry: 'Intentar de nuevo',
    saveFailed: 'No se guardó tu aceptación. Inténtalo de nuevo.',
    readEnglish: 'Leer en inglés',
    readOwn: 'Leer en español',
    machineNote: 'Traducción automática, aún sin revisar.',
    versionLine: (v, date) => `Versión ${v} · vigente desde el ${date}`,
    starTitle: 'Únete a Star One',
    starLede: 'Star One es la forma en que nos reconocemos, con estrellas que puedes canjear en la Tienda. Acepta sus términos para ver tus estrellas, dar estrellas y usar la Tienda.',
    starPoints: (limit, months) => [
      'El programa puede cambiar cuando se estime conveniente, sin previo aviso.',
      'Las estrellas no tienen valor en dinero.',
      'Las estrellas solo se pueden canjear en la Tienda de OneConetec.',
      'Las estrellas no tienen valor cuando termina tu relación laboral o tu contrato.',
      `Tu saldo puede tener hasta ${limit} estrellas. El límite puede cambiar.`,
      `Las estrellas no canjeadas vencen a los ${months} meses. El plazo puede cambiar.`,
    ],
    starWaiting: 'Las estrellas que te den tus colegas se guardan hasta que te unas.',
    readFull: 'Leer los términos completos de Star One',
    hideFull: 'Ocultar los términos completos',
    agreeStarOne: 'He leído y acepto los Términos del programa Star One.',
    join: 'Unirme a Star One',
    pageTitle: 'Términos',
    agreedOn: (date) => `Los aceptaste el ${date}.`,
    starNotJoined: 'Todavía no te has unido a Star One.',
    joinFromStarOne: 'Unirme desde Star One',
    nothingToShow: 'No hay términos que mostrar.',
    giveNeedsStarOne: 'Únete a Star One para agregar estrellas. Igual puedes reconocer a un colega sin ellas.',
    goToStarOne: 'Ir a Star One',
  },
}

/* ── 302 (FED-70): One's tags on your own photo, and the Gallery's tag list ── */

export type PhotoWhatCopy = {
  /** The line under your own photo in the viewer. */
  oneSees: (labels: string) => string
  stillLooking: string
  noTagsYet: string
  check: string
  addTags: string
  /** The dialog. */
  dialogTitle: string
  intro: string
  facet: { brand: string; equipment: string; sector: string; feature: string }
  suggested: (pct: number) => string
  yes: string
  no: string
  notInPhoto: string
  add: string
  none: string
  couldNotSave: string
  couldNotLoad: string
  done: string
  /** The Gallery admin's tray. */
  listButton: string
  listTitle: string
  listIntro: string
  record: (one: number, yes: number, no: number, added: number) => string
  references: (n: number) => string
  noteLabel: string
  notePlaceholder: string
  save: string
  saved: string
  /** 305 (FED-72). Everyone answers; a reason is asked for a no and for an added tag; what colleagues said. */
  oneSeesAsk: (labels: string) => string
  introCrowd: string
  agree: string
  youAgreed: string
  youSaidNo: string
  counts: (yes: number, no: number) => string
  suggestedByPerson: (name: string) => string
  whyNo: (label: string) => string
  whyNoSure: (label: string, pct: number) => string
  whyAdd: (label: string) => string
  reasonPlaceholder: string
  reasonNote: string
  send: string
  skipReason: string
  cancel: string
  answers: string
  saidYes: (label: string) => string
  saidNo: (label: string) => string
  notesLabel: string
  notesNone: string
  newCue: string
}

export const PHOTO_WHAT: Record<HubLang, PhotoWhatCopy> = {
  en: {
    oneSees: (labels) => `One sees: ${labels}`,
    stillLooking: 'One is still looking at this photo.',
    noTagsYet: 'No tags on this photo yet.',
    check: 'Check tags',
    addTags: 'Add tags',
    dialogTitle: 'What is in your photo',
    intro:
      'One suggested these. Say yes to the right ones and no to the wrong ones, and add any it missed. Your answers help One read the next photos, and help colleagues find this one in the Gallery.',
    facet: { brand: 'Brand', equipment: 'Equipment', sector: 'Sector / site', feature: 'In the photo' },
    suggested: (pct) => `Suggested by One (${pct}% sure)`,
    yes: 'Yes, it is in the photo',
    no: 'No, it is not',
    notInPhoto: 'Not in the photo',
    add: 'Add…',
    none: 'None',
    couldNotSave: 'That did not save. Try again.',
    couldNotLoad: 'The tags did not load.',
    done: 'Done',
    listButton: 'Tag list',
    listTitle: 'Tag list',
    listIntro:
      'What One is told about each tag when it reads a photo. A note says how to recognise the tag, in plain words, and One follows it. For brands, One is also shown up to two photos that a person confirmed. Only people\'s yes and no count; a suggestion nobody answered teaches One nothing.',
    record: (one, yes, no, added) =>
      `One suggested it on ${one} ${one === 1 ? 'photo' : 'photos'} · people said yes ${yes}, no ${no}` +
      (added ? ` · people added it ${added} ${added === 1 ? 'time' : 'times'}` : ''),
    references: (n) => (n === 0 ? 'No confirmed photo to show One yet' : `One is shown ${n} confirmed ${n === 1 ? 'photo' : 'photos'}`),
    noteLabel: 'How to recognise it',
    notePlaceholder: 'For example: yellow rigs and vehicles with green lettering.',
    save: 'Save',
    saved: 'Saved',
    // 305 (FED-72)
    oneSeesAsk: (labels) => `One thinks this shows: ${labels}. Is that right?`,
    introCrowd:
      'Say yes or no to what One sees here, and add anything it missed. The person who posted the photo and the Gallery team have the last word; two colleagues agreeing settles a tag nobody has decided. If you say no, you can say why — that is how One gets better.',
    agree: 'Agree',
    youAgreed: 'you agreed',
    youSaidNo: 'you said no',
    counts: (yes, no) => `${yes} yes · ${no} no`,
    suggestedByPerson: (name) => `suggested by ${name}`,
    whyNo: (label) => `Not ${label}? Say why, if you like — what is it instead?`,
    whyNoSure: (label, pct) => `One was ${pct}% sure this shows ${label}. What gave it away?`,
    whyAdd: (label) => `Adding ${label}. What shows it, if you like?`,
    reasonPlaceholder: 'For example: the mast is blue, that is a Geotech rig.',
    reasonNote: 'Optional, up to 280 characters. The person who posted the photo and the Gallery team see it with your name; One is told it when the tag ends up the way you said.',
    send: 'Send',
    skipReason: 'Send without a reason',
    cancel: 'Cancel',
    answers: 'What people said',
    saidYes: (label) => `yes to ${label}`,
    saidNo: (label) => `no to ${label}`,
    notesLabel: 'What colleagues said, as One is told it',
    notesNone: 'Nothing yet.',
    newCue: 'How to recognise it (optional)',
  },
  es: {
    oneSees: (labels) => `One ve: ${labels}`,
    stillLooking: 'One todavía está mirando esta foto.',
    noTagsYet: 'Esta foto aún no tiene etiquetas.',
    check: 'Revisar etiquetas',
    addTags: 'Agregar etiquetas',
    dialogTitle: 'Qué hay en tu foto',
    intro:
      'One sugirió estas. Di sí a las correctas y no a las incorrectas, y agrega las que le faltaron. Tus respuestas ayudan a One a leer las próximas fotos y a tus colegas a encontrar esta en la Galería.',
    facet: { brand: 'Marca', equipment: 'Equipo', sector: 'Sector / sitio', feature: 'En la foto' },
    suggested: (pct) => `Sugerida por One (${pct}% de seguridad)`,
    yes: 'Sí, está en la foto',
    no: 'No, no está',
    notInPhoto: 'No está en la foto',
    add: 'Agregar…',
    none: 'Ninguna',
    couldNotSave: 'No se guardó. Inténtalo de nuevo.',
    couldNotLoad: 'Las etiquetas no se cargaron.',
    done: 'Listo',
    listButton: 'Lista de etiquetas',
    listTitle: 'Lista de etiquetas',
    listIntro:
      'Lo que se le dice a One de cada etiqueta cuando lee una foto. Una nota dice cómo reconocer la etiqueta, en palabras simples, y One la sigue. Para las marcas, a One también se le muestran hasta dos fotos que una persona confirmó. Solo cuentan el sí y el no de las personas; una sugerencia que nadie respondió no le enseña nada a One.',
    record: (one, yes, no, added) =>
      `One la sugirió en ${one} ${one === 1 ? 'foto' : 'fotos'} · las personas dijeron sí ${yes}, no ${no}` +
      (added ? ` · las personas la agregaron ${added} ${added === 1 ? 'vez' : 'veces'}` : ''),
    references: (n) => (n === 0 ? 'Aún no hay una foto confirmada para mostrarle a One' : `A One se le muestran ${n} ${n === 1 ? 'foto confirmada' : 'fotos confirmadas'}`),
    noteLabel: 'Cómo reconocerla',
    notePlaceholder: 'Por ejemplo: equipos y vehículos amarillos con letras verdes.',
    save: 'Guardar',
    saved: 'Guardado',
    // 305 (FED-72)
    oneSeesAsk: (labels) => `One cree que esto muestra: ${labels}. ¿Es así?`,
    introCrowd:
      'Di sí o no a lo que One ve aquí y agrega lo que le faltó. Quien publicó la foto y el equipo de la galería tienen la última palabra; dos colegas de acuerdo resuelven una etiqueta que nadie ha decidido. Si dices que no, puedes decir por qué: así One mejora.',
    agree: 'De acuerdo',
    youAgreed: 'dijiste que sí',
    youSaidNo: 'dijiste que no',
    counts: (yes, no) => `${yes} sí · ${no} no`,
    suggestedByPerson: (name) => `sugerida por ${name}`,
    whyNo: (label) => `¿No es ${label}? Di por qué, si quieres: ¿qué es en realidad?`,
    whyNoSure: (label, pct) => `One estaba ${pct}% seguro de que esto muestra ${label}. ¿Qué lo delató?`,
    whyAdd: (label) => `Agregando ${label}. ¿Qué lo muestra, si quieres decirlo?`,
    reasonPlaceholder: 'Por ejemplo: el mástil es azul, es un equipo de Geotech.',
    reasonNote: 'Opcional, hasta 280 caracteres. Quien publicó la foto y el equipo de la galería lo ven con tu nombre; a One se le cuenta cuando la etiqueta termina como dijiste.',
    send: 'Enviar',
    skipReason: 'Enviar sin motivo',
    cancel: 'Cancelar',
    answers: 'Lo que dijeron las personas',
    saidYes: (label) => `sí a ${label}`,
    saidNo: (label) => `no a ${label}`,
    notesLabel: 'Lo que dijeron los colegas, tal como se le cuenta a One',
    notesNone: 'Nada todavía.',
    newCue: 'Cómo reconocerla (opcional)',
  },
}

/* ── 310: Surveys (David, 25 Oct) ────────────────────────────────────────────
   What a person who is ASKED reads: the list, the answer form, shared
   results. The editor and the presenter's screen are a Survey manager's tools
   and stay English, like the other admin screens. */
export type SurveysCopy = {
  title: string
  lede: string
  openNow: string
  closedHeading: string
  yours: string
  none: string
  answered: string
  notAnswered: string
  closes: (when: string) => string
  closedTag: string
  draftTag: string
  namedNote: string
  anonNote: string
  send: string
  sending: string
  update: string
  thanks: string
  change: string
  seeResults: string
  required: string
  pleaseAnswer: (prompts: string) => string
  wordsPlaceholder: string
  textPlaceholder: string
  chooseAny: string
  scaleLow: string
  scaleHigh: string
  closedNote: string
  draftNote: string
  answeredOnce: string
  back: string
  loading: string
  answers: (n: number) => string
  responses: (n: number) => string
  waitingFor: (n: number) => string
  noAnswers: string
  average: (v: string) => string
  liveNow: string
  liveThanks: string
  /** 315. The screen on the wall, read by the room. */
  scanToAnswer: string
  scanHint: string
  questionOf: (i: number, n: number) => string
  liveTag: string
  /** 317. An ideas question on the phone. */
  ideasHow: string
  ideaPlaceholder: string
  addIdea: string
  yourIdeas: string
  takeBack: string
  ideasAdded: (n: number) => string
  ideasFull: (n: number) => string
  ideasKeepGoing: string
  ideasSentAsYouGo: string
  /** 317. The screen: who answered this question, out of everyone. */
  answeredThis: string
  ofPeople: (n: number) => string
  ideasCount: (n: number) => string
  peopleCount: (n: number) => string
  liveAlready: string
  yourAnswer: string
  changeAnswer: string
  sendOne: string
}

export const SURVEYS: Record<HubLang, SurveysCopy> = {
  en: {
    title: 'Surveys',
    lede: 'Surveys you have been asked to answer, open and closed.',
    openNow: 'Open now',
    closedHeading: 'Closed',
    yours: 'Surveys you run',
    none: 'Nothing has been sent to you yet.',
    answered: 'Answered',
    notAnswered: 'Not answered yet',
    closes: (when) => `Closes ${when}`,
    closedTag: 'Closed',
    draftTag: 'Draft',
    namedNote: 'Your name is recorded with your answers, so the people running this survey can follow up with you.',
    anonNote: 'Anonymous: your answers are kept without your name, and nothing is shown until 6 people have answered. You can answer once.',
    send: 'Send answers',
    sending: 'Sending…',
    update: 'Update my answers',
    thanks: 'Thank you, your answers are in.',
    change: 'Change my answers',
    seeResults: 'See the results',
    required: 'Required',
    pleaseAnswer: (p) => `Please answer: ${p}`,
    wordsPlaceholder: 'A word or two',
    textPlaceholder: 'Your answer',
    chooseAny: 'Choose any that apply',
    scaleLow: 'Not at all',
    scaleHigh: 'Completely',
    closedNote: 'This survey is closed.',
    draftNote: 'This survey is not open yet.',
    answeredOnce: 'You have answered this survey.',
    back: 'All surveys',
    loading: 'Loading…',
    answers: (n) => (n === 1 ? '1 answer' : `${n} answers`),
    responses: (n) => (n === 1 ? '1 response' : `${n} responses`),
    waitingFor: (n) => `Results show once ${n} people have answered.`,
    noAnswers: 'No answers yet.',
    average: (v) => `Average ${v} of 5`,
    liveNow: 'Live question',
    scanToAnswer: 'Scan to answer',
    scanHint: 'Point your phone camera at the code. Sign in if it asks.',
    questionOf: (i, n) => `Question ${i} of ${n}`,
    liveTag: 'Live',
    ideasHow: 'Add as many ideas as you like. Press Enter to send each one.',
    ideaPlaceholder: 'Type an idea',
    addIdea: 'Add',
    yourIdeas: 'Your ideas',
    takeBack: 'Take back',
    ideasAdded: (n) => (n === 1 ? 'You have added 1 idea.' : `You have added ${n} ideas.`),
    ideasFull: (n) => `That is ${n} ideas, the most for one question.`,
    ideasKeepGoing: 'You can keep adding ideas.',
    ideasSentAsYouGo: 'Each idea is sent as you add it - there is nothing else to send.',
    answeredThis: 'answered',
    ofPeople: (n) => `of ${n}`,
    ideasCount: (n) => (n === 1 ? '1 idea' : `${n} ideas`),
    peopleCount: (n) => (n === 1 ? '1 person' : `${n} people`),
    liveThanks: 'Thanks, your answer is in. The next question will appear here when the presenter moves on.',
    liveAlready: 'You have already answered this question. The next one will appear here when the presenter moves on.',
    yourAnswer: 'Your answer',
    changeAnswer: 'Change my answer',
    sendOne: 'Send',
  },
  es: {
    title: 'Encuestas',
    lede: 'Las encuestas que te pidieron responder, abiertas y cerradas.',
    openNow: 'Abiertas',
    closedHeading: 'Cerradas',
    yours: 'Encuestas que administras',
    none: 'Todavía no te han enviado ninguna encuesta.',
    answered: 'Respondida',
    notAnswered: 'Sin responder',
    closes: (when) => `Cierra el ${when}`,
    closedTag: 'Cerrada',
    draftTag: 'Borrador',
    namedNote: 'Tu nombre queda registrado con tus respuestas, para que quienes administran esta encuesta puedan hacerte seguimiento.',
    anonNote: 'Anónima: tus respuestas se guardan sin tu nombre y no se muestra nada hasta que respondan 6 personas. Puedes responder una vez.',
    send: 'Enviar respuestas',
    sending: 'Enviando…',
    update: 'Actualizar mis respuestas',
    thanks: 'Gracias, tus respuestas quedaron registradas.',
    change: 'Cambiar mis respuestas',
    seeResults: 'Ver los resultados',
    required: 'Obligatoria',
    pleaseAnswer: (p) => `Por favor responde: ${p}`,
    wordsPlaceholder: 'Una o dos palabras',
    textPlaceholder: 'Tu respuesta',
    chooseAny: 'Elige todas las que correspondan',
    scaleLow: 'Nada',
    scaleHigh: 'Totalmente',
    closedNote: 'Esta encuesta está cerrada.',
    draftNote: 'Esta encuesta aún no está abierta.',
    answeredOnce: 'Ya respondiste esta encuesta.',
    back: 'Todas las encuestas',
    loading: 'Cargando…',
    answers: (n) => (n === 1 ? '1 respuesta' : `${n} respuestas`),
    responses: (n) => (n === 1 ? '1 participante' : `${n} participantes`),
    waitingFor: (n) => `Los resultados se muestran cuando hayan respondido ${n} personas.`,
    noAnswers: 'Todavía no hay respuestas.',
    average: (v) => `Promedio ${v} de 5`,
    liveNow: 'Pregunta en vivo',
    scanToAnswer: 'Escanea para responder',
    scanHint: 'Apunta la cámara de tu teléfono al código. Inicia sesión si te lo pide.',
    questionOf: (i, n) => `Pregunta ${i} de ${n}`,
    liveTag: 'En vivo',
    ideasHow: 'Agrega todas las ideas que quieras. Presiona Enter para enviar cada una.',
    ideaPlaceholder: 'Escribe una idea',
    addIdea: 'Agregar',
    yourIdeas: 'Tus ideas',
    takeBack: 'Retirar',
    ideasAdded: (n) => (n === 1 ? 'Agregaste 1 idea.' : `Agregaste ${n} ideas.`),
    ideasFull: (n) => `Son ${n} ideas, el máximo para una pregunta.`,
    ideasKeepGoing: 'Puedes seguir agregando ideas.',
    ideasSentAsYouGo: 'Cada idea se envía al agregarla; no hay nada más que enviar.',
    answeredThis: 'respondieron',
    ofPeople: (n) => `de ${n}`,
    ideasCount: (n) => (n === 1 ? '1 idea aportada' : `${n} ideas aportadas`),
    peopleCount: (n) => (n === 1 ? '1 persona' : `${n} personas`),
    liveThanks: 'Gracias, tu respuesta quedó registrada. La siguiente pregunta aparecerá aquí cuando el presentador avance.',
    liveAlready: 'Ya respondiste esta pregunta. La siguiente aparecerá aquí cuando el presentador avance.',
    yourAnswer: 'Tu respuesta',
    changeAnswer: 'Cambiar mi respuesta',
    sendOne: 'Enviar',
  },
}

/** 318 (FED-55): the Gallery map. */
export type GalleryMapCopy = {
  toggle: string
  region: string
  loading: string
  failed: string
  /** "12 on the map · 3 without location" */
  counts: (located: number, without: number) => string
  /** No photo the filters chose has a location. */
  none: (without: number) => string
  capped: (shown: number, located: number) => string
  pinLabel: (place: string | null) => string
  clusterLabel: (n: number) => string
  /** The line above the grid once a pin or cluster is chosen. */
  picked: (n: number, place: string | null) => string
  clear: string
  outline: string
  /** The basemap switcher (only with a MapTiler key): its group name and its three choices. */
  basemapGroup: string
  basemaps: { streets: string; satellite: string; topo: string }
  /** MapLibre's own controls and its two-finger hint, in the reader's language. */
  mapLocale: Record<string, string>
  /** 319: the line under a photo, and its button. */
  add: string
  move: string
  notPlaced: string
  placedCamera: string
  placedBy: (name: string | null) => string
  optedOut: string
  placedSaved: string
  /** 322: the small map under a photo's details, for a screen reader. */
  miniMapLabel: string
  /** 324: One suggests where it was taken - the button, the list and its words. */
  suggest: string
  suggestAgain: string
  suggesting: string
  suggestTitle: string
  suggestNone: string
  suggestFailed: string
  suggestKnown: string
  suggestSure: { likely: string; possible: string; guess: string }
  suggestDismiss: string
  suggestChoose: (name: string) => string
  suggestSaved: (name: string) => string
  /** 319: the location picker. */
  pickTitleAdd: string
  pickTitleMove: string
  pickIntro: string
  pickSearchLabel: string
  pickFind: string
  pickFinding: string
  pickNoHits: string
  pickCoords: string
  pickBadCoords: string
  pickHere: string
  pickLocating: string
  pickNoGeo: string
  pickCancel: string
  pickSave: string
  pickSaving: string
  pickFailed: string
  pickChosen: (coords: string) => string
  pickNone: string
}

export const GALLERY_MAP: Record<HubLang, GalleryMapCopy> = {
  en: {
    toggle: 'Map',
    region: 'Where the photos were taken',
    loading: 'Loading the map…',
    failed: 'The map could not load. The photos are all still in the list.',
    counts: (located, without) =>
      `${located.toLocaleString()} on the map · ${without.toLocaleString()} without location`,
    none: (without) =>
      without === 1 ? 'This photo has no location.' : `None of these ${without.toLocaleString()} photos has a location.`,
    capped: (shown, located) => `The map shows the newest ${shown.toLocaleString()} of ${located.toLocaleString()}.`,
    pinLabel: (place) => (place ? `1 photo, ${place}` : '1 photo'),
    clusterLabel: (n) => `${n.toLocaleString()} photos here`,
    picked: (n, place) =>
      `${n === 1 ? 'Showing the photo' : `Showing the ${n.toLocaleString()} photos`} ${place ? `from ${place}` : 'from the spot you chose'} on the map.`,
    clear: 'Show all',
    outline: 'Outline map: coastlines and borders only.',
    basemapGroup: 'Map type',
    basemaps: { streets: 'Streets', satellite: 'Satellite', topo: 'Topo' },
    mapLocale: {
      'NavigationControl.ZoomIn': 'Zoom in',
      'NavigationControl.ZoomOut': 'Zoom out',
      'CooperativeGesturesHandler.WindowsHelpText': 'Use Ctrl + scroll to zoom the map',
      'CooperativeGesturesHandler.MacHelpText': 'Use ⌘ + scroll to zoom the map',
      'CooperativeGesturesHandler.MobileHelpText': 'Use two fingers to move the map',
    },
    add: 'Add location',
    move: 'Move',
    notPlaced: 'Not on the map yet.',
    placedCamera: 'On the map, from the camera.',
    placedBy: (name) => (name ? `On the map, placed by ${name}.` : 'On the map, placed by hand.'),
    optedOut: 'The person who posted it chose not to share where it was taken.',
    placedSaved: 'Saved. It is on the map.',
    miniMapLabel: 'Map with a pin where this photo was taken',
    suggest: 'Suggest location',
    suggestAgain: 'Suggest other places',
    suggesting: 'One is looking at the photo…',
    suggestTitle: 'Where One thinks it was taken. Choose one to put it on the map:',
    suggestNone: 'One found nothing to go on. Add the location by hand instead.',
    suggestFailed: 'One could not suggest a place just now.',
    suggestKnown: 'known site',
    suggestSure: { likely: 'Likely', possible: 'Possible', guess: 'A guess' },
    suggestDismiss: 'None of these',
    suggestChoose: (name) => `Put it on the map at ${name}`,
    suggestSaved: (name) => `Saved at ${name}. It is on the map.`,
    pickTitleAdd: 'Add where it was taken',
    pickTitleMove: 'Move where it was taken',
    pickIntro: 'Tap the map where the photo was taken, then drag the pin to adjust. You can also type the coordinates.',
    pickSearchLabel: 'Find a place',
    pickFind: 'Find',
    pickFinding: 'Finding…',
    pickNoHits: 'No place by that name.',
    pickCoords: 'Latitude, longitude',
    pickBadCoords: 'Type two numbers, latitude then longitude, such as -23.65093, -70.39751.',
    pickHere: 'Use where I am',
    pickLocating: 'Finding you…',
    pickNoGeo: 'Your browser did not share where you are.',
    pickCancel: 'Cancel',
    pickSave: 'Save location',
    pickSaving: 'Saving…',
    pickFailed: 'The location could not be saved.',
    pickChosen: (c) => `Pin at ${c}.`,
    pickNone: 'No pin yet.',
  },
  es: {
    toggle: 'Mapa',
    region: 'Dónde se tomaron las fotos',
    loading: 'Cargando el mapa…',
    failed: 'No se pudo cargar el mapa. Todas las fotos siguen en la lista.',
    counts: (located, without) =>
      `${located.toLocaleString()} en el mapa · ${without.toLocaleString()} sin ubicación`,
    none: (without) =>
      without === 1 ? 'Esta foto no tiene ubicación.' : `Ninguna de estas ${without.toLocaleString()} fotos tiene ubicación.`,
    capped: (shown, located) => `El mapa muestra las ${shown.toLocaleString()} más recientes de ${located.toLocaleString()}.`,
    pinLabel: (place) => (place ? `1 foto, ${place}` : '1 foto'),
    clusterLabel: (n) => `${n.toLocaleString()} fotos aquí`,
    picked: (n, place) =>
      `${n === 1 ? 'Se muestra la foto' : `Se muestran las ${n.toLocaleString()} fotos`} ${place ? `de ${place}` : 'del punto que elegiste'} en el mapa.`,
    clear: 'Mostrar todas',
    outline: 'Mapa de contorno: solo costas y fronteras.',
    basemapGroup: 'Tipo de mapa',
    basemaps: { streets: 'Calles', satellite: 'Satélite', topo: 'Topográfico' },
    mapLocale: {
      'NavigationControl.ZoomIn': 'Acercar',
      'NavigationControl.ZoomOut': 'Alejar',
      'CooperativeGesturesHandler.WindowsHelpText': 'Usa Ctrl + rueda para acercar el mapa',
      'CooperativeGesturesHandler.MacHelpText': 'Usa ⌘ + rueda para acercar el mapa',
      'CooperativeGesturesHandler.MobileHelpText': 'Usa dos dedos para mover el mapa',
    },
    add: 'Agregar ubicación',
    move: 'Mover',
    notPlaced: 'Todavía no está en el mapa.',
    placedCamera: 'En el mapa, según la cámara.',
    placedBy: (name) => (name ? `En el mapa, ubicada por ${name}.` : 'En el mapa, ubicada a mano.'),
    optedOut: 'Quien la publicó eligió no compartir dónde se tomó.',
    placedSaved: 'Guardado. Ya está en el mapa.',
    miniMapLabel: 'Mapa con un marcador donde se tomó esta foto',
    suggest: 'Sugerir ubicación',
    suggestAgain: 'Sugerir otros lugares',
    suggesting: 'One está mirando la foto…',
    suggestTitle: 'Dónde cree One que se tomó. Elige uno para ponerla en el mapa:',
    suggestNone: 'One no encontró en qué basarse. Agrega la ubicación a mano.',
    suggestFailed: 'One no pudo sugerir un lugar en este momento.',
    suggestKnown: 'sitio conocido',
    suggestSure: { likely: 'Probable', possible: 'Posible', guess: 'Una suposición' },
    suggestDismiss: 'Ninguno de estos',
    suggestChoose: (name) => `Ponerla en el mapa en ${name}`,
    suggestSaved: (name) => `Guardada en ${name}. Ya está en el mapa.`,
    pickTitleAdd: 'Agregar dónde se tomó',
    pickTitleMove: 'Mover dónde se tomó',
    pickIntro: 'Toca el mapa donde se tomó la foto y arrastra el marcador para ajustar. También puedes escribir las coordenadas.',
    pickSearchLabel: 'Buscar un lugar',
    pickFind: 'Buscar',
    pickFinding: 'Buscando…',
    pickNoHits: 'No hay un lugar con ese nombre.',
    pickCoords: 'Latitud, longitud',
    pickBadCoords: 'Escribe dos números, latitud y luego longitud, como -23.65093, -70.39751.',
    pickHere: 'Usar dónde estoy',
    pickLocating: 'Buscándote…',
    pickNoGeo: 'Tu navegador no compartió dónde estás.',
    pickCancel: 'Cancelar',
    pickSave: 'Guardar ubicación',
    pickSaving: 'Guardando…',
    pickFailed: 'No se pudo guardar la ubicación.',
    pickChosen: (c) => `Marcador en ${c}.`,
    pickNone: 'Todavía no hay marcador.',
  },
}

/** 321 (FED-74). The one searchable picker (components/SearchPick). */
export type SearchPickCopy = {
  noMatch: string
  addNew: (text: string) => string
}

export const SEARCH_PICK: Record<HubLang, SearchPickCopy> = {
  en: {
    noMatch: 'Nothing matches.',
    addNew: (q) => `Add “${q}”`,
  },
  es: {
    noMatch: 'Nada coincide.',
    addNew: (q) => `Agregar “${q}”`,
  },
}

/** 321 (FED-74). The Gallery admin's site list (components/SiteListTray). */
export type SiteListCopy = {
  button: string
  title: string
  intro: string
  filter: string
  proposed: string
  retired: string
  counts: (confirmed: number, suggested: number) => string
  rename: string
  save: string
  cancel: string
  merge: string
  mergePick: string
  mergeConfirm: (from: string, into: string) => string
  mergeYes: string
  retire: string
  retireConfirm: (name: string) => string
  retireYes: string
  none: string
  couldNotLoad: string
  failed: string
}

export const SITE_LIST: Record<HubLang, SiteListCopy> = {
  en: {
    button: 'Site list',
    title: 'Site list',
    intro:
      'The sites photos are named with. OneConetec proposes a site from where a photo was taken and what is in it; a name joins the list the first time somebody confirms it on a photo, and from then on anyone naming a site can pick it. Rename one that is spelled wrong, merge two that are the same place, retire one that should not be offered.',
    filter: 'Find a site',
    proposed: 'OneConetec’s proposal, not yet confirmed',
    retired: 'Retired',
    counts: (a, s) => `${a.toLocaleString()} confirmed · ${s.toLocaleString()} suggested`,
    rename: 'Rename',
    save: 'Save',
    cancel: 'Cancel',
    merge: 'Merge into…',
    mergePick: 'Find the site it is the same as',
    mergeConfirm: (a, b) => `Move every photo of “${a}” to “${b}”, and take “${a}” off the list?`,
    mergeYes: 'Merge',
    retire: 'Retire',
    retireConfirm: (a) => `Take “${a}” off the list? OneConetec’s guesses of it are cleared; photos somebody confirmed keep it.`,
    retireYes: 'Retire',
    none: 'No sites yet. They appear as OneConetec names places and people confirm them.',
    couldNotLoad: 'The site list could not load.',
    failed: 'That did not work.',
  },
  es: {
    button: 'Lista de sitios',
    title: 'Lista de sitios',
    intro:
      'Los sitios con que se nombran las fotos. OneConetec propone un sitio a partir de dónde se tomó la foto y lo que se ve en ella; un nombre entra a la lista la primera vez que alguien lo confirma en una foto, y desde entonces cualquiera que nombre un sitio puede elegirlo. Cambia el nombre de uno mal escrito, une dos que son el mismo lugar o retira uno que no debe ofrecerse.',
    filter: 'Buscar un sitio',
    proposed: 'Propuesta de OneConetec, aún sin confirmar',
    retired: 'Retirado',
    counts: (a, s) => `${a.toLocaleString()} ${a === 1 ? 'confirmada' : 'confirmadas'} · ${s.toLocaleString()} ${s === 1 ? 'sugerida' : 'sugeridas'}`,
    rename: 'Cambiar nombre',
    save: 'Guardar',
    cancel: 'Cancelar',
    merge: 'Unir con…',
    mergePick: 'Busca el sitio que es el mismo',
    mergeConfirm: (a, b) => `¿Mover todas las fotos de “${a}” a “${b}” y quitar “${a}” de la lista?`,
    mergeYes: 'Unir',
    retire: 'Retirar',
    retireConfirm: (a) => `¿Quitar “${a}” de la lista? Se borran las suposiciones de OneConetec; las fotos que alguien confirmó lo conservan.`,
    retireYes: 'Retirar',
    none: 'Todavía no hay sitios. Aparecen a medida que OneConetec nombra lugares y las personas los confirman.',
    couldNotLoad: 'No se pudo cargar la lista de sitios.',
    failed: 'Eso no funcionó.',
  },
}

/* ── 333: recognitions in the feed, one Star One card per gap between posts ── */
export type FeedStarsCopy = {
  /** The card's accessible name. */
  label: string
  /** "6 recognitions", or "100+ recognitions" when the server says capped. */
  count: (n: number, capped: boolean) => string
  showAll: (n: number, capped: boolean) => string
  showFewer: string
  loading: string
  couldNotLoad: string
  openWall: string
}

export const FEED_STARS: Record<HubLang, FeedStarsCopy> = {
  en: {
    label: 'Recognitions from Star One',
    count: (n, capped) => `${capped ? `${n}+` : n} ${n === 1 && !capped ? 'recognition' : 'recognitions'}`,
    showAll: (n, capped) => `Show all ${capped ? `${n}+` : n}`,
    showFewer: 'Show fewer',
    loading: 'Loading…',
    couldNotLoad: 'Those did not load. They are all on the Star One wall.',
    openWall: 'Star One wall',
  },
  es: {
    label: 'Reconocimientos de Star One',
    count: (n, capped) => `${capped ? `${n}+` : n} ${n === 1 && !capped ? 'reconocimiento' : 'reconocimientos'}`,
    showAll: (n, capped) => `Ver todos (${capped ? `${n}+` : n})`,
    showFewer: 'Ver menos',
    loading: 'Cargando…',
    couldNotLoad: 'No se cargaron. Están todos en el muro de Star One.',
    openWall: 'Muro de Star One',
  },
}

/* ── 337: HELP — #/help, a ticket, feedback (HLP-43, One and Help v2 S1) ─────
 *
 * The page for reaching a PERSON. Ask One stays the first door (the New menu
 * lists it first); a ticket goes to the team the person picks (D2); feedback
 * goes to the platform desk (D3). Every line that says who reads something is
 * the v1.1 rule carried over: private to a named team, not anonymous.
 */
export type HelpCopy = {
  lede: string
  newLabel: string
  newMenuLabel: string
  newAskOne: string
  newAskOneWho: string
  newTicket: string
  newTicketWho: string
  newFeedback: string
  newFeedbackWho: string
  guidesTitle: string
  guidesBody: string
  guidesAsk: string
  guidesOpen: string
  guidesCount: (n: number) => string
  wikiTitle: string
  wikiBody: string
  wikiOpen: string
  ticketsTitle: string
  ticketsLede: string
  feedbackTag: string
  ticketTitle: string
  ticketLede: string
  ticketAskOne: string
  whoReads: string
  laneClosed: (queue: string) => string
  cancel: string
  done: string
  feedbackTitle: string
  feedbackLabel: string
  feedbackPlaceholder: string
  feedbackWho: Emph
  feedbackClosed: string
  sendFeedback: string
  /** The desk's kind filter (lane holders). */
  kindLabel: string
  kindAll: string
  kindQuestion: string
  kindFeedback: string
  /** 348 (D4, Mockup C). The ticket's "how much is this in your way?" - four
   *  answers in the words of somebody who does not send email for a living.
   *  Keys are the server's and never change; these words may. */
  priorityLabel: string
  priorities: Record<'cant_work' | 'blocked' | 'annoying' | 'question', { label: string; hint: string }>
  /** Under Send until a choice is made: no default is picked for anybody. */
  priorityNeeded: string
  /** Your tickets: what you said, shown back, not editable. */
  youSaid: (label: string) => string
}

export const HELP: Record<HubLang, HelpCopy> = {
  en: {
    lede: 'Need help with something? A person on the support team reads every ticket.',
    newLabel: 'New',
    newMenuLabel: 'Start something new',
    newAskOne: 'Ask One',
    newAskOneWho: 'An answer now, from AI',
    newTicket: 'Ticket',
    newTicketWho: 'A person on the team you choose replies',
    newFeedback: 'Feedback',
    newFeedbackWho: 'An idea or a suggestion',
    guidesTitle: 'Guides',
    guidesBody:
      'How to do your part on OneConetec: getting started, getting help, and a guide for each role you hold.',
    guidesAsk: 'Ask One how something works',
    guidesOpen: 'Open guides',
    guidesCount: (n) => (n === 1 ? '1 guide for you' : n + ' guides for you'),
    wikiTitle: 'Wiki One',
    wikiBody: 'Answers about the brand change, written by Communications.',
    wikiOpen: 'Read Wiki One',
    ticketsTitle: 'Your tickets',
    ticketsLede: 'Everything you have sent privately — tickets and feedback. Only you and the team you sent it to can read it.',
    feedbackTag: 'Feedback',
    ticketTitle: 'New ticket',
    ticketLede: 'A person replies here, and you are notified.',
    ticketAskOne: 'For an instant answer, ask One.',
    whoReads: 'Who should read this?',
    laneClosed: (queue) => `Nobody is answering ${queue} yet, so a ticket cannot go there. Choose the other team, or ask One.`,
    cancel: 'Cancel',
    done: 'Done',
    feedbackTitle: 'Share feedback',
    feedbackLabel: 'Your thoughts, ideas or suggestions',
    feedbackPlaceholder: 'What would make OneConetec better for you?',
    feedbackWho: [
      'The platform team reads every one, and ',
      'they see your name',
      '. It is not published. You will find it under Your tickets, and you are notified when they reply.',
    ],
    feedbackClosed: 'Nobody is reading feedback yet, so it cannot be sent. Ask One, or try again later.',
    sendFeedback: 'Send feedback',
    kindLabel: 'Kind',
    kindAll: 'Tickets and feedback',
    kindQuestion: 'Tickets',
    kindFeedback: 'Feedback',
    priorityLabel: 'How much is this in your way?',
    priorities: {
      cant_work: { label: "I can't work", hint: "or I can't sign in" },
      blocked: { label: 'Blocked', hint: 'but I can work around it' },
      annoying: { label: 'Annoying', hint: "it affects me, it doesn't stop me" },
      question: { label: 'A question', hint: "I'm looking for guidance" },
    },
    priorityNeeded: 'Choose how much this is in your way to send it.',
    youSaid: (label) => `You said: ${label}`,
  },
  es: {
    lede: '¿Necesitas ayuda con algo? Una persona del equipo de soporte lee cada solicitud.',
    newLabel: 'Nuevo',
    newMenuLabel: 'Empezar algo nuevo',
    newAskOne: 'Ask One',
    newAskOneWho: 'Una respuesta ahora, de la IA',
    newTicket: 'Solicitud',
    newTicketWho: 'Responde una persona del equipo que elijas',
    newFeedback: 'Comentarios',
    newFeedbackWho: 'Una idea o una sugerencia',
    guidesTitle: 'Guías',
    guidesBody:
      'Cómo hacer tu parte en OneConetec: primeros pasos, cómo pedir ayuda y una guía para cada rol que tienes.',
    guidesAsk: 'Pregúntale a One cómo funciona algo',
    guidesOpen: 'Abrir guías',
    guidesCount: (n) => (n === 1 ? '1 guía para ti' : n + ' guías para ti'),
    wikiTitle: 'Wiki One',
    wikiBody: 'Respuestas sobre el cambio de marca, escritas por Comunicaciones.',
    wikiOpen: 'Leer Wiki One',
    ticketsTitle: 'Tus solicitudes',
    ticketsLede: 'Todo lo que enviaste en privado: solicitudes y comentarios. Solo tú y el equipo al que lo enviaste pueden leerlo.',
    feedbackTag: 'Comentarios',
    ticketTitle: 'Nueva solicitud',
    ticketLede: 'Una persona responde aquí, y recibes un aviso.',
    ticketAskOne: 'Para una respuesta inmediata, pregúntale a One.',
    whoReads: '¿Quién debería leer esto?',
    laneClosed: (queue) => `Todavía nadie atiende ${queue}, así que una solicitud no puede ir ahí. Elige el otro equipo, o pregúntale a One.`,
    cancel: 'Cancelar',
    done: 'Listo',
    feedbackTitle: 'Enviar comentarios',
    feedbackLabel: 'Tus ideas, opiniones o sugerencias',
    feedbackPlaceholder: '¿Qué haría que OneConetec fuera mejor para ti?',
    feedbackWho: [
      'El equipo de la plataforma lee cada uno, y ',
      've tu nombre',
      '. No se publica. Lo encontrarás en Tus solicitudes, y recibes un aviso cuando respondan.',
    ],
    feedbackClosed: 'Todavía nadie está leyendo los comentarios, así que no se pueden enviar. Pregúntale a One, o inténtalo más tarde.',
    sendFeedback: 'Enviar comentarios',
    kindLabel: 'Tipo',
    kindAll: 'Solicitudes y comentarios',
    kindQuestion: 'Solicitudes',
    kindFeedback: 'Comentarios',
    priorityLabel: '¿Cuánto te afecta esto?',
    priorities: {
      cant_work: { label: 'No puedo trabajar', hint: 'o no puedo iniciar sesión' },
      blocked: { label: 'Bloqueado', hint: 'pero puedo arreglármelas de otra forma' },
      annoying: { label: 'Molesto', hint: 'me afecta, pero no me detiene' },
      question: { label: 'Una pregunta', hint: 'busco orientación' },
    },
    priorityNeeded: 'Elige cuánto te afecta esto para poder enviarlo.',
    youSaid: (label) => `Dijiste: ${label}`,
  },
}

/* ── 339: GUIDES — #/help/guides (HLP-45, One and Help v2 S3) ───────────────
 *
 * The docs layout's own words. The guides themselves are markdown (role
 * guides are English, like the admin screens they describe); this chrome is
 * read by everyone who opens Help, so it has its Spanish column.
 */
export type GuidesCopy = {
  title: string
  forEveryone: string
  forRoles: string
  otherRoles: string
  onThisPage: string
  pickGuide: string
  pickSection: string
  /** 353 (D5 revised, David 1 Nov late). A role guide is read by the people
   *  who can open its pages, and by the support desk and tenant
   *  administrators, who answer questions about every guide. It says who it
   *  is FOR, so one of those readers knows why it reads as it does. */
  forWho: Record<'store_manager' | 'shop_admin' | 'support_desk' | 'comms_admin' | 'gallery_viewer' | 'gallery_doc_user' | 'gallery_admin', string>
  youHoldIt: string
  youDoNotHoldIt: string
  relevance: string
  /** The phone's collapsed list of guides for roles you do not hold. */
  othersFold: (n: number) => string
  audience: Record<'everyone' | 'store_manager' | 'shop_admin' | 'support_desk' | 'comms_admin' | 'gallery_viewer' | 'gallery_doc_user' | 'gallery_admin', string>
  askAbout: string
  askNote: string
  none: string
  notYours: string
  loading: string
  loadFailed: string
  backToList: string
  /** HLP-49: the guide sheet beside a working page. */
  sheetOpenFull: string
  sheetCopyLink: string
  sheetCopied: string
  sheetAsk: string
  sheetRefused: string
  sheetClose: string
  sheetLoading: string
}

export const GUIDES: Record<HubLang, GuidesCopy> = {
  en: {
    title: 'Guides',
    forEveryone: 'For everyone',
    forRoles: 'For your roles',
    otherRoles: "Other teams' guides",
    onThisPage: 'On this page',
    pickGuide: 'Guide',
    pickSection: 'Section',
    forWho: {
      store_manager: 'For store managers',
      shop_admin: 'For shop administrators',
      support_desk: 'For the support desk',
      comms_admin: 'For communications administrators',
      gallery_viewer: 'For people who browse the Gallery',
      gallery_doc_user: 'For Gallery document users',
      gallery_admin: 'For Gallery administrators',
    },
    youHoldIt: 'You hold this role, so it is one of your guides.',
    youDoNotHoldIt: 'You do not hold this role. You can read it because you answer questions about every guide.',
    relevance:
      'A guide for a role is open to the people who hold it, and to the support desk and administrators, who answer questions about every guide. The buttons a guide names still check, every time, that you may use them.',
    othersFold: (n) => (n === 1 ? "1 guide for another team" : `${n} guides for other teams`),
    audience: {
      everyone: 'Everyone',
      store_manager: 'Store manager',
      shop_admin: 'Shop administrator',
      support_desk: 'Support desk',
      comms_admin: 'Communications administrator',
      gallery_viewer: 'Gallery viewer',
      gallery_doc_user: 'Gallery document user',
      gallery_admin: 'Gallery administrator',
    },
    askAbout: 'Ask One about this guide',
    askNote: 'Opens Ask One with this section as its context. Where your company has switched it on, One reads the guides you can see here.',
    none: 'There are no guides for you yet. Ask One can tell you how any page works.',
    notYours: 'That guide does not exist, or it is not open to you. Choose one from the list.',
    loading: 'Loading guides…',
    loadFailed: 'The guides could not be loaded. Check your connection and try again.',
    backToList: 'All guides',
    sheetOpenFull: 'Open the full guide',
    sheetCopyLink: 'Copy link to this section',
    sheetCopied: 'Link copied',
    sheetAsk: 'Ask One about this',
    sheetRefused: 'That guide is not open to you, so there is nothing to show here.',
    sheetClose: 'Close',
    sheetLoading: 'Loading the guide…',
  },
  es: {
    title: 'Guías',
    forEveryone: 'Para todos',
    forRoles: 'Para tus roles',
    otherRoles: 'Guías de otros equipos',
    onThisPage: 'En esta página',
    pickGuide: 'Guía',
    pickSection: 'Sección',
    forWho: {
      store_manager: 'Para encargados de bodega',
      shop_admin: 'Para administradores de la tienda',
      support_desk: 'Para la mesa de soporte',
      comms_admin: 'Para administradores de comunicaciones',
      gallery_viewer: 'Para quienes navegan la Galería',
      gallery_doc_user: 'Para usuarios de documentos de la Galería',
      gallery_admin: 'Para administradores de la Galería',
    },
    youHoldIt: 'Tienes este rol, así que es una de tus guías.',
    youDoNotHoldIt: 'No tienes este rol. Puedes leerla porque respondes preguntas sobre todas las guías.',
    relevance:
      'La guía de un rol está abierta a quienes lo tienen, y a la mesa de soporte y los administradores, que responden preguntas sobre todas las guías. Los botones que nombra una guía siempre verifican que puedas usarlos.',
    othersFold: (n) => (n === 1 ? '1 guía de otro equipo' : `${n} guías de otros equipos`),
    audience: {
      everyone: 'Todos',
      store_manager: 'Encargado de bodega',
      shop_admin: 'Administrador de la tienda',
      support_desk: 'Mesa de soporte',
      comms_admin: 'Administrador de comunicaciones',
      gallery_viewer: 'Visitante de la Galería',
      gallery_doc_user: 'Usuario de documentos de la Galería',
      gallery_admin: 'Administrador de la Galería',
    },
    askAbout: 'Pregúntale a One sobre esta guía',
    askNote: 'Abre Ask One con esta sección como contexto. Donde tu empresa lo ha activado, One lee las guías que ves aquí.',
    none: 'Todavía no hay guías para ti. Ask One puede explicarte cómo funciona cualquier página.',
    notYours: 'Esa guía no existe, o no está abierta para ti. Elige una de la lista.',
    loading: 'Cargando guías…',
    loadFailed: 'No se pudieron cargar las guías. Revisa tu conexión e inténtalo de nuevo.',
    backToList: 'Todas las guías',
    sheetOpenFull: 'Abrir la guía completa',
    sheetCopyLink: 'Copiar el enlace a esta sección',
    sheetCopied: 'Enlace copiado',
    sheetAsk: 'Pregúntale a One sobre esto',
    sheetRefused: 'Esa guía no está abierta para ti, así que no hay nada que mostrar aquí.',
    sheetClose: 'Cerrar',
    sheetLoading: 'Cargando la guía…',
  },
}

/**
 * 353 (HLP-50). The "How … works" links on the everyday pages, which speak
 * Spanish (admin and desk pages stay English, like their screens). Each opens
 * one guide section beside the page (GuideLink), and only renders when the
 * person may read that guide.
 */
export const GUIDE_LINKS: Record<HubLang, {
  ticket: string
  feedback: string
  yourTickets: string
  starOne: string
  photos: string
  language: string
  profileCard: string
}> = {
  en: {
    ticket: 'How a ticket works',
    feedback: 'How feedback works',
    yourTickets: 'How your tickets work',
    starOne: 'How Star One works',
    photos: 'How photos and contests work',
    language: 'How languages work',
    profileCard: 'How your profile and card work',
  },
  es: {
    ticket: 'Cómo funciona una solicitud',
    feedback: 'Cómo funcionan los comentarios',
    yourTickets: 'Cómo funcionan tus solicitudes',
    starOne: 'Cómo funciona Star One',
    photos: 'Cómo funcionan las fotos y los concursos',
    language: 'Cómo funcionan los idiomas',
    profileCard: 'Cómo funcionan tu perfil y tu tarjeta',
  },
}

export const HUB_COPY = {
  guideLinks: GUIDE_LINKS,
  photoWhat: PHOTO_WHAT,
  shell: SHELL,
  notifications: NOTIFICATIONS,
  welcome: WELCOME,
  ask: ASK,
  home: HOME,
  compose: COMPOSE,
  thread: THREAD,
  checkNote: CHECK_NOTE,
  wiki: WIKI,
  stars: STARS,
  shop: SHOP,
  profile: PROFILE,
  card: CARD,
  support: SUPPORT,
  supportStarLevels: SUPPORT_STAR_LEVELS,
  hero: HERO,
  oneQuestion: ONE_QUESTION,
  photoRoom: PHOTO_ROOM,
  contests: CONTESTS,
  photoReviews: PHOTO_REVIEW,
  galleryBrowse: GALLERY_BROWSE,
  adminHome: ADMIN_HOME,
  noticesPage: NOTICES_PAGE,
  tour: TOUR,
  terms: TERMS,
  surveys: SURVEYS,
  galleryMap: GALLERY_MAP,
  searchPick: SEARCH_PICK,
  siteList: SITE_LIST,
  feedStars: FEED_STARS,
  help: HELP,
  guides: GUIDES,
} as const

